import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronUp, ChevronDown, Download, Plus, Search, Edit, Trash2 } from 'lucide-react';
import { isAuthenticated, getCachedUser } from '../utils/auth';
import BuyerOrders from './BuyerOrders';
import SellerOrders from './SellerOrders';
import RequestManagement from './RequestManagement';
import { getSellerAds, updateSellerAd, deleteSellerAd, createSellerAd } from '../utils/seller';
import { showToast } from './ToastContainer';
import { getGameFilterConfig } from '../config/gameFilters';
import { formatPriceDecimals, getEffectivePrice } from '../utils/formatPrice';
import { Skeleton } from './common/Skeleton';
import { compressImageToBase64 } from '../utils/imageCompression';
import { getAdImageUrl } from '../utils/adImageUrl';
import CurrencyAdEditModal from './CurrencyAdEditModal';
import '../styles/OffersTab.css';
import '../styles/SellerDashboard.css';
import '../styles/FortniteTopUpsPage.css';

/** Currency unit label (word in parentheses) per game for "in M (Silver)" etc. Add new games here only. */
const CURRENCY_UNIT_LABEL_BY_GAME = {
  'Final Fantasy XIV': 'Gil',
  'Animal Crossing: New Horizons': 'Bells',
  'Fisch': 'Money',
  'Forza Horizon 5': 'Credits',
  'Grow a Garden': 'Tokens',
  'Lost Ark': 'Gold',
  'Minecraft': 'Coins',
  'New World': 'Coins',
  'Old School RuneScape': 'GP',
  'Path of Exile': 'Orbs',
  'Path of Exile 2': 'Orbs',
  'Roblox': 'Robux',
  'RuneScape 3': 'Gold',
  'Throne and Liberty': 'Lucent',
  'Warframe': 'Platinum',
  'WoW Classic': 'Gold',
  'WoW Mists of Pandaria': 'Gold',
};

const OffersTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ads, setAds] = useState([]);
  const [filteredAds, setFilteredAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('currency');
  const [offerTypeDropdownOpen, setOfferTypeDropdownOpen] = useState(false);
  const [sellerDropdownOpen, setSellerDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingAd, setEditingAd] = useState(null);
  const [deletingAd, setDeletingAd] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const offerTypeDropdownRef = useRef(null);
  const sellerDropdownRef = useRef(null);

  const offerTypeOptions = [
    { id: 'currency', label: 'Currency', path: '/profile/offers/currency' },
    { id: 'accounts', label: 'Accounts', path: '/profile/offers/accounts' },
    { id: 'top-ups', label: 'Top Ups', path: '/profile/offers/top-ups' },
    { id: 'items', label: 'Items', path: '/profile/offers/items' },
  ];

  const sellerOptions = [
    { id: 'purchased-orders', label: 'Purchased Orders', path: '/profile/seller/purchased-orders' },
    { id: 'sold-orders', label: 'Sold Orders', path: '/profile/seller/sold-orders' },
    { id: 'my-items', label: 'My Items', path: '/profile/seller/my-items' },
    { id: 'requests', label: 'Requests', path: '/profile/seller/requests' },
  ];

  // Determine current view from URL
  useEffect(() => {
    const path = location.pathname;
    const offerTypeView = offerTypeOptions.find(v => path === v.path);
    const sellerView = sellerOptions.find(v => path === v.path);
    if (offerTypeView) {
      setCurrentView(offerTypeView.id);
    } else if (sellerView) {
      setCurrentView(sellerView.id);
    } else if (path === '/profile/offers') {
      // Default to currency
      navigate('/profile/offers/currency', { replace: true });
    } else if (path === '/profile/seller') {
      // Default to purchased orders
      navigate('/profile/seller/purchased-orders', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Load data based on current view
  useEffect(() => {
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }

    if (['currency', 'accounts', 'top-ups', 'items'].includes(currentView)) {
      loadAds();
    } else if (currentView === 'my-items') {
      loadAllAds();
    } else {
      setLoading(false);
    }
  }, [currentView]);

  const loadAds = async () => {
    setLoading(true);
    try {
      const user = getCachedUser();
      if (!user || !user.id) {
        setAds([]);
        setLoading(false);
        return;
      }

      // Map currentView to service_type
      const serviceTypeMap = {
        'currency': 'currency',
        'accounts': 'accounts',
        'top-ups': 'top-ups',
        'items': 'items',
      };

      const serviceType = serviceTypeMap[currentView];
      if (!serviceType) {
        setAds([]);
        setLoading(false);
        return;
      }

      // Load all ads first, then filter client-side
      const { data, error } = await getSellerAds();
      if (error) {
        console.error('Error loading ads:', error);
        setAds([]);
      } else {
        console.log('All ads loaded:', data);
        console.log('Filtering for service_type:', serviceType);
        
        // Filter ads by service_type
        const filteredAds = (data || []).filter(ad => {
          const matches = ad.service_type === serviceType;
          console.log(`Ad ${ad.id}: service_type="${ad.service_type}", matches=${matches}`);
          return matches;
        });
        
        console.log('Filtered ads:', filteredAds);
        // Show all ads regardless of status in Offers section
        setAds(filteredAds);
        setFilteredAds(filteredAds);
      }
    } catch (error) {
      console.error('Error loading ads:', error);
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAllAds = async () => {
    setLoading(true);
    try {
      const { data } = await getSellerAds();
      if (data) {
        setAds(data);
        setFilteredAds(data);
      } else {
        setAds([]);
        setFilteredAds([]);
      }
    } catch (error) {
      console.error('Error loading ads:', error);
      setAds([]);
      setFilteredAds([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter ads based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredAds(ads);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = ads.filter(ad => 
      ad.title?.toLowerCase().includes(query) ||
      ad.description?.toLowerCase().includes(query) ||
      ad.game?.toLowerCase().includes(query)
    );
    setFilteredAds(filtered);
  }, [searchQuery, ads]);

  const handleDeleteClick = (adId) => {
    setConfirmDelete(adId);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;

    const adId = confirmDelete;
    setDeletingAd(adId);
    setConfirmDelete(null);
    
    try {
      const { error } = await deleteSellerAd(adId);
      if (error) {
        showToast('Failed to delete ad: ' + error.message, 'error');
      } else {
        showToast('Ad deleted successfully', 'success');
        // Reload ads
        if (['currency', 'accounts', 'top-ups', 'items'].includes(currentView)) {
          loadAds();
        } else if (currentView === 'my-items') {
          loadAllAds();
        }
      }
    } catch (error) {
      showToast('Failed to delete ad: ' + error.message, 'error');
    } finally {
      setDeletingAd(null);
    }
  };

  const handleEditAd = (ad) => {
    setEditingAd(ad);
  };

  const handleCreateAd = async (adData) => {
    try {
      // Convert game name to slug for API
      // Handle special cases first
      const gameSlugMap = {
        '8 Ball Pool': '8-ball-pool',
        '8-ball-pool': '8-ball-pool'
      };
      let gameSlug = gameSlugMap[adData.game] || adData.game?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const processedData = {
        ...adData,
        game: gameSlug || adData.game
      };

      const { error } = await createSellerAd(processedData);
      if (error) {
        showToast('Failed to create ad: ' + error.message, 'error');
        throw error;
      }
      showToast('Ad created successfully', 'success');
      setShowCreateForm(false);
      
      // Clear game page cache for this game
      if (gameSlug && typeof localStorage !== 'undefined') {
        ['accounts', 'items', 'top-ups', 'currency'].forEach(serviceType => {
          const cacheKey = `${gameSlug}_ads_${serviceType}`;
          localStorage.removeItem(cacheKey);
        });
      }
      
      // Reload ads
      if (['currency', 'accounts', 'top-ups', 'items'].includes(currentView)) {
        loadAds();
      } else if (currentView === 'my-items') {
        loadAllAds();
      }
    } catch (error) {
      // Error already handled
    }
  };

  // Currency ads use the simple edit modal (description, stock, min purchase only)
  const isCurrencyAd = (ad) => {
    if (!ad) return false;
    const st = String(ad.service_type ?? '').toLowerCase();
    if (st === 'currency') return true;
    if ((ad.service_type == null || ad.service_type === '') && /8\s*ball\s*pool|8-ball-pool/i.test(ad.game || '')) return true;
    return false;
  };

  const handleUpdateAd = async (adId, adData) => {
    try {
      // Don't send game or top_up_option when updating (they're read-only)
      const processedData = { ...adData };
      delete processedData.game;
      delete processedData.top_up_option;
      
      // Debug logging for top-ups platform
      if (adData.service_type === 'top-ups') {
        console.log('[handleUpdateAd] Updating top-up ad:', {
          adId: adId,
          platform: processedData.platform,
          platformFromAdData: adData.platform,
          metadata: processedData.metadata
        });
      }

      const { error } = await updateSellerAd(adId, processedData);
      if (error) {
        showToast('Failed to update ad: ' + error.message, 'error');
        throw error;
      }
      showToast('Ad updated successfully', 'success');
      setEditingAd(null);
      
      // Clear game page cache for this game
      if (adData.game && typeof localStorage !== 'undefined') {
        const gameSlugMap = {
          '8 Ball Pool': '8-ball-pool',
          '8-ball-pool': '8-ball-pool'
        };
        const gameSlug = gameSlugMap[adData.game] || adData.game?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        
        if (gameSlug) {
          ['accounts', 'items', 'top-ups', 'currency'].forEach(serviceType => {
            const cacheKey = `${gameSlug}_ads_${serviceType}`;
            localStorage.removeItem(cacheKey);
          });
        }
      }
      
      // Reload ads
      if (['currency', 'accounts', 'top-ups', 'items'].includes(currentView)) {
        loadAds();
      } else if (currentView === 'my-items') {
        loadAllAds();
      }
    } catch (error) {
      // Error already handled
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (offerTypeDropdownRef.current && !offerTypeDropdownRef.current.contains(event.target)) {
        setOfferTypeDropdownOpen(false);
      }
      if (sellerDropdownRef.current && !sellerDropdownRef.current.contains(event.target)) {
        setSellerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOfferTypeSelect = (viewId) => {
    setCurrentView(viewId);
    setOfferTypeDropdownOpen(false);
    const view = offerTypeOptions.find(v => v.id === viewId);
    if (view) {
      navigate(view.path);
    }
  };

  const handleSellerSelect = (viewId) => {
    setCurrentView(viewId);
    setSellerDropdownOpen(false);
    const view = sellerOptions.find(v => v.id === viewId);
    if (view) {
      navigate(view.path);
    }
  };

  const currentOfferTypeLabel = offerTypeOptions.find(v => v.id === currentView)?.label || 'Currency';
  const currentSellerLabel = sellerOptions.find(v => v.id === currentView)?.label || null;
  const isSellerView = sellerOptions.some(v => v.id === currentView);

  const getQuantityDisplay = (ad) => {
    const n = Math.max(0, Number(ad.quantity) || 0);
    const unit = (ad.metadata?.unit_label != null && ad.metadata.unit_label !== '') ? String(ad.metadata.unit_label) : (ad.service_type === 'currency' && /8\s*ball\s*pool|8-ball-pool/i.test(ad.game || '')) ? 'B' : (ad.service_type === 'currency' && /animal\s*crossing|animal-crossing-new-horizons|albion\s*online/i.test(ad.game || '')) ? 'M' : (ad.service_type === 'currency' && /arc\s*raiders/i.test(ad.game || '')) ? 'K' : '';
    if (n === 0) return unit ? `0${unit} sold out` : '0 sold out';
    return unit ? `${n}${unit}` : `${n}`;
  };

  const renderContent = () => {
    switch (currentView) {
      case 'currency':
      case 'accounts':
      case 'top-ups':
      case 'items':
        if (loading) {
          return (
            <div className="offers-skeleton">
              <div className="offers-ads-grid">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="offer-ad-card-skeleton">
                    <Skeleton className="skeleton-image" style={{ height: '120px', borderRadius: '8px 8px 0 0' }} />
                    <div style={{ padding: '0.75rem' }}>
                      <Skeleton style={{ width: '80%', height: '16px', marginBottom: '0.5rem' }} />
                      <Skeleton style={{ width: '60%', height: '14px', marginBottom: '0.75rem' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Skeleton style={{ width: '80px', height: '16px' }} />
                        <Skeleton style={{ width: '60px', height: '20px', borderRadius: '4px' }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (!loading && filteredAds.length === 0) {
          return (
            <div className="offers-empty-state">
              <div className="offers-empty-illustration">
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="60" cy="60" r="50" fill="#2a2a2a" />
                  <path d="M40 50L60 70L80 50" stroke="#666" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="offers-empty-title">No {currentView} ads</h3>
              <p className="offers-empty-text">
                You don't have any {currentView} ads. Create a new ad to start selling!
              </p>
            </div>
          );
        }
        return (
          <div className="offers-ads-grid">
            {filteredAds.map((ad) => (
              <div key={ad.id} className="offer-ad-card">
                <div className="offer-ad-actions-top">
                  <button
                    className="offer-ad-action-btn offer-ad-edit"
                    onClick={() => handleEditAd(ad)}
                    title="Edit ad"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    className="offer-ad-action-btn offer-ad-delete"
                    onClick={() => handleDeleteClick(ad.id)}
                    disabled={deletingAd === ad.id}
                    title="Delete ad"
                  >
                    {deletingAd === ad.id ? '...' : <Trash2 size={14} />}
                  </button>
                </div>
                {ad.image_urls && ad.image_urls.length > 0 && (
                  <div className="offer-ad-image">
                    <img src={getAdImageUrl(ad.image_urls[0])} alt={ad.title} />
                  </div>
                )}
                <div className="offer-ad-info">
                  <div className="offer-ad-header">
                    <h4 className="offer-ad-title" title={ad.title}>{ad.title}</h4>
                    <span className="offer-ad-game">{ad.game}</span>
                  </div>
                  <div className="offer-ad-footer">
                    <div className="offer-ad-price">
                      {(() => {
                        const effective = getEffectivePrice(ad);
                        return effective ? `${ad.currency || 'EUR'} ${formatPriceDecimals(effective)}` : 'Price on request';
                      })()}
                    </div>
                    <div className="offer-ad-meta">
                      <span className="offer-ad-quantity">Qty: {getQuantityDisplay(ad)}</span>
                      <span className={`offer-ad-status status-${ad.status}`}>{ad.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'purchased-orders':
        return <BuyerOrders />;

      case 'sold-orders':
        return <SellerOrders />;

      case 'my-items':
        return (
          <div className="my-items-section">
            {!loading && ads.length === 0 ? (
              <div className="offers-empty-state">
                <div className="offers-empty-illustration">
                  <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="60" cy="60" r="50" fill="#2a2a2a" />
                    <path d="M40 50L60 70L80 50" stroke="#666" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="offers-empty-title">No items yet</h3>
                <p className="offers-empty-text">
                  You haven't created any ads yet. Create your first ad to start selling!
                </p>
              </div>
            ) : (
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Item name</th>
                      <th>Game</th>
                      <th>Quantity</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ads.map(ad => (
                      <tr key={ad.id}>
                        <td>#{ad.id.substring(0, 8).toUpperCase()}</td>
                        <td>{ad.title}</td>
                        <td>{ad.game}</td>
                        <td>
                          <span className="quantity-badge">
                            {getQuantityDisplay(ad)} {ad.auto_delivery && ad.service_type === 'accounts' ? '🔐' : ''}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge status-${ad.status}`}>
                            {ad.status.toUpperCase()}
                          </span>
                        </td>
                        <td>{ad.price ? `${ad.currency} ${ad.price}` : 'N/A'}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-action">Edit</button>
                            <button className="btn-action btn-delete">Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'requests':
        return <RequestManagement />;

      default:
        return null;
    }
  };

  return (
    <div className="offers-tab">
      <div className="offers-header">
        <div className="offers-title-section">
          <h2 className="offers-title">Offers</h2>
          <div className="offers-dropdowns">
            <div className="offers-dropdown-wrapper" ref={offerTypeDropdownRef}>
              <button
                className="offers-type-dropdown"
                onClick={() => {
                  setOfferTypeDropdownOpen(!offerTypeDropdownOpen);
                  setSellerDropdownOpen(false);
                }}
              >
                <span>{isSellerView ? 'Select Type' : currentOfferTypeLabel}</span>
                {offerTypeDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {offerTypeDropdownOpen && (
                <div className="offers-dropdown-menu">
                  {offerTypeOptions.map((view) => (
                    <button
                      key={view.id}
                      className={`offers-dropdown-item ${currentView === view.id ? 'active' : ''}`}
                      onClick={() => handleOfferTypeSelect(view.id)}
                    >
                      {view.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="offers-dropdown-wrapper" ref={sellerDropdownRef}>
              <button
                className="offers-type-dropdown"
                onClick={() => {
                  setSellerDropdownOpen(!sellerDropdownOpen);
                  setOfferTypeDropdownOpen(false);
                }}
              >
                <span>{isSellerView ? currentSellerLabel : 'Seller'}</span>
                {sellerDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {sellerDropdownOpen && (
                <div className="offers-dropdown-menu">
                  {sellerOptions.map((view) => (
                    <button
                      key={view.id}
                      className={`offers-dropdown-item ${currentView === view.id ? 'active' : ''}`}
                      onClick={() => handleSellerSelect(view.id)}
                    >
                      {view.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="offers-actions">
          <button className="offers-export-btn">
            <span>Export</span>
            <Download size={16} />
          </button>
          {['currency', 'accounts', 'top-ups', 'items'].includes(currentView) && (
            <button 
              className="offers-new-btn"
              onClick={() => setShowCreateForm(true)}
            >
              <Plus size={16} />
              <span>Create Ad</span>
            </button>
          )}
          {currentView === 'my-items' && (
            <button 
              className="offers-new-btn"
              onClick={() => setShowCreateForm(true)}
            >
              <Plus size={16} />
              <span>Publish Item</span>
            </button>
          )}
        </div>
      </div>

      {renderContent()}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="delete-confirm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Ad</h3>
            <p>Are you sure you want to delete this ad? This action cannot be undone.</p>
            <div className="delete-confirm-actions">
              <button
                className="delete-confirm-cancel"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-delete"
                onClick={handleDeleteConfirm}
                disabled={deletingAd === confirmDelete}
              >
                {deletingAd === confirmDelete ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal - Currency ads: limited fields (stock, min purchase, description); others: full AdForm */}
      {editingAd && isCurrencyAd(editingAd) && (
        <CurrencyAdEditModal
          ad={editingAd}
          onSave={(data) => handleUpdateAd(editingAd.id, data)}
          onCancel={() => setEditingAd(null)}
        />
      )}
      {editingAd && !isCurrencyAd(editingAd) && (
        <AdForm
          ad={editingAd}
          initialServiceType={editingAd?.service_type || currentView}
          onSubmit={(data) => handleUpdateAd(editingAd.id, data)}
          onCancel={() => setEditingAd(null)}
        />
      )}

      {/* Create Ad Form */}
      {showCreateForm && (
        <AdForm
          initialServiceType={currentView === 'currency' ? 'currency' : currentView === 'accounts' ? 'accounts' : currentView === 'top-ups' ? 'top-ups' : currentView === 'items' ? 'items' : ''}
          onSubmit={handleCreateAd}
          onCancel={() => setShowCreateForm(false)}
        />
      )}
    </div>
  );
};

// Simple Edit Ad Modal Component
const EditAdModal = ({ ad, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: ad?.title || '',
    description: ad?.description || '',
    price: ad?.price || '',
    quantity: ad?.quantity || 1,
    status: ad?.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  
  // Update formData when ad changes (in case ad is loaded asynchronously or updated)
  useEffect(() => {
    if (ad) {
      // Parse metadata if it's a string
      const metadata = ad.metadata && typeof ad.metadata === 'string' 
        ? (() => {
            try {
              return JSON.parse(ad.metadata);
            } catch (e) {
              return {};
            }
          })()
        : (ad.metadata || {});
      
      setFormData({
        title: ad.title || '',
        description: ad.description || '', // Ensure description is loaded
        price: ad.price || '',
        quantity: ad.quantity || 1, // Ensure quantity is loaded
        status: ad.status || 'active',
      });
    }
  }, [ad]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Don't send title in update (it's read-only)
      const { title, ...updateData } = formData;
      await onSave(updateData);
    } catch (error) {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="edit-ad-overlay" onClick={onCancel}>
      <div className="edit-ad-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-ad-header">
          <h3>Edit Ad</h3>
          <button className="edit-ad-close" onClick={onCancel}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="edit-ad-form">
          <div className="edit-ad-field">
            <label>Title</label>
            <input
              type="text"
              value={formData.title}
              readOnly
              disabled
              style={{
                backgroundColor: '#1a1a1a',
                color: '#a0a0a0',
                cursor: 'not-allowed',
                opacity: 0.7
              }}
            />
            <p style={{ fontSize: '0.75rem', color: '#a0a0a0', marginTop: '0.25rem' }}>
              Title cannot be edited
            </p>
          </div>
          <div className="edit-ad-field">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="4"
            />
          </div>
          <div className="edit-ad-row">
            <div className="edit-ad-field">
              <label>Price (USD) *</label>
              <input
                type="number"
                step="0.01"
                value={formData.price || ''}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                min="0"
                required
                placeholder="0.00"
              />
            </div>
            <div className="edit-ad-field">
              <label>Quantity *</label>
              <input
                type="number"
                value={formData.quantity || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setFormData({ ...formData, quantity: '' });
                  } else {
                    const qty = parseInt(value, 10);
                    if (!isNaN(qty) && qty >= 1) {
                      setFormData({ ...formData, quantity: qty });
                    }
                  }
                }}
                onBlur={(e) => {
                  if (!formData.quantity || formData.quantity < 1) {
                    setFormData({ ...formData, quantity: 1 });
                  }
                }}
                min="1"
                required
                placeholder="1"
              />
            </div>
          </div>
          <div className="edit-ad-field">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="edit-ad-actions">
            <button type="button" onClick={onCancel} className="edit-ad-cancel">
              Cancel
            </button>
            <button type="submit" className="edit-ad-save" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
  };

// AdForm Component (extracted from SellerDashboard)
const AdForm = ({ ad, initialServiceType, onSubmit, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  // Parse metadata if it's a string
  const parsedMetadata = ad?.metadata && typeof ad?.metadata === 'string' 
    ? (() => {
        try {
          return JSON.parse(ad.metadata);
        } catch (e) {
          return {};
        }
      })()
    : (ad?.metadata || {});

  const [formData, setFormData] = useState({
    game: ad?.game || '',
    service_type: ad?.service_type || initialServiceType || '',
    platform: ad?.platform || (ad?.game === '8 Ball Pool' ? 'All Devices' : ''),
    account_type: ad?.account_type || '',
    account_attributes: ad?.account_attributes || parsedMetadata?.account_attributes || {},
    title: ad?.title || '',
    description: ad?.description || '',
    price: (parsedMetadata?.price_per_unit != null && parsedMetadata.price_per_unit !== '') ? String(parsedMetadata.price_per_unit) : (ad?.price ?? ''),
    currency: 'EUR',
    metadata: parsedMetadata,
    image_urls: ad?.image_urls || [],
    quantity: ad != null ? (ad.quantity != null ? ad.quantity : 1) : (initialServiceType === 'currency' ? '' : 1),
    auto_delivery: ad?.auto_delivery || false,
    account_details: ad?.account_details || [],
    delivery_time: parsedMetadata?.delivery_time || (ad?.auto_delivery ? 'Instant' : ''),
    top_up_option: ad?.top_up_option || parsedMetadata?.top_up_option || '', // Check both places
    delivery_method: ad?.delivery_method || parsedMetadata?.delivery_method || '', // Delivery method: 'autoclaim-uid', 'login-method', 'redeem-code', 'donation'
    delivery_instructions: ad?.delivery_instructions || parsedMetadata?.delivery_instructions || '', // Delivery instructions textarea
    top_up_region: ad?.top_up_region || parsedMetadata?.top_up_region || '', // Region for top-ups (if game requires it)
    device: ad?.metadata?.device || ad?.device || parsedMetadata?.device || '', // Black Desert Online: device (PC, PlayStation, Xbox)
    region: ad?.metadata?.region || ad?.region || parsedMetadata?.region || '', // Black Desert Online: region (EU, NA, etc.)
    server: ad?.metadata?.server || ad?.server || parsedMetadata?.server || '', // Final Fantasy XIV: server (Adamantoise, etc.)
    currency_type: ad?.metadata?.currency || parsedMetadata?.currency || '', // Fisch Money: currency filter (C$, E$, Other)
    min_quantity: ad?.min_quantity ?? parsedMetadata?.min_quantity ?? 1, // Minimum offer quantity (e.g. for 8 Ball Pool currency: 1 B)
    volume_discount: parsedMetadata?.volume_discount && Array.isArray(parsedMetadata.volume_discount)
      ? parsedMetadata.volume_discount
      : [{ min_quantity: 0, discount_percent: 0 }], // Volume discount tiers: min quantity (B) -> discount %
  });
  
  // Normalize game name when editing (convert slug like "pubg" to "PUBG" for proper lookup)
  useEffect(() => {
    if (ad && formData.game) {
      // Try to normalize using gameNameMap (defined earlier in file)
      const normalized = gameNameMap[formData.game.toLowerCase()];
      if (normalized && normalized !== formData.game) {
        console.log('[AdForm] Normalizing game name:', formData.game, '->', normalized);
        setFormData(prev => ({ ...prev, game: normalized }));
      }
    }
  }, [ad]); // Only run once when ad is loaded
  
  // Debug logging when editing
  useEffect(() => {
    if (ad) {
      console.log('[AdForm] Editing ad - loaded data:', {
        id: ad.id,
        game: ad.game,
        normalizedGame: formData.game,
        platform: ad.platform,
        service_type: ad.service_type,
        top_up_option: ad.top_up_option || parsedMetadata?.top_up_option,
        metadata: parsedMetadata
      });
    }
  }, [ad, formData.game]);

  // Animal Crossing currency: when reaching step 2 (Details), default min_quantity to 10 so it doesn't show 1
  const isACNHCurrencyFlowForEffect = formData.game === 'Animal Crossing: New Horizons' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (currentStep === 2 && isACNHCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity === 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 10 }));
    }
  }, [currentStep, isACNHCurrencyFlowForEffect]);
  // Final Fantasy XIV currency: default min_quantity to 5 (Gil) when game is FFXIV + currency
  const isFinalFantasyXIVCurrencyFlowForEffect = formData.game === 'Final Fantasy XIV' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isFinalFantasyXIVCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 5)) {
      setFormData(prev => ({ ...prev, min_quantity: 5 }));
    }
  }, [isFinalFantasyXIVCurrencyFlowForEffect, formData.min_quantity]);
  // Fisch currency: default min_quantity to 10 (M) when game is Fisch + currency
  const isFischCurrencyFlowForEffect = formData.game === 'Fisch' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isFischCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 10)) {
      setFormData(prev => ({ ...prev, min_quantity: 10 }));
    }
  }, [isFischCurrencyFlowForEffect, formData.min_quantity]);
  // Forza Horizon 5 currency: default min_quantity to 100 (M) when game is Forza Horizon 5 + currency
  const isForzaHorizon5CurrencyFlowForEffect = formData.game === 'Forza Horizon 5' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isForzaHorizon5CurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 100)) {
      setFormData(prev => ({ ...prev, min_quantity: 100 }));
    }
  }, [isForzaHorizon5CurrencyFlowForEffect, formData.min_quantity]);
  // Grow a Garden currency: default min_quantity to 500 (unit) when game is Grow a Garden + currency
  const isGrowAGardenCurrencyFlowForEffect = formData.game === 'Grow a Garden' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isGrowAGardenCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 500)) {
      setFormData(prev => ({ ...prev, min_quantity: 500 }));
    }
  }, [isGrowAGardenCurrencyFlowForEffect, formData.min_quantity]);
  // Minecraft currency: default min_quantity to 10 (M) when game is Minecraft + currency
  const isMinecraftCurrencyFlowForEffect = formData.game === 'Minecraft' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isMinecraftCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 10)) {
      setFormData(prev => ({ ...prev, min_quantity: 10 }));
    }
  }, [isMinecraftCurrencyFlowForEffect, formData.min_quantity]);
  // New World currency: default min_quantity to 10 (K) when game is New World + currency
  const isNewWorldCurrencyFlowForEffect = formData.game === 'New World' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isNewWorldCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 10)) {
      setFormData(prev => ({ ...prev, min_quantity: 10 }));
    }
  }, [isNewWorldCurrencyFlowForEffect, formData.min_quantity]);
  // Old School RuneScape currency: default min_quantity to 1 (M) when game is Old School RuneScape + currency
  const isOldSchoolRuneScapeCurrencyFlowForEffect = formData.game === 'Old School RuneScape' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isOldSchoolRuneScapeCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 1 }));
    }
  }, [isOldSchoolRuneScapeCurrencyFlowForEffect, formData.min_quantity]);
  // Path of Exile currency: default min_quantity to 1 (unit) when game is Path of Exile + currency
  const isPathOfExileCurrencyFlowForEffect = formData.game === 'Path of Exile' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isPathOfExileCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 1 }));
    }
  }, [isPathOfExileCurrencyFlowForEffect, formData.min_quantity]);
  // Path of Exile 2 currency: default min_quantity to 1 (unit) when game is Path of Exile 2 + currency
  const isPathOfExile2CurrencyFlowForEffect = formData.game === 'Path of Exile 2' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isPathOfExile2CurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 1 }));
    }
  }, [isPathOfExile2CurrencyFlowForEffect, formData.min_quantity]);
  // Roblox currency: default min_quantity to 100 (unit) when game is Roblox + currency
  const isRobloxCurrencyFlowForEffect = formData.game === 'Roblox' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isRobloxCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 100)) {
      setFormData(prev => ({ ...prev, min_quantity: 100 }));
    }
  }, [isRobloxCurrencyFlowForEffect, formData.min_quantity]);
  // RuneScape 3 currency: default min_quantity to 100 (M) when game is RuneScape 3 + currency
  const isRuneScape3CurrencyFlowForEffect = formData.game === 'RuneScape 3' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isRuneScape3CurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 100)) {
      setFormData(prev => ({ ...prev, min_quantity: 100 }));
    }
  }, [isRuneScape3CurrencyFlowForEffect, formData.min_quantity]);
  // Throne and Liberty currency: default min_quantity to 100 (unit) when game is Throne and Liberty + currency
  const isThroneAndLibertyCurrencyFlowForEffect = formData.game === 'Throne and Liberty' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isThroneAndLibertyCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 100)) {
      setFormData(prev => ({ ...prev, min_quantity: 100 }));
    }
  }, [isThroneAndLibertyCurrencyFlowForEffect, formData.min_quantity]);
  // Warframe currency: default min_quantity to 1000 (1 K) when game is Warframe + currency
  const isWarframeCurrencyFlowForEffect = formData.game === 'Warframe' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isWarframeCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 1000)) {
      setFormData(prev => ({ ...prev, min_quantity: 1000 }));
    }
  }, [isWarframeCurrencyFlowForEffect, formData.min_quantity]);
  // WoW Classic currency: default min_quantity to 100 (unit) when game is WoW Classic + currency
  const isWoWClassicCurrencyFlowForEffect = formData.game === 'WoW Classic' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isWoWClassicCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 100)) {
      setFormData(prev => ({ ...prev, min_quantity: 100 }));
    }
  }, [isWoWClassicCurrencyFlowForEffect, formData.min_quantity]);
  // WoW Mists of Pandaria currency: default min_quantity to 1000 (1 K) when game is WoW Mists of Pandaria + currency
  const isWoWMistsOfPandariaCurrencyFlowForEffect = formData.game === 'WoW Mists of Pandaria' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (isWoWMistsOfPandariaCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity < 1000)) {
      setFormData(prev => ({ ...prev, min_quantity: 1000 }));
    }
  }, [isWoWMistsOfPandariaCurrencyFlowForEffect, formData.min_quantity]);
  // Arc Raiders currency: when reaching step 2 (Details), default min_quantity to 1000
  const isArcRaidersCurrencyFlowForEffect = formData.game === 'Arc Raiders' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (currentStep === 2 && isArcRaidersCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity === 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 1000 }));
    }
  }, [currentStep, isArcRaidersCurrencyFlowForEffect]);
  // Black Desert Online currency: when reaching step 2 (Details), default min_quantity to 1000
  const isBDOCurrencyFlowForEffect = formData.game === 'Black Desert Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (currentStep === 2 && isBDOCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity === 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 1000 }));
    }
  }, [currentStep, isBDOCurrencyFlowForEffect]);
  // Dark and Darker currency: when reaching step 2 (Details), default min_quantity to 10
  const isDarkAndDarkerCurrencyFlowForEffect = formData.game === 'Dark and Darker' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (currentStep === 2 && isDarkAndDarkerCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity === 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 10 }));
    }
  }, [currentStep, isDarkAndDarkerCurrencyFlowForEffect]);
  // Delta Force currency: when reaching step 2 (Details), default min_quantity to 5
  const isDeltaForceCurrencyFlowForEffect = formData.game === 'Delta Force' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (currentStep === 2 && isDeltaForceCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity === 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 5 }));
    }
  }, [currentStep, isDeltaForceCurrencyFlowForEffect]);
  const isDiablo4CurrencyFlowForEffect = formData.game === 'Diablo 4' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (currentStep === 2 && isDiablo4CurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity === 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 5000 }));
    }
  }, [currentStep, isDiablo4CurrencyFlowForEffect]);
  const isDonutSMPCurrencyFlowForEffect = formData.game === 'DonutSMP' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (currentStep === 2 && isDonutSMPCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity === 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 10 }));
    }
  }, [currentStep, isDonutSMPCurrencyFlowForEffect]);
  const isEASportsFCCurrencyFlowForEffect = formData.game === 'EA Sports FC' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (currentStep === 2 && isEASportsFCCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity === 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 10 }));
    }
  }, [currentStep, isEASportsFCCurrencyFlowForEffect]);
  const isEASportsFCMobileCurrencyFlowForEffect = formData.game === 'EA Sports FC Mobile' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  useEffect(() => {
    if (currentStep === 2 && isEASportsFCMobileCurrencyFlowForEffect && (formData.min_quantity == null || formData.min_quantity === '' || formData.min_quantity === 1)) {
      setFormData(prev => ({ ...prev, min_quantity: 10 }));
    }
  }, [currentStep, isEASportsFCMobileCurrencyFlowForEffect]);

  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagePreviews, setImagePreviews] = useState(ad?.image_urls || []);
  const [submitting, setSubmitting] = useState(false);

  // Determine total steps: 4 for 8 Ball Pool / Albion Online currency (Game+Server?, Details, Pricing, Preview), 5 for top-ups, 6 for others
  const is8BPCurrencyFlow = formData.game === '8 Ball Pool' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isAlbionCurrencyFlow = formData.game === 'Albion Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isACNHCurrencyFlow = formData.game === 'Animal Crossing: New Horizons' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isArcRaidersCurrencyFlow = formData.game === 'Arc Raiders' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isBDOCurrencyFlow = formData.game === 'Black Desert Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isDarkAndDarkerCurrencyFlow = formData.game === 'Dark and Darker' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isDeltaForceCurrencyFlow = formData.game === 'Delta Force' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isDiablo4CurrencyFlow = formData.game === 'Diablo 4' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isDigimonSuperRumbleCurrencyFlow = formData.game === 'Digimon Super Rumble' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isDonutSMPCurrencyFlow = formData.game === 'DonutSMP' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isEASportsFCCurrencyFlow = formData.game === 'EA Sports FC' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isEASportsFCMobileCurrencyFlow = formData.game === 'EA Sports FC Mobile' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isEldenRingCurrencyFlow = formData.game === 'Elden Ring' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isElderScrollsOnlineCurrencyFlow = formData.game === 'Elder Scrolls Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isEscapeFromTarkovCurrencyFlow = formData.game === 'Escape from Tarkov' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isFinalFantasyXIVCurrencyFlow = formData.game === 'Final Fantasy XIV' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isFischCurrencyFlow = formData.game === 'Fisch' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isForzaHorizon5CurrencyFlow = formData.game === 'Forza Horizon 5' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isGrowAGardenCurrencyFlow = formData.game === 'Grow a Garden' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isLostArkCurrencyFlow = formData.game === 'Lost Ark' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isMinecraftCurrencyFlow = formData.game === 'Minecraft' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isNewWorldCurrencyFlow = formData.game === 'New World' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isOldSchoolRuneScapeCurrencyFlow = formData.game === 'Old School RuneScape' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isPathOfExileCurrencyFlow = formData.game === 'Path of Exile' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isPathOfExile2CurrencyFlow = formData.game === 'Path of Exile 2' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isRobloxCurrencyFlow = formData.game === 'Roblox' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isRuneScape3CurrencyFlow = formData.game === 'RuneScape 3' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isThroneAndLibertyCurrencyFlow = formData.game === 'Throne and Liberty' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isWarframeCurrencyFlow = formData.game === 'Warframe' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isWoWClassicCurrencyFlow = formData.game === 'WoW Classic' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isWoWMistsOfPandariaCurrencyFlow = formData.game === 'WoW Mists of Pandaria' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isFallout76CurrencyFlow = formData.game === 'Fallout 76' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const isPokemonGoCurrencyFlow = formData.game === 'Pokemon Go' && (formData.service_type === 'currency' || initialServiceType === 'currency');
  const totalSteps = (is8BPCurrencyFlow || isAlbionCurrencyFlow || isACNHCurrencyFlow || isArcRaidersCurrencyFlow || isBDOCurrencyFlow || isDarkAndDarkerCurrencyFlow || isDeltaForceCurrencyFlow || isDiablo4CurrencyFlow || isDigimonSuperRumbleCurrencyFlow || isDonutSMPCurrencyFlow || isEASportsFCCurrencyFlow || isEASportsFCMobileCurrencyFlow || isEldenRingCurrencyFlow || isElderScrollsOnlineCurrencyFlow || isEscapeFromTarkovCurrencyFlow || isFallout76CurrencyFlow || isFinalFantasyXIVCurrencyFlow || isFischCurrencyFlow || isForzaHorizon5CurrencyFlow || isGrowAGardenCurrencyFlow || isLostArkCurrencyFlow || isMinecraftCurrencyFlow || isNewWorldCurrencyFlow || isOldSchoolRuneScapeCurrencyFlow || isPathOfExileCurrencyFlow || isPathOfExile2CurrencyFlow || isRobloxCurrencyFlow || isRuneScape3CurrencyFlow || isThroneAndLibertyCurrencyFlow || isWarframeCurrencyFlow || isWoWClassicCurrencyFlow || isWoWMistsOfPandariaCurrencyFlow || isPokemonGoCurrencyFlow) ? 4 : ((initialServiceType || formData.service_type) === 'top-ups' ? 5 : 6);
  
  // All games from Header
  const allGames = [
    '8 Ball Pool',
    '99 Nights in the Forest',
    'Adopt Me',
    'Albion Online',
    'All Star Tower Defense X',
    'Animal Crossing: New Horizons',
    'Anime Defenders',
    'Anime Vanguards',
    'Apex Legends',
    'Arc Raiders',
    'Arena Breakout',
    'Arena Breakout: Infinite',
    'ARK: Survival Ascended',
    'Arknights',
    'Ayakashi Rumble!',
    'Battlefield',
    'Battlenet',
    'Black Clover M',
    'Black Desert Online',
    'Bleach: Brave Souls',
    'Blood Strike',
    'Blox Fruits',
    'Brawl Stars',
    'Call of Duty',
    'Call of Duty Mobile',
    'Car Parking Multiplayer',
    'Chaos Zero Nightmare',
    'Chocotto Land',
    'Clash of Clans',
    'Clash Royale',
    'Cookie Run: Kingdom',
    'Counter-Strike 2',
    'Crunchyroll',
    'Dark and Darker',
    'DarkOrbit',
    'DayZ',
    'Dead By Daylight',
    'Dead Rails',
    'Delta Force',
    'Destiny 2',
    'Diablo 4',
    'Diablo Immortal',
    'Digimon Super Rumble',
    'Discord',
    'DonutSMP',
    'Dota 2',
    'Dragon Ball Legends',
    'Dragon Ball Z Dokkan Battle',
    'Duet Night Abyss',
    'EA Sports FC',
    'EA Sports FC Mobile',
    'eFootball',
    'Elden Ring',
    'Elder Scrolls Online',
    'Epic Games',
    'Epic Seven',
    'Escape from Tarkov',
    'Etheria: Restart',
    'Fallout 76',
    'Fate/Grand Order',
    'Final Fantasy XIV',
    'Fisch',
    'Fortnite',
    'Forza Horizon 5',
    'Garena Free Fire',
    'Genshin Impact',
    'Girls Frontline 2: Exilium',
    'Goddess of Victory: Nikke',
    'Golden HoYeah',
    'Granblue Fantasy',
    'Grand Roleplay',
    'Grand Theft Auto 5',
    'Grow a Garden',
    'Hay Day',
    'Honkai Impact 3rd',
    'Honkai: Star Rail',
    'Houchi Shoujo',
    'Identity V',
    'Jailbreak',
    "JoJo's Bizarre Adventure: Ora Ora Overdrive",
    'Jujutsu Infinite',
    'Jujutsu Kaisen Phantom Parade',
    'Kaiju No. 8',
    'King Legacy',
    'Knight Online',
    'Knives Out',
    'Last Island of Survival',
    'Last War: Survival',
    'League of Legends',
    'League of Legends: Wild Rift',
    'Livly Island',
    'Lords Mobile',
    'Lost Ark',
    'Madoka Magica Magia Exedra',
    'Marvel Contest of Champions',
    'Marvel Rivals',
    'Metin2',
    'Millennium War Aigis',
    'Minecraft',
    'Mobile Legends',
    'Monster Hunter Now',
    'Monster Strike',
    'Netflix',
    'New World',
    'Old School RuneScape',
    'One Piece Bounty Rush',
    'One Piece Treasure Cruise',
    'Origin',
    'Overwatch 2',
    'Path of Exile',
    'Path of Exile 2',
    'Persona 5: The Phantom X',
    'Plants vs Brainrots',
    'PlayStation',
    'Pokecolo',
    'Pokecolo Twin',
    'Pokemon Go',
    'Pokemon TCG Pocket',
    'Princess Connect! Re:Dive',
    'Professional Baseball Spirits A',
    'Project SEKAI',
    'PUBG',
    'PUBG Mobile',
    'Puzzle and Dragons',
    'Raid: Shadow Legends',
    'Rainbow Six Siege X',
    'Rise of Kingdoms',
    'Roblox',
    'Rocket League',
    'Royale Online',
    'RuneScape 3',
    'Rust',
    'SD Gundam G Generation ETERNAL',
    'Sea of Thieves',
    'Seven Knights Re:BIRTH',
    'Shadowverse',
    'Silver and Blood',
    'Sky: Children of the Light',
    'Spotify',
    'Standoff 2',
    'Steal a Brainrot',
    'Stella Sora',
    'Summoners War',
    'The Finals',
    'The Forge',
    'The Seven Deadly Sins',
    'Throne and Liberty',
    'Tower of Fantasy',
    'Ubisoft Connect',
    'Uma Musume Pretty Derby',
    'unVEIL the world',
    'Valorant',
    'Volleyball Legends',
    'War Thunder',
    'Warframe',
    'Wartune Ultra',
    'Watcher of Realms',
    'Where Winds Meet',
    'White Cat Project',
    'Whiteout Survival',
    'WolfTeam',
    'World of Tanks',
    'World of Warcraft',
    'World of Warships',
    'WoW Classic',
    'WoW Mists of Pandaria',
    'Wuthering Waves',
    'Xbox',
    'Yo-kai Watch: Wibble Wobble',
    'Yu-Gi-Oh! Master Duel',
    'Zenless Zone Zero',
    'Zula'
  ];
  
  // Games with top-ups pages - ONLY games that actually have top-ups pages and are active
  // Games with top-ups pages - ONLY the games from user's provided list (in exact order)
  const gamesWithTopUps = [
    '99-nights-in-the-forest',           // 99 Nights in the Forest Diamonds
    'apex-legends',                      // Apex Legends Top Ups
    'arena-breakout',                    // Arena Breakout Top Ups
    'blood-strike',                      // Blood Strike Gold
    'brawl-stars',                       // Brawl Stars Gems
    'cod-call-of-duty',                  // Call of Duty Points
    'call-of-duty-mobile',               // Call of Duty Mobile CP
    'clash-of-clans',                    // Clash of Clans Gems
    'clash-royale',                      // Clash Royale Gems
    'crunchyroll',                       // Crunchyroll Subscription
    'dead-by-daylight',                 // Dead By Daylight Auric Cells
    'delta-force',                      // Delta Force Delta Coins
    'destiny-2',                        // Destiny 2 Silver
    'ea-sports-fc',                     // EA Sports FC Points
    'efootball',                        // eFootball Coins
    'elder-scrolls-online',             // Elder Scrolls Online Crowns
    'etheria-restart',                  // Etheria: Restart Crystals
    'fortnite',                         // Fortnite V-Bucks
    'garena-free-fire',                 // Garena Free Fire Diamonds
    'genshin-impact',                    // Genshin Impact Top Ups
    'honkai-star-rail',                 // Honkai: Star Rail Oneiric Shards
    'identity-v',                       // Identity V Echoes
    'league-of-legends',                // League of Legends Riot Points
    'league-of-legends-wild-rift',      // League of Legends: Wild Rift Wild Cores
    'marvel-rivals',                    // Marvel Rivals Lattice
    'minecraft',                        // Minecraft Minecoins
    'mobile-legends',                   // Mobile Legends Diamonds
    'overwatch-2',                      // Overwatch 2 Coins
    'path-of-exile-2',                  // Path of Exile 2 Points
    'playstation',                      // PlayStation Subscription
    'pokemon-go',                       // Pokemon Go Top Ups
    'pokemon-tcg-pocket',               // Pokemon TCG Pocket Poke Gold
    'pubg',                             // PUBG G-Coins
    'pubg-mobile',                      // PUBG Mobile UC
    'rainbow-six-siege-x',              // Rainbow Six Siege X Credits
    'roblox',                           // Roblox Top Ups
    'rocket-league',                    // Rocket League Credits
    'rust',                             // Rust Coins
    'sea-of-thieves',                   // Sea of Thieves Ancient Coins
    'spotify',                          // Spotify Subscription
    'valorant',                         // Valorant Points
    'whiteout-survival',                // Whiteout Survival Frost Stars
    'world-of-warcraft',                // World of Warcraft Subscription
    'wuthering-waves',                  // Wuthering Waves Lunites
    'zenless-zone-zero'                 // Zenless Zone Zero Monochromes
  ];

  // Games that support currency ads - ONLY these games in this order
  const gamesWithCurrency = [
    '8 Ball Pool',
    'Albion Online',
    'Animal Crossing: New Horizons',
    'Arc Raiders',
    'Black Desert Online',
    'Dark and Darker',
    'Delta Force',
    'Diablo 4',
    'Digimon Super Rumble',
    'DonutSMP',
    'EA Sports FC',
    'EA Sports FC Mobile',
    'Elden Ring',
    'Elder Scrolls Online',
    'Escape from Tarkov',
    'Fallout 76',
    'Final Fantasy XIV',
    'Fisch',
    'Forza Horizon 5',
    'Grow a Garden',
    'Lost Ark',
    'Minecraft',
    'New World',
    'Old School RuneScape',
    'Path of Exile',
    'Path of Exile 2',
    'Pokemon Go',
    'Roblox',
    'RuneScape 3',
    'Throne and Liberty',
    'Warframe',
    'WoW Classic',
    'WoW Mists of Pandaria'
  ];

  // Game name mapping (slug to display name)
  const gameNameMap = {
    '99-nights-in-the-forest': '99 Nights in the Forest',
    'amazon': 'Amazon',
    'anime-vanguards': 'Anime Vanguards',
    'apex-legends': 'Apex Legends',
    'arena-breakout': 'Arena Breakout',
    'arena-breakout-infinite': 'Arena Breakout: Infinite',
    'bigo-live': 'Bigo Live',
    'blood-strike': 'Blood Strike',
    'brawl-stars': 'Brawl Stars',
    'call-of-duty-mobile': 'Call of Duty Mobile',
    'cod-call-of-duty': 'Call of Duty',
    'clash-of-clans': 'Clash of Clans',
    'clash-royale': 'Clash Royale',
    'counter-strike-2': 'Counter-Strike 2',
    'crunchyroll': 'Crunchyroll',
    'dead-by-daylight': 'Dead By Daylight',
    'delta-force': 'Delta Force',
    'destiny-2': 'Destiny 2',
    'ea-play': 'EA Play',
    'ea-sports-fc': 'EA Sports FC',
    'efootball': 'eFootball',
    'elder-scrolls-online': 'Elder Scrolls Online',
    'escape-from-tarkov': 'Escape from Tarkov',
    'etheria-restart': 'Etheria: Restart',
    'fortnite': 'Fortnite',
    'garena-free-fire': 'Garena Free Fire',
    'genshin-impact': 'Genshin Impact',
    'grand-roleplay': 'Grand Roleplay',
    'growtopia': 'Growtopia',
    'honkai-star-rail': 'Honkai: Star Rail',
    'honor-of-kings': 'Honor of Kings',
    'hypixel': 'Hypixel',
    'identity-v': 'Identity V',
    'league-of-legends': 'League of Legends',
    'league-of-legends-wild-rift': 'League of Legends: Wild Rift',
    'love-and-deepspace': 'Love and Deepspace',
    'marvel-rivals': 'Marvel Rivals',
    'miliastra-wonderland': 'Miliastra Wonderland',
    'minecraft': 'Minecraft',
    'mobile-legends': 'Mobile Legends',
    'nintendo': 'Nintendo',
    'overwatch-2': 'Overwatch 2',
    'path-of-exile-2': 'Path of Exile 2',
    'playstation': 'PlayStation',
    'pokemon-go': 'Pokemon Go',
    'pokemon-tcg-pocket': 'Pokemon TCG Pocket',
    'poppo-live': 'Poppo Live',
    'pubg': 'PUBG',
    'pubg-mobile': 'PUBG Mobile',
    'rainbow-six-siege': 'Rainbow Six Siege',
    'rainbow-six-siege-x': 'Rainbow Six Siege X',
    'roblox': 'Roblox',
    'rocket-league': 'Rocket League',
    'rust': 'Rust',
    'sea-of-thieves': 'Sea of Thieves',
    'spotify': 'Spotify',
    'super-sus': 'Super Sus',
    'tiktok': 'TikTok',
    'valorant': 'Valorant',
    'whiteout-survival': 'Whiteout Survival',
    'world-of-warcraft': 'World of Warcraft',
    'warcraft': 'Warcraft',
    'wuthering-waves': 'Wuthering Waves',
    'youtube': 'YouTube',
    'zenless-zone-zero': 'Zenless Zone Zero'
  };

  // Filter games based on service type
  const getAvailableGames = (serviceType) => {
    if (serviceType === 'top-ups') {
      // Only show games with top-ups pages
      return gamesWithTopUps
        .map(slug => gameNameMap[slug])
        .filter(name => name && allGames.includes(name))
        .sort();
    }
    // For other service types, show all games
    return allGames;
  };

  const serviceTypes = ['boosting', 'accounts', 'top-ups', 'items', 'payments', 'currency'];
  // Default platforms for all games
  const defaultPlatforms = ['PC', 'PlayStation', 'Xbox', 'Android', 'iOS', 'Switch'];
  
  const normalAccountsGames = new Set([
    'All Star Tower Defense X',
    'Anime Vanguards',
    'Arc Raiders',
    'Arena Breakout',
    'Arena Breakout: Infinite',
    'Battlenet',
    'Bleach: Brave Souls',
    'Blood Strike',
    'Blox Fruits',
    'Brawl Stars',
    'Car Parking Multiplayer',
    'Chocotto Land',
    'Crunchyroll',
    'Dark and Darker',
    'DarkOrbit',
    'Dead Rails',
    'Delta Force',
    'Diablo 4',
    'Diablo Immortal',
    'Digimon Super Rumble',
    'Discord',
    'DonutSMP',
    'Duet Night Abyss',
    'Elden Ring',
    'Epic Games',
    'Final Fantasy XIV',
    'Fisch',
    'Golden HoYeah',
    'Houchi Shoujo',
    'Identity V',
    'Jujutsu Kaisen Phantom Parade',
    'Knight Online',
    'Knives Out',
    'Last Island of Survival',
    'Last War: Survival'
  ]);

  // Games that explicitly do NOT require platform selection for accounts
  const noPlatformGames = new Set([
    ...normalAccountsGames,
    '8 Ball Pool',
    'Arknights',
    'Albion Online',
    'Battlefield',
    'DayZ',
    'Etheria: Restart'
  ]);

  const requiresPlatformForAccounts = (gameName) => {
    if (!gameName) return true;
    return !noPlatformGames.has(gameName);
  };

  // External config-aware helpers
  const resolvePlatformOptions = (gameName) => {
    const cfg = getGameFilterConfig(gameName);
    if (cfg?.platformOptions) return cfg.platformOptions;
    return getPlatformsForGame(gameName || '');
  };

  const resolveRequiresPlatform = (gameName) => {
    const cfg = getGameFilterConfig(gameName);
    if (cfg?.normal) return false;
    if (cfg && 'requiresPlatform' in cfg) return cfg.requiresPlatform;
    
    // If game has Device/Region/etc in accountAttributes, don't require platform
    const attrs = resolveAccountAttributes(gameName);
    if (attrs) {
      if (attrs.type === 'multi-select' && attrs.fields) {
        const hasDeviceOrRegion = attrs.fields.some(f => 
          f.name === 'device' || f.name === 'region' || f.name === 'server'
        );
        if (hasDeviceOrRegion) return false;
      } else if (attrs.type === 'select') {
        const label = attrs.label?.toLowerCase() || '';
        if (label.includes('device') || label.includes('region') || label.includes('server')) {
          return false;
        }
      }
    }
    
    return requiresPlatformForAccounts(gameName);
  };

  const resolveAccountAttributes = (gameName) => {
    const cfg = getGameFilterConfig(gameName);
    if (cfg?.normal) return null; // Normal games have no account attributes
    if (cfg && 'accountAttributes' in cfg) return cfg.accountAttributes;
    return getAccountAttributes(gameName);
  };

  // Game-specific platform restrictions
  const getPlatformsForGame = (gameName) => {
    if (gameName === 'Adopt Me') {
      return ['PC', 'PlayStation', 'Xbox'];
    }
    if (gameName === 'Call of Duty') {
      return ['PlayStation', 'Xbox', 'Steam', 'Battlenet'];
    }
    if (gameName === 'Call of Duty Mobile') {
      return ['Android & iOS', 'Android', 'iOS'];
    }
    if (gameName === 'Epic Seven') {
      return ['iOS', 'Android'];
    }
    if (gameName === 'Forza Horizon 5') {
      return ['PC', 'Xbox', 'PS5'];
    }
    if (gameName === 'Grand Theft Auto 5') {
      return ['PC - Legacy', 'PlayStation 4', 'Xbox One', 'PlayStation 5', 'Xbox Series X/S', 'PC - Enhanced'];
    }
    return defaultPlatforms;
  };
  
  const platforms = resolvePlatformOptions(formData.game || '');
  
  // Game-specific account attributes
  const getAccountAttributes = (gameName) => {
    const attributes = {
      'Fortnite': {
        type: 'select',
        label: 'Account Type',
        options: ['OG Account', 'Stacked', 'Original Email', 'Save The World', 'Other']
      },
      'Adopt Me': {
        type: 'multi-select',
        fields: [
          {
            name: 'age_up_potions',
            label: 'Age-Up Potions',
            options: ['0 - 49 Pots', '50 - 99 Pots', '100 - 149 Pots', '150 - 199 Pots', '200 - 249 Pots', '250+ Pots']
          },
          {
            name: 'bucks',
            label: 'Bucks',
            options: ['0 - 9.99k Bucks', '10 - 49k Bucks', '50 - 99k Bucks', '100 - 199k Bucks', '200 - 299k Bucks', '300 - 399k Bucks', '400 - 499k Bucks', '500k+ Bucks']
          }
        ]
      },
      'Roblox': {
        type: 'multi-select',
        fields: [
          {
            name: 'robux',
            label: 'Robux',
            options: ['0 - 99 Robux', '100 - 499 Robux', '500 - 999 Robux', '1k - 4.9k Robux', '5k - 9.9k Robux', '10k+ Robux']
          },
          {
            name: 'game_passes',
            label: 'Game Passes',
            options: ['None', '1-5 Passes', '6-10 Passes', '11-20 Passes', '21+ Passes']
          },
          {
            name: 'limited_items',
            label: 'Limited Items',
            options: ['None', '1-5 Items', '6-10 Items', '11-20 Items', '21+ Items']
          }
        ]
      },
      'Genshin Impact': {
        type: 'select',
        label: 'Region',
        options: ['NA', 'EU', 'Asia', 'SAR']
      },
      'Valorant': {
        type: 'multi-select',
        fields: [
          {
            name: 'rank',
            label: 'Rank',
            options: ['Unranked', 'Iron', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Ascendant', 'Immortal', 'Radiant']
          },
          {
            name: 'agents',
            label: 'Agents Unlocked',
            options: ['0-5 Agents', '6-10 Agents', '11-15 Agents', '16+ Agents', 'All Agents']
          },
          {
            name: 'skins',
            label: 'Premium Skins',
            options: ['None', '1-5 Skins', '6-10 Skins', '11-20 Skins', '21+ Skins']
          }
        ]
      },
      'Albion Online': {
        type: 'select',
        label: 'Server',
        options: ['Albion Americas', 'Albion Asia', 'Albion Europe']
      },
      'Apex Legends': {
        type: 'select',
        label: 'Device',
        options: ['PC', 'PlayStation', 'Xbox']
      },
      'ARK: Survival Ascended': {
        type: 'select',
        label: 'Device',
        options: ['PC', 'PlayStation', 'Xbox']
      },
      'Arknights': {
        type: 'select',
        label: 'Region',
        options: ['Global', 'Japan', 'China']
      },
      'Battlefield': {
        type: 'select',
        label: 'Series',
        options: ['Battlefield V', 'Battlefield 2042', 'Battlefield 1', 'Battlefield 3', 'Battlefield 4', 'Battlefield 6']
      },
      'Black Desert Online': {
        type: 'select',
        label: 'Device',
        options: ['PC', 'PlayStation', 'Xbox']
      },
      'EA Sports FC': {
        type: 'multi-select',
        fields: [
          {
            name: 'device',
            label: 'Device',
            options: ['PC', 'Xbox', 'PlayStation']
          },
          {
            name: 'game_series',
            label: 'Game Series',
            options: ['EA FC 24', 'EA FC 25', 'EA FC 26']
          }
        ]
      },
      'eFootball': {
        type: 'select',
        label: 'Game Series',
        options: ['eFootball 2023', 'eFootball 2024', 'eFootball 2025', 'eFootball 2026']
      },
      'Chaos Zero Nightmare': {
        type: 'select',
        label: 'Region',
        options: ['Global', 'Asia']
      },
      'Garena Free Fire': {
        type: 'select',
        label: 'Region',
        options: ['Thailand', 'Vietnam', 'Malaysia', 'Brazil', 'LATAM', 'MENA', 'India', 'Taiwan', 'Europe', 'Pakistan', 'Bangladesh', 'North America']
      },
      'DayZ': {
        type: 'select',
        label: 'Device',
        options: ['PC', 'PlayStation', 'Xbox']
      },
      'Dragon Ball Legends': {
        type: 'select',
        label: 'Device',
        options: ['Android', 'Android & iOS', 'iOS']
      },
      'Elder Scrolls Online': {
        type: 'multi-select',
        fields: [
          {
            name: 'device',
            label: 'Device',
            options: ['PC', 'PlayStation', 'Xbox']
          },
          {
            name: 'region',
            label: 'Region',
            options: ['EU', 'NA']
          }
        ]
      },
      'Fallout 76': {
        type: 'select',
        label: 'Device',
        options: ['PC', 'PlayStation', 'Xbox']
      },
      'Epic Seven': {
        type: 'multi-select',
        fields: [
          {
            name: 'device',
            label: 'Device',
            options: ['iOS', 'Android']
          },
          {
            name: 'region',
            label: 'Region',
            options: ['Global', 'EU', 'Asia', 'Korea']
          }
        ]
      },
      'Etheria: Restart': {
        type: 'select',
        label: 'Region',
        options: ['EU', 'NA', 'SEA']
      },
      'Girls Frontline 2: Exilium': {
        type: 'select',
        label: 'Region',
        options: ['Darkwinter Global', 'Haoplay Global', 'Haoplay Korea', 'Haoplay Japan', 'Haoplay Asia', 'Durin']
      },
      'Honkai Impact 3rd': {
        type: 'select',
        label: 'Region',
        options: ['EU', 'NA', 'CN', 'JP', 'TW/HK/MO', 'KR', 'SEA']
      },
      'Honkai: Star Rail': {
        type: 'select',
        label: 'Region',
        options: ['Europe', 'America', 'Asia', 'TW/HK/MO']
      },
      'Call of Duty': {
        type: 'multi-select',
        fields: [
          {
            name: 'cod_game',
            label: 'Game',
            options: ['Modern Warfare II', 'Warzone 4', 'Vanguard', 'Cold War', 'Modern Warfare I', 'Modern Warfare III', 'Black Ops 6', 'Black Ops 7', 'Other']
          }
        ]
      },
      'Call of Duty Mobile': {
        type: 'multi-select',
        fields: [
          {
            name: 'codm_game',
            label: 'Game',
            options: ['CoD Mobile', 'CoD Mobile Warzone', 'Other']
          }
        ]
      },
      'League of Legends': {
        type: 'multi-select',
        fields: [
          {
            name: 'server',
            label: 'Server',
            options: ['Latin America North', 'Latin America South', 'Oceania', 'Turkey', 'Japan', 'North America', 'PBE', 'Singapore', 'Philippines', 'Vietnam', 'Thailand', 'Taiwan', 'Middle East']
          },
          {
            name: 'rank',
            label: 'Rank',
            options: ['Unranked', 'Iron', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Emerald', 'Diamond', 'Master', 'Grandmaster', 'Challenger', 'Other']
          },
          {
            name: 'champions',
            label: 'Champions Unlocked',
            options: ['0-20 Champions', '21-50 Champions', '51-80 Champions', '81-120 Champions', '121+ Champions', 'All Champions']
          },
          {
            name: 'skins',
            label: 'Skins',
            options: ['0 Skins', '1-9 Skins', '10-24 Skins', '25-49 Skins', '50-99 Skins', '100-199 Skins', '200-299 Skins', '300-399 Skins', '400-499 Skins', '500-699 Skins', '700-899 Skins', '900-1099 Skins', '1100-1399 Skins', '1400+ Skins', 'Other']
          },
          {
            name: 'blue_essence',
            label: 'Blue Essence',
            options: ['0-19K BE', '20-40K BE', '41-60K BE', '61-80K BE', '81-100K BE', '100K+ BE', 'Other']
          }
        ]
      },
      'Minecraft': {
        type: 'multi-select',
        fields: [
          {
            name: 'version',
            label: 'Edition',
            options: ['Java Edition', 'Bedrock Edition', 'Both']
          },
          {
            name: 'items',
            label: 'Rare Items',
            options: ['None', '1-5 Items', '6-10 Items', '11-20 Items', '21+ Items']
          },
          {
            name: 'builds',
            label: 'Builds/Worlds',
            options: ['None', '1-3 Builds', '4-10 Builds', '11+ Builds']
          }
        ]
      },
      'Clash Royale': {
        type: 'multi-select',
        fields: [
          {
            name: 'trophies',
            label: 'Trophies',
            options: ['0 - 999', '1k - 2.9k', '3k - 4.9k', '5k - 5.9k', '6k - 6.9k', '7k+']
          },
          {
            name: 'king_level',
            label: 'King Level',
            options: ['Level 1-5', 'Level 6-10', 'Level 11-13', 'Level 14']
          },
          {
            name: 'cards',
            label: 'Cards Unlocked',
            options: ['0-50 Cards', '51-80 Cards', '81-100 Cards', '101+ Cards', 'All Cards']
          }
        ]
      },
      'Apex Legends': {
        type: 'multi-select',
        fields: [
          {
            name: 'rank',
            label: 'Rank',
            options: ['Rookie', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Master', 'Predator']
          },
          {
            name: 'legends',
            label: 'Legends Unlocked',
            options: ['0-5 Legends', '6-10 Legends', '11-15 Legends', '16+ Legends', 'All Legends']
          },
          {
            name: 'heirlooms',
            label: 'Heirlooms',
            options: ['None', '1 Heirloom', '2 Heirlooms', '3+ Heirlooms']
          }
        ]
      },
      'Call of Duty': {
        type: 'multi-select',
        fields: [
          {
            name: 'rank',
            label: 'Rank',
            options: ['Level 1-20', 'Level 21-40', 'Level 41-60', 'Level 61-80', 'Level 81-100', 'Prestige']
          },
          {
            name: 'weapons',
            label: 'Weapons Unlocked',
            options: ['0-25%', '26-50%', '51-75%', '76-99%', '100%']
          },
          {
            name: 'skins',
            label: 'Operator Skins',
            options: ['None', '1-5 Skins', '6-10 Skins', '11-20 Skins', '21+ Skins']
          }
        ]
      }
    };
    
    if (normalAccountsGames.has(gameName)) {
      return null;
    }

    return attributes[gameName] || {
      type: 'select',
      label: 'Account Type',
      options: ['Standard Account', 'Premium Account', 'Starter Account', 'Other']
    };
  };
  
  // Get game icon helper - matches Header.js getGameIcon function
  const getGameIcon = (gameName) => {
    // Special cases first - these have icons in /main/games/icons/
    // Handle both "Counter Strike 2" and "Counter-Strike 2" variations
    if (gameName === 'Counter Strike 2' || gameName === 'Counter-Strike 2' || (gameName?.includes('Counter') && gameName?.includes('Strike') && gameName?.includes('2'))) {
      return '/main/games/icons/cs2.png';
    }
    
    if (gameName === 'DonutSMP') {
      return '/main/games/icons/donutsmp.jpg';
    }
    
    if (gameName === 'Hypixel') {
      return '/main/games/icons/hypixel.jpg';
    }
    
    const iconMap = {
      'Apex Legends': 'ApexLegends.png',
      'Arc Raiders': 'ARcraiders.png',
      'Clash Royale': 'Clashroyale.png',
      'COD Call of Duty': 'Callofduty.png',
      'Call of Duty': 'Callofduty.png',
      'EA Sports FC': 'EAsports.png',
      'Escape from Tarkov': 'Escapefromtarkov.png',
      'Fortnite': 'Fortnite.png',
      'League of Legends': 'Leagueoflegends.png',
      'Minecraft': 'Minecraft.png',
      'Old School RuneScape': 'Oldschoolrunescape.png',
      'Path of Exile 2': 'pathexhile.png',
      'Rainbow Six Siege': 'Rainbowsix.png',
      'Roblox': 'Roblox.png',
      'Rocket League': 'rocketleague.png',
      'Valorant': 'Valorant.png',
      'Warcraft': 'Worldofwarcraft.png'
    };
    
    if (gameName === '8 Ball Pool') {
      return '/main/games/icons/8bp.webp';
    }
    if (gameName === 'Kaiju No. 8') {
      return '/main/games/icons/kaiju8.jpg';
    }
    if (gameName === '99 Nights in the Forest') {
      return '/main/games/icons/99nitf.webp';
    }
    if (gameName === 'Adopt Me') {
      return '/main/games/icons/adoptme.jpeg';
    }
    if (gameName === 'Albion Online') {
      return '/main/games/icons/alibiononline.webp';
    }
    if (gameName === 'All Star Tower Defense X') {
      return '/main/games/icons/allstartowerdefense.jpeg';
    }
    if (gameName === 'Animal Crossing: New Horizons') {
      return '/main/games/icons/animalcrossing.jpg';
    }
    if (gameName === 'Anime Defenders') {
      return '/main/games/icons/animedefenders.webp';
    }
    if (gameName === 'Anime Vanguards') {
      return '/main/games/icons/animevanguards.jpg';
    }
    if (gameName === 'ARK: Survival Ascended') {
      return '/main/games/icons/arksurvivalascended.webp';
    }
    if (gameName === 'Arknights') {
      return '/main/games/icons/Arknights.png';
    }
    if (gameName === 'Ayakashi Rumble!') {
      return '/main/games/icons/AyakashiRumble.webp';
    }
    if (gameName === 'Battlenet') {
      return '/main/games/icons/battlenet.jpg';
    }
    if (gameName === 'Arena Breakout') {
      return '/main/games/icons/arenabreakout1.jpg';
    }
    if (gameName === 'Arena Breakout: Infinite') {
      return '/main/games/icons/arenabreakout.jpg';
    }
    if (gameName === 'Battlefield') {
      return '/main/games/icons/battlefield6.webp';
    }
    if (gameName === 'Black Clover M') {
      return '/main/games/icons/Blackclover.jpeg';
    }
    if (gameName === 'Black Desert Online') {
      return '/main/games/icons/blackdessertonline.png';
    }
    if (gameName === 'Bleach: Brave Souls') {
      return '/main/games/icons/Bleachbravesouls.webp';
    }
    if (gameName === 'Blood Strike') {
      return '/main/games/icons/bloodstrike.webp';
    }
    if (gameName === 'Blox Fruits') {
      return '/main/games/icons/bloxfruits.jpg';
    }
    if (gameName === 'Brawl Stars') {
      return '/main/games/icons/brawl-stars.webp';
    }
    if (gameName === 'Call of Duty Mobile') {
      return '/main/games/icons/codmobile.webp';
    }
    if (gameName === 'Car Parking Multiplayer') {
      return '/main/games/icons/carparkingmultiplayer.webp';
    }
    if (gameName === 'Chaos Zero Nightmare') {
      return '/main/games/icons/choaszero.webp';
    }
    if (gameName === 'Chocotto Land') {
      return '/main/games/icons/chocottoland.jpg';
    }
    if (gameName === 'Clash of Clans') {
      return '/main/games/icons/clashofclans.jpg';
    }
    if (gameName === 'Cookie Run: Kingdom') {
      return '/main/games/icons/cookierunkingdom.webp';
    }
    if (gameName === 'Crunchyroll') {
      return '/main/games/icons/crunchyroll.jpg';
    }
    if (gameName === 'Dark and Darker') {
      return '/main/games/icons/darkanddarker.jpg';
    }
    if (gameName === 'DarkOrbit') {
      return '/main/games/icons/darkorbit.jpg';
    }
    if (gameName === 'DayZ') {
      return '/main/games/icons/dayz.avif';
    }
    if (gameName === 'Dead By Daylight') {
      return '/main/games/icons/deadbydaylight.jpg';
    }
    if (gameName === 'Dead Rails') {
      return '/main/games/icons/deadrails.jpg';
    }
    if (gameName === 'Delta Force') {
      return '/main/games/icons/deltaforce.jpeg';
    }
    if (gameName === 'Destiny 2') {
      return '/main/games/icons/destiny2.jpeg';
    }
    if (gameName === 'Diablo 4') {
      return '/main/games/icons/diablo4.avif';
    }
    if (gameName === 'Diablo Immortal') {
      return '/main/games/icons/diabloimortal.jpg';
    }
    if (gameName === 'Digimon Super Rumble') {
      return '/main/games/icons/digimonsuperrumble.jpg';
    }
    if (gameName === 'Discord') {
      return '/main/games/icons/discord.jpg';
    }
    if (gameName === 'Dota 2') {
      return '/main/games/icons/dota2.jpg';
    }
    if (gameName === 'Dragon Ball Legends') {
      return '/main/games/icons/dragonballlegends.jpg';
    }
    if (gameName === 'Dragon Ball Z Dokkan Battle') {
      return '/main/games/icons/DragonBallZDokkanBattle.jpg';
    }
    if (gameName === 'Duet Night Abyss') {
      return '/main/games/icons/duetnightabyss.jpg';
    }
    if (gameName === 'EA Sports FC Mobile') {
      return '/main/games/icons/eafcmobile.jpg';
    }
    if (gameName === 'eFootball') {
      return '/main/games/icons/efotball.jpg';
    }
    if (gameName === 'Elden Ring') {
      return '/main/games/icons/eldenring.jpg';
    }
    if (gameName === 'Elder Scrolls Online') {
      return '/main/games/icons/elderscrollsonline.jpg';
    }
    if (gameName === 'Epic Games') {
      return '/main/games/icons/epicgames.jpg';
    }
    if (gameName === 'Epic Seven') {
      return '/main/games/icons/epicseven.jpg';
    }
    if (gameName === 'Etheria: Restart') {
      return '/main/games/icons/etheriarestart.jpg';
    }
    if (gameName === 'Fallout 76') {
      return '/main/games/icons/fallout76.jpg';
    }
    if (gameName === 'Fate/Grand Order') {
      return '/main/games/icons/fategrandorder.jpg';
    }
    if (gameName === 'Final Fantasy XIV') {
      return '/main/games/icons/finalfantasyxix.jpg';
    }
    if (gameName === 'Fisch') {
      return '/main/games/icons/fisch.jpg';
    }
    if (gameName === 'Forza Horizon 5') {
      return '/main/games/icons/forzahorizon5.jpg';
    }
    if (gameName === 'Garena Free Fire') {
      return '/main/games/icons/garenafreefire.jpeg';
    }
    if (gameName === 'Genshin Impact') {
      return '/main/games/icons/genshinimpact.avif';
    }
    if (gameName === 'Girls Frontline 2: Exilium') {
      return '/main/games/icons/girlsfrontline2exilium.jpg';
    }
    if (gameName === 'Golden HoYeah') {
      return '/main/games/icons/goldenhoyeah.png';
    }
    if (gameName === 'Granblue Fantasy') {
      return '/main/games/icons/granbluefantasy.webp';
    }
    if (gameName === 'Grand Roleplay') {
      return '/main/games/icons/grandrp.jpg';
    }
    if (gameName === 'Grand Theft Auto 5') {
      return '/main/games/icons/grandtheftauto.jpg';
    }
    if (gameName === 'Grow a Garden') {
      return '/main/games/icons/growagarden.jpg';
    }
    if (gameName === 'Hay Day') {
      return '/main/games/icons/hayday.jpg';
    }
    if (gameName === 'Honkai Impact 3rd') {
      return '/main/games/icons/honkaimpact3rd.jpg';
    }
    if (gameName === 'Honkai: Star Rail') {
      return '/main/games/icons/honkaistarrail.jpg';
    }
    if (gameName === 'Houchi Shoujo') {
      return '/main/games/icons/houchishouju.jpeg';
    }
    if (gameName === 'Identity V') {
      return '/main/games/icons/identityv.jpg';
    }
    if (gameName === 'Jailbreak') {
      return '/main/games/icons/jailbreak.webp';
    }
    if (gameName === "JoJo's Bizarre Adventure: Ora Ora Overdrive") {
      return '/main/games/icons/jojoOraOra.webp';
    }
    if (gameName === 'Jujutsu Infinite') {
      return '/main/games/icons/jujutsuinfinite.jpeg';
    }
    if (gameName === 'Jujutsu Kaisen Phantom Parade') {
      return '/main/games/icons/JujutsuKaisenPhantomParade.jpeg';
    }
    if (gameName === 'King Legacy') {
      return '/main/games/icons/kinglegacy.jpg';
    }
    if (gameName === 'Knight Online') {
      return '/main/games/icons/knightonline.jpg';
    }
    if (gameName === 'Knives Out') {
      return '/main/games/icons/knivesout.jpg';
    }
    if (gameName === 'Last Island of Survival') {
      return '/main/games/icons/lastislandofsurvival.avif';
    }
    if (gameName === 'Last War: Survival') {
      return '/main/games/icons/lastwarsurvival.jpg';
    }
    if (gameName === 'League of Legends') {
      return '/main/games/icons/leagueoflegends.jpg';
    }
    if (gameName === 'League of Legends: Wild Rift') {
      return '/main/games/icons/leagueoflegendswildrift.jpg';
    }
    if (gameName === 'Livly Island') {
      return '/main/games/icons/livlyisland.png';
    }
    if (gameName === 'Lords Mobile') {
      return '/main/games/icons/lordsmobile.jpg';
    }
    if (gameName === 'Lost Ark') {
      return '/main/games/icons/lostark.jpg';
    }
    if (gameName === 'Madoka Magica Magia Exedra') {
      return '/main/games/icons/MadokaMagicaMagiaExedra.jpg';
    }
    if (gameName === 'Marvel Contest of Champions') {
      return '/main/games/icons/marvelcontestofchampions.jpg';
    }
    if (gameName === 'Marvel Rivals') {
      return '/main/games/icons/marvelrivals.jpg';
    }
    if (gameName === 'Metin2') {
      return '/main/games/icons/metin2.jpg';
    }
    if (gameName === 'Millennium War Aigis') {
      return '/main/games/icons/MillenniumWarAigis.webp';
    }
    if (gameName === 'Mobile Legends') {
      return '/main/games/icons/mobilelegends.webp';
    }
    if (gameName === 'Monster Hunter Now') {
      return '/main/games/icons/MonsterHunterNow.jpeg';
    }
    if (gameName === 'Monster Strike') {
      return '/main/games/icons/monsterstrike.jpg';
    }
    if (gameName === 'Netflix') {
      return '/main/games/icons/netflix.jpg';
    }
    if (gameName === 'New World') {
      return '/main/games/icons/newworld.jpg';
    }
    if (gameName === 'One Piece Bounty Rush') {
      return '/main/games/icons/OnePieceBountyRush.jpg';
    }
    if (gameName === 'One Piece Treasure Cruise') {
      return '/main/games/icons/OnePieceTreasureCruise.jpg';
    }
    if (gameName === 'Origin') {
      return '/main/games/icons/orgin.jpg';
    }
    if (gameName === 'Overwatch 2') {
      return '/main/games/icons/overwatch2.jpg';
    }
    if (gameName === 'Path of Exile') {
      return '/main/games/icons/pathofexile.jpg';
    }
    if (gameName === 'Path of Exile 2') {
      return '/main/games/icons/pathofexile2.webp';
    }
    if (gameName === 'Persona 5: The Phantom X') {
      return '/main/games/icons/Persona5ThePhantomX.jpg';
    }
    if (gameName === 'Plants vs Brainrots') {
      return '/main/games/icons/PlantsvsBrainrots.jpg';
    }
    if (gameName === 'PlayStation') {
      return '/main/games/icons/playstation.jpg';
    }
    if (gameName === 'Pokecolo') {
      return '/main/games/icons/pokecolo.jpg';
    }
    if (gameName === 'Pokecolo Twin') {
      return '/main/games/icons/pokecolotwin.jpg';
    }
    if (gameName === 'Pokemon Go') {
      return '/main/games/icons/pokemongo.jpg';
    }
    if (gameName === 'Pokemon TCG Pocket') {
      return '/main/games/icons/pokemontcgpocket.jpg';
    }
    if (gameName === 'Princess Connect! Re:Dive') {
      return '/main/games/icons/PrincessConnectRe-Dive.png';
    }
    if (gameName === 'Professional Baseball Spirits A') {
      return '/main/games/icons/ProfessionalBaseballSpiritsA.jpg';
    }
    if (gameName === 'Project SEKAI') {
      return '/main/games/icons/projectsekai.webp';
    }
    if (gameName === 'PUBG') {
      return '/main/games/icons/pubg.jpg';
    }
    if (gameName === 'PUBG Mobile') {
      return '/main/games/icons/pubgmobile.jpg';
    }
    if (gameName === 'Puzzle and Dragons') {
      return '/main/games/icons/PuzzleandDragons.webp';
    }
    if (gameName === 'Raid: Shadow Legends') {
      return '/main/games/icons/raidshadowlegends.jpg';
    }
    if (gameName === 'Rainbow Six Siege X') {
      return '/main/games/icons/RainbowSixSiegeX.jpg';
    }
    if (gameName === 'Rise of Kingdoms') {
      return '/main/games/icons/RiseofKingdoms.jpg';
    }
    if (gameName === 'Rocket League') {
      return '/main/games/icons/rocketleague.jpg';
    }
    if (gameName === 'Royale Online') {
      return '/main/games/icons/royaleonline.jpg';
    }
    if (gameName === 'RuneScape 3') {
      return '/main/games/icons/runescape3.jpg';
    }
    if (gameName === 'Rust') {
      return '/main/games/icons/rust.png';
    }
    if (gameName === 'SD Gundam G Generation ETERNAL') {
      return '/main/games/icons/eternal.webp';
    }
    if (gameName === 'Sea of Thieves') {
      return '/main/games/icons/seaofthieves.jpg';
    }
    if (gameName === 'Seven Knights Re:BIRTH') {
      return '/main/games/icons/SevenKnightsReBIRTH.jpg';
    }
    if (gameName === 'Shadowverse') {
      return '/main/games/icons/shadowverse.jpg';
    }
    if (gameName === 'Silver and Blood') {
      return '/main/games/icons/silverandblood.webp';
    }
    if (gameName === 'Sky: Children of the Light') {
      return '/main/games/icons/SkyChildrenoftheLight.jpg';
    }
    if (gameName === 'Spotify') {
      return '/main/games/icons/spotify.jpg';
    }
    if (gameName === 'Standoff 2') {
      return '/main/games/icons/standoff2.jpg';
    }
    if (gameName === 'Steal a Brainrot') {
      return '/main/games/icons/stealabrainrot.jpg';
    }
    if (gameName === 'Stella Sora') {
      return '/main/games/icons/stellasora.jpg';
    }
    if (gameName === 'Summoners War') {
      return '/main/games/icons/SummonersWar.jpg';
    }
    if (gameName === 'The Finals') {
      return '/main/games/icons/thefinals.jpg';
    }
    if (gameName === 'The Forge') {
      return '/main/games/icons/theforge.jpg';
    }
    if (gameName === 'The Seven Deadly Sins') {
      return '/main/games/icons/thesevendeadlysins.jpg';
    }
    if (gameName === 'Throne and Liberty') {
      return '/main/games/icons/ThroneandLiberty.jpg';
    }
    if (gameName === 'Tower of Fantasy') {
      return '/main/games/icons/toweroffantasy.jpg';
    }
    if (gameName === 'Ubisoft Connect') {
      return '/main/games/icons/ubisoftconnect.jpeg';
    }
    if (gameName === 'Uma Musume Pretty Derby') {
      return '/main/games/icons/UmaMusumePrettyDerby.jpg';
    }
    if (gameName === 'unVEIL the world') {
      return '/main/games/icons/unVEILtheworld.jpeg';
    }
    if (gameName === 'Volleyball Legends') {
      return '/main/games/icons/volleyballlegends.webp';
    }
    if (gameName === 'War Thunder') {
      return '/main/games/icons/warthunder.jpg';
    }
    if (gameName === 'Warframe') {
      return '/main/games/icons/warframe.jpg';
    }
    if (gameName === 'Wartune Ultra') {
      return '/main/games/icons/wartuneultra.png';
    }
    if (gameName === 'Watcher of Realms') {
      return '/main/games/icons/watcherofrealms.jpg';
    }
    if (gameName === 'Where Winds Meet') {
      return '/main/games/icons/WhereWindsMeet.jpg';
    }
    if (gameName === 'White Cat Project') {
      return '/main/games/icons/WhiteCatProject.jpg';
    }
    if (gameName === 'Whiteout Survival') {
      return '/main/games/icons/whiteoutsurvial.jpg';
    }
    if (gameName === 'WolfTeam') {
      return '/main/games/icons/wolfteam.jpg';
    }
    if (gameName === 'World of Tanks') {
      return '/main/games/icons/worldoftanks.jpg';
    }
    if (gameName === 'World of Warcraft') {
      return '/main/games/icons/worldofwarcraft.jpg';
    }
    if (gameName === 'World of Warships') {
      return '/main/games/icons/worldofwarships.jpg';
    }
    if (gameName === 'WoW Classic') {
      return '/main/games/icons/wowclassic.jpg';
    }
    if (gameName === 'WoW Mists of Pandaria') {
      return '/main/games/icons/WoWMistsofPandaria.jpg';
    }
    if (gameName === 'Wuthering Waves') {
      return '/main/games/icons/WutheringWaves.jpg';
    }
    if (gameName === 'Xbox') {
      return '/main/games/icons/xbox.jpg';
    }
    if (gameName === 'Yo-kai Watch: Wibble Wobble') {
      return '/main/games/icons/YokaiWatchWibbleWobble.jpg';
    }
    if (gameName === 'Yu-Gi-Oh! Master Duel') {
      return '/main/games/icons/Yu-Gi-OhMasterDuel.jpg';
    }
    if (gameName === 'Zenless Zone Zero') {
      return '/main/games/icons/ZenlessZoneZero.jpg';
    }
    if (gameName === 'Zula') {
      return '/main/games/icons/zula.jpeg';
    }
    if (gameName === 'Goddess of Victory: Nikke') {
      return '/main/games/icons/nikke.jpg';
    }
    
    return iconMap[gameName] ? `/main/hero section/promos/navbaricons/${iconMap[gameName]}` : null;
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingImages(true);
    const token = localStorage.getItem('auth_token');
    const uploadedUrls = [...imagePreviews];

    try {
      for (const file of Array.from(files)) {
        const compressedDataUrl = await compressImageToBase64(file, 1200, 1200, 0.85);
        const base64Data = compressedDataUrl.split(',')[1];

        const response = await fetch('/api/seller/ads/images', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileData: base64Data,
            fileName: file.name,
            fileType: 'image/jpeg',
          }),
        });

        const data = await response.json();
        if (response.ok && data.image_url) {
          uploadedUrls.push(data.image_url);
        } else {
          showToast(`Failed to upload ${file.name}: ${data.error || 'Unknown error'}`, 'error');
        }
      }

      setImagePreviews(uploadedUrls);
      setFormData({ ...formData, image_urls: uploadedUrls });
    } catch (error) {
      console.error('Image upload error:', error);
      showToast('Failed to upload images. Please try again.', 'error');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    const newUrls = imagePreviews.filter((_, i) => i !== index);
    setImagePreviews(newUrls);
    setFormData({ ...formData, image_urls: newUrls });
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    
    setSubmitting(true);
    // Set quantity first from form so it's never lost (stock for currency/top-ups)
    const rawQ = formData.quantity;
    const qty = (rawQ !== undefined && rawQ !== null && rawQ !== '') ? (typeof rawQ === 'number' ? rawQ : parseInt(String(rawQ), 10)) : (ad ? 0 : 1);
    const quantityToSend = (Number.isNaN(qty) || qty < 0) ? (ad ? 0 : 1) : qty;
    
    let processedData = { ...formData, quantity: quantityToSend };
    
    // When editing, don't send game and top_up_option (they're read-only)
    if (ad) {
      delete processedData.game;
      delete processedData.top_up_option;
    }
    
    // For top-ups, remove account_attributes (not applicable)
    const serviceType = initialServiceType || formData.service_type;
    if (serviceType === 'top-ups') {
      delete processedData.account_attributes;
      delete processedData.account_type;
      delete processedData.account_details;
    }
    
    // For top-ups, generate title from top-up option if not provided
    if (serviceType === 'top-ups' && !processedData.title && formData.top_up_option) {
      const optionLabel = gameTopUpsOptions[formData.game]?.find(opt => opt.value === formData.top_up_option)?.label || formData.top_up_option;
      processedData.title = `${optionLabel} - ${formData.game}`;
      if (formData.platform) {
        processedData.title += ` (${formData.platform})`;
      }
      if (formData.top_up_region) {
        processedData.title += ` - ${formData.top_up_region}`;
      }
    }
    // 8 Ball Pool currency: no title field — use default so API has a title
    if (formData.game === '8 Ball Pool' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = '8 Ball Pool Coins';
    }
    if (formData.game === 'Animal Crossing: New Horizons' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Animal Crossing: New Horizons Bells';
    }
    if (formData.game === 'Arc Raiders' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Arc Raiders Currency';
    }
    if (formData.game === 'Black Desert Online' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Black Desert Online Silver';
    }
    if (formData.game === 'Dark and Darker' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Dark and Darker Gold';
    }
    if (formData.game === 'Delta Force' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Delta Force Tekniq Alloy';
    }
    if (formData.game === 'Diablo 4' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Diablo 4 Gold';
    }
    if (formData.game === 'Digimon Super Rumble' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Digimon Super Rumble Bits';
    }
    if (formData.game === 'DonutSMP' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'DonutSMP Money';
    }
    if (formData.game === 'EA Sports FC' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'EA Sports FC Coins';
    }
    if (formData.game === 'EA Sports FC Mobile' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'EA Sports FC Mobile Coins';
    }
    if (formData.game === 'Elden Ring' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Elden Ring Runes';
    }
    if (formData.game === 'Elder Scrolls Online' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Elder Scrolls Online Gold';
    }
    if (formData.game === 'Fisch' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Fisch Money';
    }
    if (formData.game === 'Forza Horizon 5' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Forza Horizon 5 Credits';
    }
    if (formData.game === 'Grow a Garden' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Grow a Garden Tokens';
    }
    if (formData.game === 'Lost Ark' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Lost Ark Gold';
    }
    if (formData.game === 'Minecraft' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Minecraft Hypixel Coins';
    }
    if (formData.game === 'New World' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'New World Coins';
    }
    if (formData.game === 'Old School RuneScape' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Old School RuneScape Currency';
    }
    if (formData.game === 'Path of Exile' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Path of Exile Currency';
    }
    if (formData.game === 'Path of Exile 2' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Path of Exile 2 Currency';
    }
    if (formData.game === 'Roblox' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Roblox Robux';
    }
    if (formData.game === 'RuneScape 3' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'RuneScape 3 Gold';
    }
    if (formData.game === 'Throne and Liberty' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Throne and Liberty Lucent';
    }
    if (formData.game === 'Warframe' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Warframe Platinum';
    }
    if (formData.game === 'WoW Classic' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'WoW Classic Gold';
    }
    if (formData.game === 'WoW Mists of Pandaria' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'WoW Mists of Pandaria Gold';
    }
    if (formData.game === 'Escape from Tarkov' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Escape From Tarkov Roubles';
    }
    if (formData.game === 'Fallout 76' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Fallout 76 Caps';
    }
    if (formData.game === 'Pokemon Go' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Pokemon Go Stardust';
    }
    if (formData.game === 'Final Fantasy XIV' && (formData.service_type === 'currency' || initialServiceType === 'currency') && !processedData.title) {
      processedData.title = 'Final Fantasy XIV Gil';
    }
    // Delta Force currency: require platform (server)
    if (formData.game === 'Delta Force' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.platform || formData.platform.trim() === '') {
        alert('Please select a server before submitting.');
        setSubmitting(false);
        return;
      }
      processedData.platform = formData.platform.trim();
    }
    // Black Desert Online currency: require device and region
    if (formData.game === 'Black Desert Online' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.device || !formData.region) {
        alert('Please select Device and Region before submitting.');
        setSubmitting(false);
        return;
      }
    }
    // Elder Scrolls Online currency: require device and region
    if (formData.game === 'Elder Scrolls Online' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.device || !formData.region) {
        alert('Please select Device and Region before submitting.');
        setSubmitting(false);
        return;
      }
    }
    // Final Fantasy XIV currency: require region and server
    if (formData.game === 'Final Fantasy XIV' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.region || !formData.server) {
        alert('Please select Region and Server before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'Fisch' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.currency_type || formData.currency_type.trim() === '') {
        alert('Please select Currency (C$, E$, or Other) before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'Forza Horizon 5' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.device || formData.device.trim() === '') {
        alert('Please select Device (PS5, PC, or Xbox) before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'Lost Ark' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.region || !formData.server) {
        alert('Please select Region and Server before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'New World' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.region || !formData.server) {
        alert('Please select Region and Server before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'Throne and Liberty' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.region || !formData.server) {
        alert('Please select Region and Server before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'Warframe' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.device || formData.device.trim() === '') {
        alert('Please select Device before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'WoW Classic' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.region || !formData.server || !formData.faction) {
        alert('Please select Region, Realm and Faction before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'WoW Mists of Pandaria' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.region || !formData.server || !formData.faction) {
        alert('Please select Region, Server and Faction before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'Path of Exile' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.device || !formData.server || !formData.orb) {
        alert('Please select Device, Server and Orbs before submitting.');
        setSubmitting(false);
        return;
      }
    }
    if (formData.game === 'Path of Exile 2' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.server || !formData.orb) {
        alert('Please select Server and Orbs before submitting.');
        setSubmitting(false);
        return;
      }
    }
    // Diablo 4 currency: require game mode
    if (formData.game === 'Diablo 4' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.game_mode || formData.game_mode.trim() === '') {
        alert('Please select a game mode before submitting.');
        setSubmitting(false);
        return;
      }
    }
    // Digimon Super Rumble currency: require platform (server)
    if (formData.game === 'Digimon Super Rumble' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.platform || formData.platform.trim() === '') {
        alert('Please select a server before submitting.');
        setSubmitting(false);
        return;
      }
      processedData.platform = formData.platform.trim();
    }
    // Escape from Tarkov currency: require platform (server)
    if (formData.game === 'Escape from Tarkov' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.platform || formData.platform.trim() === '') {
        alert('Please select a server before submitting.');
        setSubmitting(false);
        return;
      }
      processedData.platform = formData.platform.trim();
    }
    // EA Sports FC currency: require device
    if (formData.game === 'EA Sports FC' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.device || formData.device.trim() === '') {
        alert('Please select a device before submitting.');
        setSubmitting(false);
        return;
      }
    }
    // EA Sports FC Mobile currency: require region (iOS/Android)
    if (formData.game === 'EA Sports FC Mobile' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.region || formData.region.trim() === '') {
        alert('Please select a region (iOS or Android) before submitting.');
        setSubmitting(false);
        return;
      }
    }
    // Elden Ring currency: require device
    if (formData.game === 'Elden Ring' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.device || formData.device.trim() === '') {
        alert('Please select a device before submitting.');
        setSubmitting(false);
        return;
      }
    }
    // Fallout 76 currency: require device
    if (formData.game === 'Fallout 76' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!formData.device || formData.device.trim() === '') {
        alert('Please select a device before submitting.');
        setSubmitting(false);
        return;
      }
    }
    // Albion Online currency: default title and require platform (server) — always set so DB has correct region
    if (formData.game === 'Albion Online' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
      if (!processedData.title) processedData.title = 'Albion Online Silver';
      if (!formData.platform || formData.platform.trim() === '') {
        alert('Please select a server (Europe, Americas, or Asia) before submitting.');
        setSubmitting(false);
        return;
      }
      processedData.platform = formData.platform.trim();
    }

    if (formData.auto_delivery && (formData.service_type === 'accounts' || initialServiceType === 'accounts')) {
      if (formData.account_details && Array.isArray(formData.account_details)) {
        processedData.account_details = formData.account_details
          .map(detail => detail.trim())
          .filter(detail => detail.length > 0);
      } else {
        processedData.account_details = [];
      }
    } else {
      processedData.account_details = [];
    }

    // For top-ups, always ensure metadata contains top_up_option and delivery fields
    if (serviceType === 'top-ups') {
      // Always create metadata for top-ups to ensure top_up_option is stored
      // Don't include account_attributes for top-ups (not applicable)
      processedData.metadata = {
        ...(processedData.metadata || {}),
        ...(formData.top_up_option ? { top_up_option: formData.top_up_option } : {}),
        ...(formData.delivery_time ? { delivery_time: formData.delivery_time } : {}),
        ...(formData.delivery_method ? { delivery_method: formData.delivery_method } : {}),
        ...(formData.delivery_instructions ? { delivery_instructions: formData.delivery_instructions } : {}),
        ...(formData.top_up_region ? { top_up_region: formData.top_up_region } : {}),
      };
      // Remove account_attributes from processedData for top-ups
      delete processedData.account_attributes;
    } else {
      // For non-top-ups, store delivery_time, delivery_method, delivery_instructions, top_up_region, min_quantity in metadata
      const is8BPCurrencySubmit = formData.game === '8 Ball Pool' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isAlbionCurrencySubmit = formData.game === 'Albion Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isACNHCurrencySubmit = formData.game === 'Animal Crossing: New Horizons' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isArcRaidersCurrencySubmit = formData.game === 'Arc Raiders' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isBDOCurrencySubmit = formData.game === 'Black Desert Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isDarkAndDarkerCurrencySubmit = formData.game === 'Dark and Darker' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isDeltaForceCurrencySubmit = formData.game === 'Delta Force' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isDiablo4CurrencySubmit = formData.game === 'Diablo 4' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isDigimonSuperRumbleCurrencySubmit = formData.game === 'Digimon Super Rumble' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isDonutSMPCurrencySubmit = formData.game === 'DonutSMP' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isEASportsFCCurrencySubmit = formData.game === 'EA Sports FC' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isEASportsFCMobileCurrencySubmit = formData.game === 'EA Sports FC Mobile' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isEldenRingCurrencySubmit = formData.game === 'Elden Ring' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isElderScrollsOnlineCurrencySubmit = formData.game === 'Elder Scrolls Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isEscapeFromTarkovCurrencySubmit = formData.game === 'Escape from Tarkov' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isFallout76CurrencySubmit = formData.game === 'Fallout 76' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isFinalFantasyXIVCurrencySubmit = formData.game === 'Final Fantasy XIV' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isFischCurrencySubmit = formData.game === 'Fisch' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isForzaHorizon5CurrencySubmit = formData.game === 'Forza Horizon 5' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isGrowAGardenCurrencySubmit = formData.game === 'Grow a Garden' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isLostArkCurrencySubmit = formData.game === 'Lost Ark' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isMinecraftCurrencySubmit = formData.game === 'Minecraft' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isNewWorldCurrencySubmit = formData.game === 'New World' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isOldSchoolRuneScapeCurrencySubmit = formData.game === 'Old School RuneScape' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isPathOfExileCurrencySubmit = formData.game === 'Path of Exile' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isPathOfExile2CurrencySubmit = formData.game === 'Path of Exile 2' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isRobloxCurrencySubmit = formData.game === 'Roblox' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isRuneScape3CurrencySubmit = formData.game === 'RuneScape 3' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isThroneAndLibertyCurrencySubmit = formData.game === 'Throne and Liberty' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isWarframeCurrencySubmit = formData.game === 'Warframe' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isWoWClassicCurrencySubmit = formData.game === 'WoW Classic' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isWoWMistsOfPandariaCurrencySubmit = formData.game === 'WoW Mists of Pandaria' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const isPokemonGoCurrencySubmit = formData.game === 'Pokemon Go' && (formData.service_type === 'currency' || initialServiceType === 'currency');
      const hasCurrencyMeta = (is8BPCurrencySubmit || isAlbionCurrencySubmit || isACNHCurrencySubmit || isArcRaidersCurrencySubmit || isBDOCurrencySubmit || isDarkAndDarkerCurrencySubmit || isDeltaForceCurrencySubmit || isDiablo4CurrencySubmit || isDigimonSuperRumbleCurrencySubmit || isDonutSMPCurrencySubmit || isEASportsFCCurrencySubmit || isEASportsFCMobileCurrencySubmit || isEldenRingCurrencySubmit || isElderScrollsOnlineCurrencySubmit || isEscapeFromTarkovCurrencySubmit || isFallout76CurrencySubmit || isFinalFantasyXIVCurrencySubmit || isFischCurrencySubmit || isForzaHorizon5CurrencySubmit || isGrowAGardenCurrencySubmit || isLostArkCurrencySubmit || isMinecraftCurrencySubmit || isNewWorldCurrencySubmit || isOldSchoolRuneScapeCurrencySubmit || isPathOfExileCurrencySubmit || isPathOfExile2CurrencySubmit || isRobloxCurrencySubmit || isRuneScape3CurrencySubmit || isThroneAndLibertyCurrencySubmit || isWarframeCurrencySubmit || isWoWClassicCurrencySubmit || isWoWMistsOfPandariaCurrencySubmit || isPokemonGoCurrencySubmit) && (formData.min_quantity != null || (formData.volume_discount && formData.volume_discount.length) || (formData.quantity != null && formData.quantity !== '') || (isFischCurrencySubmit && formData.currency_type));
      if (formData.delivery_time || formData.delivery_method || formData.delivery_instructions || formData.top_up_region || hasCurrencyMeta) {
        processedData.metadata = {
          ...processedData.metadata,
          ...(formData.delivery_time ? { delivery_time: formData.delivery_time } : {}),
          ...(formData.delivery_method ? { delivery_method: formData.delivery_method } : {}),
          ...(formData.delivery_instructions ? { delivery_instructions: formData.delivery_instructions } : {}),
          ...(formData.top_up_region ? { top_up_region: formData.top_up_region } : {}),
          ...(is8BPCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: parseInt(formData.min_quantity, 10) } : {}),
          ...(is8BPCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(is8BPCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'B', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isAlbionCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: parseInt(formData.min_quantity, 10) } : {}),
          ...(isAlbionCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isAlbionCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isACNHCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(10, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isACNHCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isACNHCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isArcRaidersCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1000, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isArcRaidersCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isArcRaidersCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'K', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isBDOCurrencySubmit && formData.device ? { device: formData.device } : {}),
          ...(isBDOCurrencySubmit && formData.region ? { region: formData.region } : {}),
          ...(isBDOCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1000, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isBDOCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isBDOCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isDarkAndDarkerCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(10, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isDarkAndDarkerCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isDarkAndDarkerCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'K', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isDeltaForceCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(5, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isDeltaForceCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isDeltaForceCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isDiablo4CurrencySubmit && formData.game_mode ? { game_mode: formData.game_mode.trim() } : {}),
          ...(isDiablo4CurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(5000, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isDiablo4CurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isDiablo4CurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isDigimonSuperRumbleCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isDigimonSuperRumbleCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isDigimonSuperRumbleCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isDonutSMPCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(10, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isDonutSMPCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isDonutSMPCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isEASportsFCCurrencySubmit && formData.device ? { device: formData.device.trim() } : {}),
          ...(isEASportsFCCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(10, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isEASportsFCCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isEASportsFCCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'K', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isEASportsFCMobileCurrencySubmit && formData.region ? { region: formData.region.trim() } : {}),
          ...(isEASportsFCMobileCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(10, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isEASportsFCMobileCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isEASportsFCMobileCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'K', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isEldenRingCurrencySubmit && formData.device ? { device: formData.device.trim() } : {}),
          ...(isEldenRingCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isEldenRingCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isEldenRingCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'B', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isElderScrollsOnlineCurrencySubmit && formData.device ? { device: formData.device } : {}),
          ...(isElderScrollsOnlineCurrencySubmit && formData.region ? { region: formData.region } : {}),
          ...(isElderScrollsOnlineCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isElderScrollsOnlineCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isElderScrollsOnlineCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isFinalFantasyXIVCurrencySubmit && formData.region ? { region: formData.region.trim() } : {}),
          ...(isFinalFantasyXIVCurrencySubmit && formData.server ? { server: formData.server.trim() } : {}),
          ...(isFinalFantasyXIVCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(5, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isFinalFantasyXIVCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isFinalFantasyXIVCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isEscapeFromTarkovCurrencySubmit && formData.platform ? { platform: formData.platform.trim() } : {}),
          ...(isEscapeFromTarkovCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isEscapeFromTarkovCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isEscapeFromTarkovCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isFallout76CurrencySubmit && formData.device ? { device: formData.device.trim() } : {}),
          ...(isFallout76CurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(40, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isFallout76CurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isFallout76CurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'K', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isPokemonGoCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isPokemonGoCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isPokemonGoCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isFischCurrencySubmit && formData.currency_type ? { currency: formData.currency_type.trim() } : {}),
          ...(isFischCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(10, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isFischCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isFischCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isForzaHorizon5CurrencySubmit && formData.device ? { device: formData.device.trim() } : {}),
          ...(isForzaHorizon5CurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(100, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isForzaHorizon5CurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isForzaHorizon5CurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isGrowAGardenCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(500, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isGrowAGardenCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isGrowAGardenCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'unit', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isLostArkCurrencySubmit && formData.region ? { region: formData.region.trim() } : {}),
          ...(isLostArkCurrencySubmit && formData.server ? { server: formData.server.trim() } : {}),
          ...(isLostArkCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isLostArkCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isLostArkCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'K', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isMinecraftCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(10, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isMinecraftCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isMinecraftCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isNewWorldCurrencySubmit && formData.region ? { region: formData.region.trim() } : {}),
          ...(isNewWorldCurrencySubmit && formData.server ? { server: formData.server.trim() } : {}),
          ...(isNewWorldCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(10, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isNewWorldCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isNewWorldCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'K', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isOldSchoolRuneScapeCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isOldSchoolRuneScapeCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isOldSchoolRuneScapeCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isPathOfExileCurrencySubmit && formData.device ? { device: formData.device.trim() } : {}),
          ...(isPathOfExileCurrencySubmit && formData.server ? { server: formData.server.trim() } : {}),
          ...(isPathOfExileCurrencySubmit && formData.orb ? { orb: formData.orb.trim() } : {}),
          ...(isPathOfExileCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isPathOfExileCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isPathOfExileCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'unit', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isPathOfExile2CurrencySubmit && formData.server ? { server: formData.server.trim() } : {}),
          ...(isPathOfExile2CurrencySubmit && formData.orb ? { orb: formData.orb.trim() } : {}),
          ...(isPathOfExile2CurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isPathOfExile2CurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isPathOfExile2CurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'unit', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isRobloxCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(100, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isRobloxCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isRobloxCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'unit', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isRuneScape3CurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(100, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isRuneScape3CurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isRuneScape3CurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'M', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isThroneAndLibertyCurrencySubmit && formData.region ? { region: formData.region.trim() } : {}),
          ...(isThroneAndLibertyCurrencySubmit && formData.server ? { server: formData.server.trim() } : {}),
          ...(isThroneAndLibertyCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(100, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isThroneAndLibertyCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isThroneAndLibertyCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'unit', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isWarframeCurrencySubmit && formData.device ? { device: formData.device.trim() } : {}),
          ...(isWarframeCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1000, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isWarframeCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isWarframeCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'K', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isWoWClassicCurrencySubmit && formData.region ? { region: formData.region.trim() } : {}),
          ...(isWoWClassicCurrencySubmit && formData.server ? { server: formData.server.trim() } : {}),
          ...(isWoWClassicCurrencySubmit && formData.faction ? { faction: formData.faction.trim() } : {}),
          ...(isWoWClassicCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(100, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isWoWClassicCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isWoWClassicCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'unit', in_stock: parseInt(formData.quantity, 10) } : {}),
          ...(isWoWMistsOfPandariaCurrencySubmit && formData.region ? { region: formData.region.trim() } : {}),
          ...(isWoWMistsOfPandariaCurrencySubmit && formData.server ? { server: formData.server.trim() } : {}),
          ...(isWoWMistsOfPandariaCurrencySubmit && formData.faction ? { faction: formData.faction.trim() } : {}),
          ...(isWoWMistsOfPandariaCurrencySubmit && formData.min_quantity != null && formData.min_quantity !== '' ? { min_quantity: Math.max(1000, parseInt(formData.min_quantity, 10)) } : {}),
          ...(isWoWMistsOfPandariaCurrencySubmit && formData.volume_discount?.length ? { volume_discount: formData.volume_discount } : {}),
          ...(isWoWMistsOfPandariaCurrencySubmit && formData.quantity != null && formData.quantity !== '' ? { unit_label: 'K', in_stock: parseInt(formData.quantity, 10) } : {}),
          // Store exact price string so decimals like 0.0055 are not rounded by DB (price column may be decimal(10,2))
          ...(formData.price != null && formData.price !== '' ? { price_per_unit: String(formData.price) } : {}),
          // Ensure account_attributes are in metadata as backup
          ...(formData.account_attributes ? { account_attributes: formData.account_attributes } : {})
        };
      } else if (formData.account_attributes) {
        // If no delivery fields but we have account_attributes, still add them to metadata
        processedData.metadata = {
          ...processedData.metadata,
          ...(formData.account_attributes ? { account_attributes: formData.account_attributes } : {})
        };
      }
      // Always store exact price in metadata when provided (so 0.0055 etc. is not rounded by DB)
      if (formData.price != null && formData.price !== '') {
        processedData.metadata = { ...(processedData.metadata || {}), price_per_unit: String(formData.price) };
      }
    }
    
    // Debug logging for 8 Ball Pool
    if (formData.game === '8 Ball Pool' || formData.game === '8-ball-pool') {
      console.log('Submitting 8 Ball Pool ad:', {
        game: processedData.game,
        account_attributes: processedData.account_attributes,
        metadata: processedData.metadata,
        fullData: processedData
      });
    }
    
    // For top-ups, ensure platform is explicitly included if the game requires it
    if (serviceType === 'top-ups') {
      // Normalize game name for lookup
      const normalizedGameName = gameNameMap[formData.game?.toLowerCase()] || formData.game;
      const gameRequiresPlatform = gamesWithPlatforms[normalizedGameName] || gamesWithPlatforms[formData.game];
      if (gameRequiresPlatform && gameRequiresPlatform.length > 0) {
        // Game requires platform - ensure it's set
        if (!formData.platform || formData.platform.trim() === '') {
          console.error('[AdForm] ERROR: Platform is required for this game but not set!', {
            game: formData.game,
            requiredPlatforms: gameRequiresPlatform,
            currentPlatform: formData.platform
          });
          alert('Please select a platform before submitting');
          setSubmitting(false);
          return;
        }
        // Explicitly set platform
        processedData.platform = formData.platform.trim();
        console.log('[AdForm] Platform explicitly set for top-up:', processedData.platform);
      } else if (formData.platform) {
        // Game doesn't require platform, but one was selected - include it anyway
        processedData.platform = formData.platform.trim();
        console.log('[AdForm] Platform included (not required but provided):', processedData.platform);
      }
      
      // Debug logging for top-ups
      console.log('[AdForm] Submitting top-up ad:', {
        game: processedData.game,
        platform: processedData.platform,
        platformFromFormData: formData.platform,
        top_up_option: processedData.top_up_option,
        top_up_region: processedData.top_up_region,
        metadata: processedData.metadata,
        fullProcessedData: JSON.stringify(processedData, null, 2)
      });
    } else {
      // For non-top-ups, include platform if provided
      if (formData.platform) {
        processedData.platform = formData.platform.trim();
      }
    }
    
    try {
      await onSubmit(processedData);
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Set default platform for 8 Ball Pool
  useEffect(() => {
    if (formData.game === '8 Ball Pool' && !formData.platform) {
      setFormData(prev => ({ ...prev, platform: 'All Devices' }));
    }
  }, [formData.game]);

  // Set delivery time to Instant when auto_delivery is enabled
  useEffect(() => {
    if ((formData.service_type === 'accounts' || initialServiceType === 'accounts') &&
        formData.auto_delivery) {
      setFormData(prev => ({ ...prev, delivery_time: 'Instant' }));
    }
  }, [formData.auto_delivery, formData.service_type, initialServiceType]);

  // Helper to get the actual step number for validation/rendering
  const getActualStep = (logicalStep) => {
    const serviceType = initialServiceType || formData.service_type;
    const isTopUps = serviceType === 'top-ups';
    if (isTopUps) {
      return logicalStep; // For top-ups, steps are 1, 2, 3, 4, 5, 6, 7
    }
    // For non-top-ups, step 2 doesn't exist, so shift everything
    if (logicalStep === 1) return 1;
    return logicalStep + 1; // Step 2 becomes 3, step 3 becomes 4, etc.
  };

  // Helper function to validate service type and platform step
  const canProceedForServiceTypeStep = () => {
    const serviceType = initialServiceType || formData.service_type;
    if (!serviceType) {
      return false;
    }
    
    const is8BallPool = formData.game === '8 Ball Pool';
    const isAlbionCurrency = formData.game === 'Albion Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
    const requiresPlatform = (!is8BallPool && resolveRequiresPlatform(formData.game)) || isAlbionCurrency;
    
    if (requiresPlatform && !formData.platform) {
      return false;
    }
    
    return true;
  };

  const canProceed = () => {
    const serviceType = initialServiceType || formData.service_type;
    const isTopUps = serviceType === 'top-ups';

    // 8 Ball Pool / Albion / Animal Crossing / Arc Raiders / Black Desert / Dark and Darker / Delta Force / Diablo 4 / Fisch currency: 4 steps — Game (+ Server/Device+Region/Game mode/Currency), Details, Pricing, Preview
    if (is8BPCurrencyFlow || isAlbionCurrencyFlow || isACNHCurrencyFlow || isArcRaidersCurrencyFlow || isBDOCurrencyFlow || isDarkAndDarkerCurrencyFlow || isDeltaForceCurrencyFlow || isDiablo4CurrencyFlow || isDigimonSuperRumbleCurrencyFlow || isDonutSMPCurrencyFlow || isEASportsFCCurrencyFlow || isEASportsFCMobileCurrencyFlow || isEldenRingCurrencyFlow || isElderScrollsOnlineCurrencyFlow || isEscapeFromTarkovCurrencyFlow || isFallout76CurrencyFlow || isFinalFantasyXIVCurrencyFlow || isFischCurrencyFlow || isForzaHorizon5CurrencyFlow || isGrowAGardenCurrencyFlow || isLostArkCurrencyFlow || isMinecraftCurrencyFlow || isNewWorldCurrencyFlow || isOldSchoolRuneScapeCurrencyFlow || isPathOfExileCurrencyFlow || isPathOfExile2CurrencyFlow || isRobloxCurrencyFlow || isRuneScape3CurrencyFlow || isPokemonGoCurrencyFlow) {
      switch (currentStep) {
        case 1: return !!formData.game && (isAlbionCurrencyFlow || isDeltaForceCurrencyFlow || isDigimonSuperRumbleCurrencyFlow || isEscapeFromTarkovCurrencyFlow ? !!formData.platform : true) && (isBDOCurrencyFlow || isElderScrollsOnlineCurrencyFlow ? !!(formData.device && formData.region) : true) && (isFinalFantasyXIVCurrencyFlow || isLostArkCurrencyFlow || isNewWorldCurrencyFlow || isThroneAndLibertyCurrencyFlow ? !!(formData.region && formData.server) : true) && (isWoWClassicCurrencyFlow || isWoWMistsOfPandariaCurrencyFlow ? !!(formData.region && formData.server && formData.faction) : true) && (isPathOfExileCurrencyFlow ? !!(formData.device && formData.server && formData.orb) : true) && (isPathOfExile2CurrencyFlow ? !!(formData.server && formData.orb) : true) && (isFischCurrencyFlow ? !!formData.currency_type : true) && (isForzaHorizon5CurrencyFlow || isWarframeCurrencyFlow ? !!formData.device : true) && (isDiablo4CurrencyFlow ? !!formData.game_mode : true) && (isEASportsFCCurrencyFlow || isEldenRingCurrencyFlow || isFallout76CurrencyFlow ? !!formData.device : true) && (isEASportsFCMobileCurrencyFlow ? !!formData.region : true);
        case 2: {
          const minRequiredCurrency = formData.game === 'Animal Crossing: New Horizons' || formData.game === 'Dark and Darker' || formData.game === 'EA Sports FC' || formData.game === 'EA Sports FC Mobile' ? 10 : (formData.game === 'Fallout 76' ? 40 : (formData.game === 'Arc Raiders' || formData.game === 'Black Desert Online' ? 1000 : (formData.game === 'Delta Force' ? 5 : (formData.game === 'Diablo 4' ? 5000 : (formData.game === 'Digimon Super Rumble' || formData.game === 'Elder Scrolls Online' || formData.game === 'Escape from Tarkov' || formData.game === 'Old School RuneScape' || formData.game === 'Path of Exile' || formData.game === 'Path of Exile 2' || formData.game === 'Pokemon Go' ? 1 : (formData.game === 'Roblox' ? 100 : (formData.game === 'Final Fantasy XIV' ? 5 : (formData.game === 'DonutSMP' || formData.game === 'Minecraft' || formData.game === 'New World' ? 10 : (formData.game === 'RuneScape 3' ? 100 : (formData.game === 'Throne and Liberty' ? 100 : (formData.game === 'Warframe' ? 1000 : (formData.game === 'WoW Classic' ? 100 : (formData.game === 'WoW Mists of Pandaria' ? 1000 : (formData.game === 'Elden Ring' ? 1 : 1))))))))))))));
          const minQ = formData.min_quantity != null && formData.min_quantity !== '' ? parseInt(formData.min_quantity, 10) : null;
          if (minQ === null || isNaN(minQ) || minQ < minRequiredCurrency) return false;
          if (!formData.delivery_time) return false;
          if (!formData.delivery_method) return false;
          if (!formData.quantity || formData.quantity < 1) return false;
          const stockQ = parseInt(formData.quantity, 10);
          if (!isNaN(stockQ) && !isNaN(minQ) && stockQ < minQ) return false; // stock must be >= min offer quantity
          return true;
        }
        case 3: return !!(formData.price && parseFloat(formData.price) > 0);
        case 4: return true;
        default: return false;
      }
    }

    switch (currentStep) {
      case 1:
        // Step 1: Only game selection required
        return formData.game;
      case 2:
        // Step 2: Offer Details (only for top-ups) OR Service type/platform (for others)
        if (isTopUps) {
          // If game has top-up options defined, require selection. If no options, allow proceeding.
          const topUpsOptions = gameTopUpsOptions[formData.game] || [];
          if (topUpsOptions.length > 0) {
            return formData.top_up_option && formData.top_up_option.length > 0;
          }
          // If no options available, still allow proceeding (game may not be configured yet)
          return true;
        }
        // 8 Ball Pool / Albion / Animal Crossing / Arc Raiders / Black Desert Online / Dark and Darker / Delta Force / Elder Scrolls Online / Escape from Tarkov / Fallout 76 / Final Fantasy XIV / Fisch / Pokemon Go currency: step 2 = Details (delivery, quantity, min_quantity required)
        if ((formData.game === '8 Ball Pool' || formData.game === 'Albion Online' || formData.game === 'Animal Crossing: New Horizons' || formData.game === 'Arc Raiders' || formData.game === 'Black Desert Online' || formData.game === 'Dark and Darker' || formData.game === 'Delta Force' || formData.game === 'Elder Scrolls Online' || formData.game === 'Escape from Tarkov' || formData.game === 'Fallout 76' || formData.game === 'Final Fantasy XIV' || formData.game === 'Fisch' || formData.game === 'Forza Horizon 5' || formData.game === 'Grow a Garden' || formData.game === 'Lost Ark' || formData.game === 'Minecraft' || formData.game === 'New World' || formData.game === 'Old School RuneScape' || formData.game === 'Path of Exile' || formData.game === 'Path of Exile 2' || formData.game === 'Pokemon Go' || formData.game === 'Roblox' || formData.game === 'RuneScape 3' || formData.game === 'Throne and Liberty' || formData.game === 'Warframe' || formData.game === 'WoW Classic' || formData.game === 'WoW Mists of Pandaria') && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
          const minQ = formData.min_quantity != null && formData.min_quantity !== '' ? parseInt(formData.min_quantity, 10) : null;
          const minRequired = formData.game === 'Animal Crossing: New Horizons' || formData.game === 'Dark and Darker' ? 10 : (formData.game === 'Fallout 76' ? 40 : (formData.game === 'Arc Raiders' || formData.game === 'Black Desert Online' ? 1000 : (formData.game === 'Delta Force' ? 5 : (formData.game === 'Elder Scrolls Online' || formData.game === 'Escape from Tarkov' || formData.game === 'Lost Ark' || formData.game === 'Old School RuneScape' || formData.game === 'Path of Exile' || formData.game === 'Path of Exile 2' || formData.game === 'Pokemon Go' ? 1 : (formData.game === 'Roblox' ? 100 : (formData.game === 'RuneScape 3' ? 100 : (formData.game === 'Throne and Liberty' ? 100 : (formData.game === 'Warframe' ? 1000 : (formData.game === 'WoW Classic' ? 100 : (formData.game === 'WoW Mists of Pandaria' ? 1000 : (formData.game === 'Final Fantasy XIV' ? 5 : (formData.game === 'Fisch' ? 10 : (formData.game === 'Minecraft' || formData.game === 'New World' ? 10 : (formData.game === 'Forza Horizon 5' ? 100 : (formData.game === 'Grow a Garden' ? 500 : 1)))))))))))))));
          if (minQ === null || isNaN(minQ) || minQ < minRequired) return false;
          if (!formData.delivery_time) return false;
          if (!formData.delivery_method) return false;
          if (!formData.quantity || formData.quantity < 1) return false;
          const stockQ = parseInt(formData.quantity, 10);
          if (!isNaN(stockQ) && !isNaN(minQ) && stockQ < minQ) return false; // stock must be >= min offer quantity
          return true;
        }
        // For non-top-ups, step 2 is service type/platform
        return canProceedForServiceTypeStep();
      case 3:
        // Step 3: Service type/platform/region (for top-ups) OR Title (for others)
        if (isTopUps) {
          const is8BallPool = formData.game === '8 Ball Pool';
          
          // Normalize game name for lookup (handle slugs like "pubg" -> "PUBG")
          const normalizeGameName = (gameName) => {
            if (!gameName) return '';
            if (gamesWithPlatforms[gameName] || gamesWithRegions[gameName]) {
              return gameName;
            }
            return gameNameMap[gameName.toLowerCase()] || gameName;
          };
          
          const normalizedGameName = normalizeGameName(formData.game);
          const gameRequiresRegion = gamesWithRegions[normalizedGameName] || gamesWithRegions[formData.game];
          const gameRequiresPlatform = gamesWithPlatforms[normalizedGameName] || gamesWithPlatforms[formData.game];
          
          // Check if service type is set (required)
          const serviceType = initialServiceType || formData.service_type;
          if (!serviceType) {
            return false;
          }
          
          // Check if region is required and selected
          if (gameRequiresRegion && !formData.top_up_region) {
            return false;
          }
          // Check if platform is required and selected (button-based)
          if (gameRequiresPlatform && !formData.platform) {
            return false;
          }
          // If nothing is required, allow proceeding
          return true;
        }
        // 8 Ball Pool / Albion / Animal Crossing / Arc Raiders / Black Desert Online / Dark and Darker / Delta Force / Elder Scrolls Online / Escape from Tarkov / Fallout 76 / Final Fantasy XIV / Fisch / Pokemon Go / New World currency: no title required, only optional description (on next step)
        if ((formData.game === '8 Ball Pool' || formData.game === 'Albion Online' || formData.game === 'Animal Crossing: New Horizons' || formData.game === 'Arc Raiders' || formData.game === 'Black Desert Online' || formData.game === 'Dark and Darker' || formData.game === 'Delta Force' || formData.game === 'Elder Scrolls Online' || formData.game === 'Escape from Tarkov' || formData.game === 'Fallout 76' || formData.game === 'Final Fantasy XIV' || formData.game === 'Fisch' || formData.game === 'Forza Horizon 5' || formData.game === 'Grow a Garden' || formData.game === 'Lost Ark' || formData.game === 'Minecraft' || formData.game === 'New World' || formData.game === 'Old School RuneScape' || formData.game === 'Path of Exile' || formData.game === 'Path of Exile 2' || formData.game === 'Pokemon Go' || formData.game === 'Roblox' || formData.game === 'RuneScape 3' || formData.game === 'Throne and Liberty' || formData.game === 'Warframe' || formData.game === 'WoW Classic') && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
          return true;
        }
        return formData.title;
      case 4:
        // Step 4: Quantity/Price/Delivery (for top-ups) OR Images (for others)
        if (isTopUps) {
          if (!formData.quantity || formData.quantity < 1) {
            return false;
          }
          if (!formData.price || parseFloat(formData.price) <= 0) {
            return false;
          }
          if (!formData.delivery_method) {
            return false;
          }
          if (!formData.delivery_time) {
            return false;
          }
          return true;
        }
        // Step 4: for 8 Ball Pool / Animal Crossing / Arc Raiders / Black Desert Online / Dark and Darker / Delta Force / Diablo 4 / Digimon Super Rumble / DonutSMP / EA Sports FC / EA Sports FC Mobile / Elder Scrolls Online / Escape from Tarkov / Fallout 76 / Final Fantasy XIV / Fisch / Pokemon Go / New World currency, no images required
        if ((formData.game === '8 Ball Pool' || formData.game === 'Animal Crossing: New Horizons' || formData.game === 'Arc Raiders' || formData.game === 'Black Desert Online' || formData.game === 'Dark and Darker' || formData.game === 'Delta Force' || formData.game === 'Diablo 4' || formData.game === 'Digimon Super Rumble' || formData.game === 'DonutSMP' || formData.game === 'EA Sports FC' || formData.game === 'EA Sports FC Mobile' || formData.game === 'Elder Scrolls Online' || formData.game === 'Escape from Tarkov' || formData.game === 'Fallout 76' || formData.game === 'Final Fantasy XIV' || formData.game === 'Fisch' || formData.game === 'Forza Horizon 5' || formData.game === 'Grow a Garden' || formData.game === 'Lost Ark' || formData.game === 'Minecraft' || formData.game === 'New World' || formData.game === 'Old School RuneScape' || formData.game === 'Path of Exile' || formData.game === 'Path of Exile 2' || formData.game === 'Pokemon Go' || formData.game === 'Roblox' || formData.game === 'RuneScape 3' || formData.game === 'Throne and Liberty' || formData.game === 'Warframe' || formData.game === 'WoW Classic') && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
          return true;
        }
        return true;
      case 5:
        // Step 5: Preview (for top-ups) OR Quantity/Price (for others) OR 8 Ball Pool currency Pricing
        if (isTopUps) {
          return true; // Preview step - all validation done in previous steps
        }
        // 8 Ball Pool / Animal Crossing currency: step 5 = Pricing only (delivery/quantity validated in step 2)
        const is8BPCurrency = formData.game === '8 Ball Pool' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isACNHCurrency = formData.game === 'Animal Crossing: New Horizons' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        if (is8BPCurrency || isACNHCurrency) {
          if (!formData.price || parseFloat(formData.price) <= 0) return false;
          return true;
        }
        if (!formData.quantity || formData.quantity < 1) {
          return false;
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
          return false;
        }
        return true;
      case 6:
        // Step 6: Preview (only for non-top-ups, top-ups have 5 steps)
        if (isTopUps) {
          return true; // This shouldn't be reached for top-ups
        }
        // For non-top-ups, step 6 is preview
        const is8BallPoolFinal = formData.game === '8 Ball Pool';
        const requiresPlatform = resolveRequiresPlatform(formData.game);
        const hasGame = formData.game && (initialServiceType || formData.service_type) && (!requiresPlatform ? true : formData.platform);
        let hasAccountType = true;
        if (formData.service_type === 'accounts' || initialServiceType === 'accounts') {
          if (is8BallPoolFinal) {
            // For 8 Ball Pool, check Level, Cash, and Coins
            hasAccountType = !!(formData.account_attributes?.level && 
                               formData.account_attributes?.cash && 
                               formData.account_attributes?.coins);
          } else {
            const accountAttrs = resolveAccountAttributes(formData.game);
            if (!accountAttrs) {
              hasAccountType = true; // normal games: no extra attributes required
            } else if (accountAttrs.type === 'multi-select') {
              hasAccountType = accountAttrs.fields.every(field => 
                formData.account_attributes?.[field.name]
              );
            } else {
              hasAccountType = !!formData.account_type;
            }
          }
        }
        // 8 Ball Pool / Animal Crossing currency: no title required
        const hasTitle = ((formData.game === '8 Ball Pool' || formData.game === 'Animal Crossing: New Horizons') && (formData.service_type === 'currency' || initialServiceType === 'currency')) ? true : formData.title;
        const hasQuantity = formData.quantity && formData.quantity >= 1;
        const hasPrice = formData.price && parseFloat(formData.price) > 0;
        
        let hasValidAutoDelivery = true;
        if (formData.auto_delivery && (formData.service_type === 'accounts' || initialServiceType === 'accounts')) {
          if (!formData.account_details || !Array.isArray(formData.account_details)) {
            return false;
          }
          const validDetails = formData.account_details.filter(detail => detail && detail.trim().length > 0);
          if (validDetails.length !== formData.quantity) {
            return false;
          }
        }
        // Require delivery_time for accounts
        if ((formData.service_type === 'accounts' || initialServiceType === 'accounts')) {
          if (!formData.delivery_time) {
            return false;
          }
        }
        return hasGame && hasAccountType && hasTitle && hasQuantity && hasPrice && hasValidAutoDelivery;
      default:
        return false;
    }
  };

  // Delivery method options for 8 Ball Pool currency (BETA) – with info descriptions
  const deliveryMethods8BallPool = [
    { value: 'in-game-trade', label: 'In-game trade', description: 'Trade directly with the buyer in-game.', steps: ['Exchange in-game nicknames with the buyer in the order chat.', 'Agree on a meeting location for the trade.', 'Meet the buyer in-game and deliver the currency using the in-game trade system.'] },
    { value: 'game-pass', label: 'Game Pass', description: 'Deliver via game pass feature.', steps: ['Use the Game Pass link provided by the buyer in the order chat.', 'Purchase the Game Pass to complete the order.', 'Ensure the price of the Game Pass matches the order amount before purchasing.'] },
    { value: 'auction-house', label: 'Auction House', description: 'Use the in-game auction house.', steps: ['Ask the buyer to list an overpriced item in the in-game auction house matching the purchased currency amount.', 'Have the buyer share the item name, auction house or city location, or a screenshot in the order chat.', 'Purchase the listed item to complete the delivery.'] },
    { value: 'mail-trade', label: 'Mail Trade', description: 'Send currency via in-game mail.', steps: ['Ask the buyer for their in-game account name in the order chat.', 'Send the currency to the buyer using the in-game mail system.'] },
    { value: 'island-delivery', label: 'Island Delivery', description: 'Deliver via island/private area.', steps: ['Request the island name or coordinates from the buyer in the order chat, or provide your own if required.', 'Travel to the specified island and place the currency or items in the island chest.', 'The buyer will then collect the currency from the same chest.'] },
    { value: 'epic-gifting', label: 'Epic Gifting', description: 'Use Epic Games gifting.', steps: ['Add the requested currency to your Epic account and share the account login details (email and password) with the buyer on the order page.', 'The buyer will use these details to log into the Epic Games Launcher and access the game.', 'After completing a few in-game steps, the currency will be delivered to the buyer\'s account.'] },
    { value: 'login-method', label: 'Login Method', description: 'Login to the buyer\'s account to deliver.', steps: ['Ask the buyer to provide their game account login (email/username and password) in the order chat.', 'Log in to the buyer\'s account and deliver the coins using secure, official methods.', 'Advise the buyer not to log in, transfer currency, or use mobile features during the delivery process.'] },
  ];

  // Delivery method options with descriptions (top-ups)
  const deliveryMethods = [
    {
      value: 'autoclaim-uid',
      label: 'AutoClaim (UID)',
      description: 'AutoClaim (UID)',
      steps: [
        'Collect the buyer\'s game account UID in the order chat.',
        'Use this UID to process the order and credit the top-up directly to the buyer\'s game account.'
      ]
    },
    {
      value: 'login-method',
      label: 'Login Method',
      description: 'Login Method',
      steps: [
        'Request the buyer\'s game account username and password in the order chat.',
        'Log in to the buyer\'s account.',
        'Complete the top-up using official game sources. If needed, change the account region to proceed.'
      ]
    },
    {
      value: 'redeem-code',
      label: 'Redeem Code',
      description: 'Redeem Code',
      steps: [
        'You must provide a valid redeemable code in the order page.',
        'The buyer will enter this code in the game\'s Redeem Code section.',
        'The top-up will be instantly credited to the buyer\'s game account upon redemption.'
      ]
    },
    {
      value: 'donation',
      label: 'Donation',
      description: 'Donation',
      steps: [
        'Get the buyer\'s UID or username in the chat.',
        'Add the buyer as a friend in the game.',
        'Wait until gifting is available (usually 1-2 days).',
        'Send the purchased Top Ups as a gift/donation.'
      ]
    }
  ];

  // Filter games based on service type (defined inside AdForm to access parent scope)
  const getAvailableGamesForAdForm = (serviceType) => {
    if (serviceType === 'top-ups') {
      // Only show games with top-ups pages, preserving the order from gamesWithTopUps
      return gamesWithTopUps
        .map(slug => gameNameMap[slug])
        .filter(name => name && allGames.includes(name));
    }
    if (serviceType === 'currency') {
      // Only show games that support currency ads, in the specified order
      return gamesWithCurrency.filter(name => allGames.includes(name));
    }
    // For other service types, show all games
    return allGames;
  };

  // Game to top-up options mapping
  const gameTopUpsOptions = {
    '99 Nights in the Forest': [
      { value: '20', label: '20 Diamonds' },
      { value: '100', label: '100 Diamonds' },
      { value: '250', label: '250 Diamonds' },
      { value: '500', label: '500 Diamonds' },
      { value: '700', label: '700 Diamonds' },
      { value: '1400', label: '1400 Diamonds' },
    ],
    'Apex Legends': [
      { value: '1000-coins', label: '1000 Coins' },
      { value: '2150-coins', label: '2150 Coins' },
      { value: '4350-coins', label: '4350 Coins' },
      { value: '6700-coins', label: '6700 Coins' },
      { value: '11500-coins', label: '11500 Coins' },
      { value: '23000-coins', label: '23000 Coins' },
      { value: '34500-coins', label: '34500 Coins' },
      { value: '46000-coins', label: '46000 Coins' },
      { value: '10-exotic-shards', label: 'Exotic Shards - 10' },
      { value: '50-exotic-shards', label: 'Exotic Shards - 50' },
      { value: '110-exotic-shards', label: 'Exotic Shards - 110' },
      { value: 'ultimate-battle-pass', label: 'Ultimate Battle Pass' },
      { value: 'ultimate-plus-battle-pass', label: 'Ultimate+ Battle Pass' },
    ],
    'Arena Breakout': [
      { value: '66', label: '66 Bonds' },
      { value: '335', label: '335 Bonds' },
      { value: '675', label: '675 Bonds' },
      { value: '1690', label: '1690 Bonds' },
      { value: '3400', label: '3400 Bonds' },
      { value: '6820', label: '6820 Bonds' },
      { value: 'advanced-battle-pass', label: 'Advanced Battle Pass' },
      { value: 'beginner-choice', label: 'Beginner Choice' },
      { value: 'bulletproof-case', label: 'Bulletproof Case' },
      { value: 'composite-case', label: 'Composite Case' },
      { value: 'premium-battle-pass', label: 'Premium Battle Pass' },
    ],
    'Fortnite': [
      { value: '1000', label: '1000 V-Bucks' },
      { value: '2800', label: '2800 V-Bucks' },
      { value: '5000', label: '5000 V-Bucks' },
      { value: '10000', label: '10000 V-Bucks' },
      { value: '13500', label: '13500 V-Bucks' },
      { value: '27000', label: '27000 V-Bucks' },
      { value: '54000', label: '54000 V-Bucks' },
      { value: '67500', label: '67500 V-Bucks' },
      { value: '135000', label: '135000 V-Bucks' },
    ],
    'Call of Duty': [
      { value: '200', label: '200 CoD Points' },
      { value: '500', label: '500 CoD Points' },
      { value: '1100', label: '1100 CoD Points' },
      { value: '2400', label: '2400 CoD Points' },
      { value: '5000', label: '5000 CoD Points' },
      { value: '9500', label: '9500 CoD Points' },
      { value: '13000', label: '13000 CoD Points' },
      { value: '21000', label: '21000 CoD Points' },
      { value: '26000', label: '26000 CoD Points' },
      { value: '39000', label: '39000 CoD Points' },
      { value: 'blackcell-battle-pass', label: 'BlackCell Battle Pass' },
    ],
    'League of Legends': [
      { value: '150', label: '150 Riot Points' },
      { value: '475', label: '475 Riot Points' },
      { value: '500', label: '500 Riot Points' },
      { value: '575', label: '575 Riot Points' },
      { value: '1240', label: '1240 Riot Points' },
      { value: '1400', label: '1400 Riot Points' },
      { value: '1425', label: '1425 Riot Points' },
      { value: '1525', label: '1525 Riot Points' },
      { value: '1895', label: '1895 Riot Points' },
      { value: '2470', label: '2470 Riot Points' },
      { value: '2850', label: '2850 Riot Points' },
      { value: '2900', label: '2900 Riot Points' },
      { value: '3135', label: '3135 Riot Points' },
      { value: '3190', label: '3190 Riot Points' },
      { value: '4600', label: '4600 Riot Points' },
      { value: '5000', label: '5000 Riot Points' },
      { value: '5250', label: '5250 Riot Points' },
      { value: '5680', label: '5680 Riot Points' },
      { value: '5700', label: '5700 Riot Points' },
      { value: '6500', label: '6500 Riot Points' },
      { value: '7620', label: '7620 Riot Points' },
      { value: '8390', label: '8390 Riot Points' },
      { value: '10000', label: '10000 Riot Points' },
      { value: '10250', label: '10250 Riot Points' },
      { value: '10290', label: '10290 Riot Points' },
      { value: '12185', label: '12185 Riot Points' },
      { value: '20000', label: '20000 Riot Points' },
      { value: '20500', label: '20500 Riot Points' },
    ],
    'Valorant': [
      { value: '475', label: '475 VP' },
      { value: '1000', label: '1000 VP' },
      { value: '2050', label: '2050 VP' },
      { value: '3650', label: '3650 VP' },
      { value: '5350', label: '5350 VP' },
      { value: '11000', label: '11000 VP' },
    ],
    'Roblox': [
      { value: '400', label: '400 Robux' },
      { value: '800', label: '800 Robux' },
      { value: '1700', label: '1700 Robux' },
      { value: '2000', label: '2000 Robux' },
      { value: '4500', label: '4500 Robux' },
      { value: '10000', label: '10000 Robux' },
      { value: '22500', label: '22500 Robux' },
      { value: '45000', label: '45000 Robux' },
      { value: '67500', label: '67500 Robux' },
      { value: 'premium-450', label: 'Premium Membership - 450' },
      { value: 'premium-1000', label: 'Premium Membership - 1000' },
      { value: 'premium-2200', label: 'Premium Membership - 2200' },
    ],
    'Zenless Zone Zero': [
      { value: '330', label: '330 Monochrome' },
      { value: '980', label: '980 Monochrome' },
      { value: '1980', label: '1980 Monochrome' },
      { value: '3280', label: '3280 Monochrome' },
      { value: '6480', label: '6480 Monochrome' },
      { value: '8080', label: '8080 Monochrome' },
      { value: '16160', label: '16160 Monochrome' },
      { value: '24240', label: '24240 Monochrome' },
      { value: '32320', label: '32320 Monochrome' },
      { value: 'inter-knot-membership', label: 'Inter-Knot Membership' },
    ],
    'Wuthering Waves': [
      { value: '300', label: '300 Lunite' },
      { value: '980', label: '980 Lunite' },
      { value: '1980', label: '1980 Lunite' },
      { value: '3280', label: '3280 Lunite' },
      { value: '6480', label: '6480 Lunite' },
      { value: 'lunite-subscription', label: 'Lunite Subscription' },
    ],
    'Pokemon Go': [
      { value: '100', label: '100 PokeCoins' },
      { value: '550', label: '550 PokeCoins' },
      { value: '1200', label: '1200 PokeCoins' },
      { value: '2500', label: '2500 PokeCoins' },
      { value: '5200', label: '5200 PokeCoins' },
      { value: '14500', label: '14500 PokeCoins' },
      { value: '15500', label: '15500 PokeCoins' },
      { value: '29000', label: '29000 PokeCoins' },
      { value: '31000', label: '31000 PokeCoins' },
      { value: '43500', label: '43500 PokeCoins' },
      { value: '46500', label: '46500 PokeCoins' },
      { value: '58000', label: '58000 PokeCoins' },
      { value: 'go-pass', label: 'Go Pass Deluxe: January' },
      { value: 'go-pass-deluxe', label: 'Go Pass Deluxe: January + 10 Ranks' },
      { value: 'ultra-ticket-box', label: 'Go Pass Deluxe: January Ultra Ticket Box' },
      { value: 'masterwork', label: 'Masterwork Research: Master Ball Ultra Ticket Box' },
      { value: 'ultra-ticket', label: 'Pokemon GO Tour 2026 Ultra Ticket Box' },
      { value: 'kalos', label: 'Pokemon GO Tour: Kalos - Global Ticket' },
      { value: 'storage-25', label: 'Pokémon Storage x25' },
      { value: 'battlepass-x185', label: 'Premium Battle Pass x 185' },
      { value: 'battlepass-x30', label: 'Premium Battle Pass x 30' },
      { value: 'raidpass-x2', label: 'Remote Raid Pass x 2' },
    ],
    'PUBG': [
      { value: '100', label: '100 G-Coins' },
      { value: '500', label: '500 G-Coins' },
      { value: '510', label: '510 G-Coins' },
      { value: '1050', label: '1050 G-Coins' },
      { value: '1100', label: '1100 G-Coins' },
      { value: '2300', label: '2300 G-Coins' },
      { value: '2700', label: '2700 G-Coins' },
      { value: '5500', label: '5500 G-Coins' },
      { value: '6000', label: '6000 G-Coins' },
      { value: '11200', label: '11200 G-Coins' },
      { value: '12000', label: '12000 G-Coins' },
      { value: '18000', label: '18000 G-Coins' },
      { value: '24000', label: '24000 G-Coins' },
    ],
    'PUBG Mobile': [
      { value: '60', label: '60 UC' },
      { value: '325', label: '325 UC' },
      { value: '660', label: '660 UC' },
      { value: '1800', label: '1800 UC' },
      { value: '3850', label: '3850 UC' },
      { value: '4000', label: '4000 UC' },
      { value: '8100', label: '8100 UC' },
      { value: '8400', label: '8400 UC' },
      { value: '9375', label: '9375 UC' },
      { value: '16200', label: '16200 UC' },
      { value: '24300', label: '24300 UC' },
      { value: '32400', label: '32400 UC' },
      { value: '40500', label: '40500 UC' },
      { value: 'elite-pass', label: 'Elite Pass' },
    ],
    'Rocket League': [
      { value: '500', label: '500 Credits' },
      { value: '1100', label: '1100 Credits' },
      { value: '3000', label: '3000 Credits' },
      { value: '6500', label: '6500 Credits' },
    ],
    'Rust': [
      { value: '500', label: '500 Coins' },
      { value: '1100', label: '1100 Coins' },
      { value: '2250', label: '2250 Coins' },
      { value: '4600', label: '4600 Coins' },
      { value: '7800', label: '7800 Coins' },
    ],
    'Sea of Thieves': [
      { value: '150', label: '150 Ancient Coins' },
      { value: '550', label: '550 Ancient Coins' },
      { value: '1000', label: '1000 Ancient Coins' },
      { value: '2550', label: '2550 Ancient Coins' },
      { value: '4250', label: '4250 Ancient Coins' },
      { value: '12750', label: '12750 Ancient Coins' },
    ],
    'Spotify': [
      { value: 'premium-1-month', label: 'Premium - 1 Month' },
      { value: 'premium-3-months', label: 'Premium - 3 Months' },
      { value: 'premium-6-months', label: 'Premium - 6 Months' },
      { value: 'premium-12-months', label: 'Premium - 12 Months' },
    ],
    'Whiteout Survival': [
      { value: '99', label: '99 Frost Stars' },
      { value: '299', label: '299 Frost Stars' },
      { value: '499', label: '499 Frost Stars' },
      { value: '999', label: '999 Frost Stars' },
      { value: '1999', label: '1999 Frost Stars' },
      { value: '4999', label: '4999 Frost Stars' },
      { value: '7499', label: '7499 Frost Stars' },
      { value: '9999', label: '9999 Frost Stars' },
      { value: '29999', label: '29999 Frost Stars' },
      { value: '49999', label: '49999 Frost Stars' },
      { value: '69999', label: '69999 Frost Stars' },
      { value: '99990', label: '99990 Frost Stars' },
    ],
    'World of Warcraft': [
      { value: 'days-of-game-time-60', label: 'Days of Game Time: 60' },
    ],
    'Overwatch 2': [
      { value: '500', label: '500 Coins' },
      { value: '1000', label: '1000 Coins' },
      { value: '2200', label: '2200 Coins' },
      { value: '4500', label: '4500 Coins' },
      { value: '6900', label: '6900 Coins' },
      { value: '11800', label: '11800 Coins' },
      { value: '23600', label: '23600 Coins' },
      { value: '35400', label: '35400 Coins' },
      { value: 'starter-pack', label: 'Starter Pack' },
      { value: 'ultimate-battle-pass-bundle', label: 'Ultimate Battle Pass Bundle' },
    ],
    'Path of Exile 2': [
      { value: '50', label: '50 Points' },
      { value: '100', label: '100 Points' },
      { value: '200', label: '200 Points' },
    ],
    'PlayStation': [
      { value: 'essential-1-month', label: 'Essential - 1 Month' },
      { value: 'essential-3-months', label: 'Essential - 3 Months' },
      { value: 'essential-12-months', label: 'Essential - 12 Months' },
      { value: 'extra-1-month', label: 'Extra - 1 Month' },
      { value: 'extra-3-months', label: 'Extra - 3 Months' },
      { value: 'extra-12-months', label: 'Extra - 12 Months' },
      { value: 'premium-1-month', label: 'Premium - 1 Month' },
      { value: 'premium-3-months', label: 'Premium - 3 Months' },
      { value: 'premium-12-months', label: 'Premium - 12 Months' },
    ],
    'Pokemon TCG Pocket': [
      { value: '5', label: '5 Poke Gold' },
      { value: '25', label: '25 Poke Gold' },
      { value: '50', label: '50 Poke Gold' },
      { value: '100', label: '100 Poke Gold' },
      { value: '200', label: '200 Poke Gold' },
      { value: '500', label: '500 Poke Gold' },
    ],
    'Rainbow Six Siege X': [
      { value: '600', label: '600 Credits' },
      { value: '1200', label: '1200 Credits' },
      { value: '3300', label: '3300 Credits' },
      { value: '7200', label: '7200 Credits' },
      { value: '15000', label: '15000 Credits' },
      { value: '30000', label: '30000 Credits' },
      { value: '45000', label: '45000 Credits' },
    ],
    'Genshin Impact': [
      { value: '60', label: '60 Genesis Crystals' },
      { value: '330', label: '330 Genesis Crystals' },
      { value: '1090', label: '1090 Genesis Crystals' },
      { value: '2240', label: '2240 Genesis Crystals' },
      { value: '3880', label: '3880 Genesis Crystals' },
      { value: '8080', label: '8080 Genesis Crystals' },
      { value: '16160', label: '16160 Genesis Crystals' },
      { value: '24240', label: '24240 Genesis Crystals' },
      { value: '32320', label: '32320 Genesis Crystals' },
      { value: '80800', label: '80800 Genesis Crystals' },
      { value: 'all-in-one-bundle', label: 'All-In-One Bundle' },
      { value: 'welkin-30', label: 'Blessing of the Welkin Moon - 30 Days' },
      { value: 'welkin-90', label: 'Blessing of the Welkin Moon - 90 Days' },
      { value: 'welkin-180', label: 'Blessing of the Welkin Moon - 180 Days' },
      { value: 'welkin-1090', label: 'Blessing of the Welkin Moon + 1090 Genesis Crystals' },
      { value: 'welkin-8080', label: 'Blessing of the Welkin Moon + 8080 Genesis Crystals' },
      { value: 'gnostic-chorus', label: 'Gnostic Chorus - Battle Pass' },
      { value: 'gnostic-hymn', label: 'Gnostic Hymn - Battle Pass' },
    ],
    'League of Legends: Wild Rift': [
      { value: '425', label: '425 Wild Cores' },
      { value: '1000', label: '1000 Wild Cores' },
      { value: '1850', label: '1850 Wild Cores' },
      { value: '3275', label: '3275 Wild Cores' },
      { value: '4800', label: '4800 Wild Cores' },
      { value: '10000', label: '10000 Wild Cores' },
    ],
    'Blood Strike': [
      { value: '105', label: '105 Gold' },
      { value: '320', label: '320 Gold' },
      { value: '520', label: '520 Gold' },
      { value: '1100', label: '1100 Gold' },
      { value: '2260', label: '2260 Gold' },
      { value: '5800', label: '5800 Gold' },
      { value: 'strike-pass-elite', label: 'Strike Pass Elite' },
      { value: 'strike-pass-premium', label: 'Strike Pass Premium' },
    ],
    'Brawl Stars': [
      { value: '30', label: '30 Gems' },
      { value: '80', label: '80 Gems' },
      { value: '170', label: '170 Gems' },
      { value: '350', label: '350 Gems' },
      { value: '950', label: '950 Gems' },
      { value: '2000', label: '2000 Gems' },
      { value: 'brawl-pass-plus', label: 'Brawl Pass Plus' },
      { value: 'brawl-pass', label: 'Brawl Pass' },
    ],
    'Clash of Clans': [
      { value: '80', label: '80 Gems' },
      { value: '500', label: '500 Gems' },
      { value: '1200', label: '1200 Gems' },
      { value: '2500', label: '2500 Gems' },
      { value: '6500', label: '6500 Gems' },
      { value: '14000', label: '14000 Gems' },
      { value: 'event-pass', label: 'Event Pass' },
      { value: 'gold-pass', label: 'Gold Pass' },
    ],
    'Clash Royale': [
      { value: '80', label: '80 Gems' },
      { value: '500', label: '500 Gems' },
      { value: '1200', label: '1200 Gems' },
      { value: '2500', label: '2500 Gems' },
      { value: '6500', label: '6500 Gems' },
      { value: '14000', label: '14000 Gems' },
      { value: 'diamond-pass', label: 'Diamond Pass' },
    ],
    'Crunchyroll': [
      { value: 'premium-1-month', label: 'Premium - 1 Month' },
      { value: 'premium-3-months', label: 'Premium - 3 Months' },
      { value: 'premium-12-months', label: 'Premium - 12 Months' },
      { value: 'premium-mega-fan-1-month', label: 'Premium Mega Fan - 1 Month' },
      { value: 'premium-mega-fan-12-months', label: 'Premium Mega Fan - 12 Months' },
    ],
    'Dead By Daylight': [
      { value: '500', label: '500 Auric Cells' },
      { value: '1100', label: '1100 Auric Cells' },
      { value: '2250', label: '2250 Auric Cells' },
      { value: '4025', label: '4025 Auric Cells' },
      { value: '6000', label: '6000 Auric Cells' },
      { value: '12500', label: '12500 Auric Cells' },
    ],
    'Delta Force': [
      { value: '60', label: '60 Delta Coins' },
      { value: '320', label: '320 Delta Coins' },
      { value: '750', label: '750 Delta Coins' },
      { value: '1480', label: '1480 Delta Coins' },
      { value: '1980', label: '1980 Delta Coins' },
      { value: '3950', label: '3950 Delta Coins' },
      { value: '8100', label: '8100 Delta Coins' },
      { value: '16200', label: '16200 Delta Coins' },
      { value: '24300', label: '24300 Delta Coins' },
    ],
    'Destiny 2': [
      { value: '500', label: '500 Silver' },
      { value: '1100', label: '1100 Silver' },
      { value: '2300', label: '2300 Silver' },
      { value: '3500', label: '3500 Silver' },
      { value: '6000', label: '6000 Silver' },
      { value: '7000', label: '7000 Silver' },
      { value: '10500', label: '10500 Silver' },
      { value: '12000', label: '12000 Silver' },
      { value: '14000', label: '14000 Silver' },
    ],
    'EA Sports FC': [
      { value: '500', label: '500 FC Points' },
      { value: '1050', label: '1050 FC Points' },
      { value: '1600', label: '1600 FC Points' },
      { value: '2800', label: '2800 FC Points' },
      { value: '5900', label: '5900 FC Points' },
      { value: '12000', label: '12000 FC Points' },
      { value: '18500', label: '18500 FC Points' },
      { value: '24000', label: '24000 FC Points' },
      { value: '36000', label: '36000 FC Points' },
      { value: '37000', label: '37000 FC Points' },
      { value: '48000', label: '48000 FC Points' },
      { value: '55500', label: '55500 FC Points' },
      { value: '60000', label: '60000 FC Points' },
      { value: '72000', label: '72000 FC Points' },
      { value: '74000', label: '74000 FC Points' },
      { value: '84000', label: '84000 FC Points' },
      { value: '96000', label: '96000 FC Points' },
    ],
    'eFootball': [
      { value: '130', label: '130 Coins' },
      { value: '300', label: '300 Coins' },
      { value: '1040', label: '1040 Coins' },
      { value: '2130', label: '2130 Coins' },
      { value: '3250', label: '3250 Coins' },
      { value: '5700', label: '5700 Coins' },
      { value: '12800', label: '12800 Coins' },
      { value: '25600', label: '25600 Coins' },
      { value: '38400', label: '38400 Coins' },
    ],
    'Elder Scrolls Online': [
      { value: '750', label: '750 Crowns' },
      { value: '1500', label: '1500 Crowns' },
      { value: '3000', label: '3000 Crowns' },
      { value: '5500', label: '5500 Crowns' },
      { value: '14000', label: '14000 Crowns' },
      { value: '21000', label: '21000 Crowns' },
    ],
    'Etheria: Restart': [
      { value: '60', label: '60 Crystals' },
      { value: '300', label: '300 Crystals' },
      { value: '980', label: '980 Crystals' },
      { value: '1980', label: '1980 Crystals' },
      { value: '3280', label: '3280 Crystals' },
      { value: '6480', label: '6480 Crystals' },
      { value: 'targeted-ssr', label: 'Targeted SSR Animus Guaranteed' },
      { value: 'any-0.99', label: 'Any 0.99 Pack' },
      { value: 'any-4.99', label: 'Any 4.99 Pack' },
      { value: 'any-10.99', label: 'Any 10.99 Pack' },
      { value: 'any-19.99', label: 'Any 19.99 Pack' },
      { value: 'any-30.99', label: 'Any 30.99 Pack' },
      { value: 'any-50.99', label: 'Any 50.99 Pack' },
      { value: 'any-99.99', label: 'Any 99.99 Pack' },
      { value: 'monthly-supply', label: 'Monthly Supply Card' },
      { value: 'premium-pass', label: 'Premium Pass' },
      { value: 'ultimate-pass', label: 'Ultimate Pass' },
    ],
    'Honkai: Star Rail': [
      { value: '60', label: '60 Oneiric Shard' },
      { value: '330', label: '330 Oneiric Shard' },
      { value: '1090', label: '1090 Oneiric Shard' },
      { value: '2240', label: '2240 Oneiric Shard' },
      { value: '3880', label: '3880 Oneiric Shard' },
      { value: '8080', label: '8080 Oneiric Shard' },
      { value: '16160', label: '16160 Oneiric Shard' },
      { value: '24240', label: '24240 Oneiric Shard' },
      { value: '32320', label: '32320 Oneiric Shard' },
      { value: 'all-in-one-bundle', label: 'All-In-One Bundle' },
      { value: 'express-supply-pass', label: 'Express Supply Pass' },
    ],
    'Identity V': [
      { value: '66', label: '66 Echoes' },
      { value: '203', label: '203 Echoes' },
      { value: '335', label: '335 Echoes' },
      { value: '759', label: '759 Echoes' },
      { value: '2227', label: '2227 Echoes' },
      { value: '3663', label: '3663 Echoes' },
      { value: '7249', label: '7249 Echoes' },
      { value: 'clues-package', label: 'Clues package' },
      { value: 'inspirations-package', label: 'Inspirations package' },
      { value: 'memory-sphere-package', label: 'Memory sphere package' },
    ],
    'Marvel Rivals': [
      { value: '100', label: '100 Lattices' },
      { value: '500', label: '500 Lattices' },
      { value: '1000', label: '1000 Lattices' },
      { value: '2180', label: '2180 Lattices' },
      { value: '5680', label: '5680 Lattices' },
      { value: '11680', label: '11680 Lattices' },
      { value: '23360', label: '23360 Lattices' },
      { value: '35040', label: '35040 Lattices' },
      { value: '46720', label: '46720 Lattices' },
    ],
    'Minecraft': [
      { value: '320', label: '320 Minecoins' },
      { value: '1020', label: '1020 Minecoins' },
      { value: '1720', label: '1720 Minecoins' },
      { value: '3500', label: '3500 Minecoins' },
      { value: '8800', label: '8800 Minecoins' },
      { value: '17600', label: '17600 Minecoins' },
    ],
    'Mobile Legends': [
      { value: '42', label: '42 Diamonds' },
      { value: '56', label: '56 Diamonds' },
      { value: '86', label: '86 Diamonds' },
      { value: '112', label: '112 Diamonds' },
      { value: '140', label: '140 Diamonds' },
      { value: '172', label: '172 Diamonds' },
      { value: '257', label: '257 Diamonds' },
      { value: '279', label: '279 Diamonds' },
      { value: '284', label: '284 Diamonds' },
      { value: '336', label: '336 Diamonds' },
      { value: '355', label: '355 Diamonds' },
      { value: '370', label: '370 Diamonds' },
      { value: '518', label: '518 Diamonds' },
      { value: '570', label: '570 Diamonds' },
      { value: '706', label: '706 Diamonds' },
      { value: '963', label: '963 Diamonds' },
      { value: '1067', label: '1067 Diamonds' },
      { value: '1084', label: '1084 Diamonds' },
      { value: '1163', label: '1163 Diamonds' },
      { value: '1584', label: '1584 Diamonds' },
      { value: '1704', label: '1704 Diamonds' },
      { value: '1733', label: '1733 Diamonds' },
      { value: '2195', label: '2195 Diamonds' },
      { value: '2398', label: '2398 Diamonds' },
      { value: '2855', label: '2855 Diamonds' },
      { value: '2976', label: '2976 Diamonds' },
      { value: '3688', label: '3688 Diamonds' },
      { value: '3976', label: '3976 Diamonds' },
      { value: '5532', label: '5532 Diamonds' },
      { value: '6042', label: '6042 Diamonds' },
      { value: '6288', label: '6288 Diamonds' },
      { value: '7210', label: '7210 Diamonds' },
      { value: '7502', label: '7502 Diamonds' },
      { value: '9288', label: '9288 Diamonds' },
      { value: '12976', label: '12976 Diamonds' },
      { value: '18576', label: '18576 Diamonds' },
      { value: 'weekly-diamond-pass', label: 'Weekly Diamond Pass' },
    ],
    'Call of Duty Mobile': [
      { value: '30', label: '30 CP' },
      { value: '80', label: '80 CP' },
      { value: '420', label: '420 CP' },
      { value: '880', label: '880 CP' },
      { value: '2400', label: '2400 CP' },
      { value: '5000', label: '5000 CP' },
      { value: '10800', label: '10800 CP' },
      { value: '21600', label: '21600 CP' },
      { value: '32400', label: '32400 CP' },
      { value: '43200', label: '43200 CP' },
      { value: '54000', label: '54000 CP' },
    ],
    'Garena Free Fire': [
      { value: '100', label: '100 Diamonds' },
      { value: '110', label: '110 Diamonds' },
      { value: '210', label: '210 Diamonds' },
      { value: '310', label: '310 Diamonds' },
      { value: '341', label: '341 Diamonds' },
      { value: '520', label: '520 Diamonds' },
      { value: '530', label: '530 Diamonds' },
      { value: '572', label: '572 Diamonds' },
      { value: '645', label: '645 Diamonds' },
      { value: '1060', label: '1060 Diamonds' },
      { value: '1080', label: '1080 Diamonds' },
      { value: '1166', label: '1166 Diamonds' },
      { value: '2180', label: '2180 Diamonds' },
      { value: '2200', label: '2200 Diamonds' },
      { value: '2398', label: '2398 Diamonds' },
      { value: '4450', label: '4450 Diamonds' },
      { value: '5600', label: '5600 Diamonds' },
      { value: '6160', label: '6160 Diamonds' },
      { value: '6900', label: '6900 Diamonds' },
      { value: 'monthly-membership', label: 'Monthly Membership' },
      { value: 'weekly-membership', label: 'Weekly Membership' },
    ],
  };

  // Games that require region selection
  const gamesWithRegions = {
    'Valorant': [
      { value: 'eu', label: 'EU' },
      { value: 'na', label: 'NA' },
      { value: 'ap', label: 'AP' },
      { value: 'latm', label: 'LATM' },
      { value: 'brazil', label: 'Brazil' },
      { value: 'kr', label: 'KR' },
    ],
    'League of Legends': [
      { value: 'eu', label: 'EU' },
      { value: 'na', label: 'NA' },
      { value: 'malaysia', label: 'Malaysia' },
      { value: 'singapore', label: 'Singapore' },
      { value: 'hong-kong', label: 'Hong Kong' },
      { value: 'middle-east', label: 'Middle East' },
      { value: 'latin-america', label: 'Latin America' },
      { value: 'turkey', label: 'Turkey' },
    ],
    'League of Legends: Wild Rift': [
      { value: 'na', label: 'NA' },
      { value: 'eu', label: 'EU' },
      { value: 'malaysia', label: 'Malaysia' },
      { value: 'hong-kong', label: 'Hong Kong' },
      { value: 'singapore', label: 'Singapore' },
      { value: 'turkey', label: 'Turkey' },
      { value: 'brazil', label: 'Brazil' },
    ],
    'Genshin Impact': [
      { value: 'eu', label: 'EU' },
      { value: 'na', label: 'NA' },
      { value: 'asia', label: 'Asia' },
      { value: 'tw', label: 'TW' },
      { value: 'hk', label: 'HK' },
      { value: 'mo', label: 'MO' },
    ],
    'Honkai: Star Rail': [
      { value: 'na', label: 'NA' },
      { value: 'eu', label: 'EU' },
      { value: 'asia', label: 'Asia' },
    ],
    'Mobile Legends': [
      { value: 'global', label: 'Global' },
      { value: 'asia', label: 'Asia' },
      { value: 'middle-east', label: 'Middle East' },
    ],
    'Garena Free Fire': [
      { value: 'global', label: 'Global' },
      { value: 'brazil', label: 'Brazil' },
      { value: 'latam', label: 'LATAM' },
      { value: 'india', label: 'India' },
      { value: 'indonesia', label: 'Indonesia' },
      { value: 'vietnam', label: 'Vietnam' },
      { value: 'mena', label: 'MENA' },
      { value: 'eu', label: 'EU' },
    ],
    'Wuthering Waves': [
      { value: 'america', label: 'America' },
      { value: 'asia', label: 'Asia' },
      { value: 'europe', label: 'Europe' },
      { value: 'hmt', label: 'HMT' },
      { value: 'sea', label: 'SEA' },
    ],
    'Zenless Zone Zero': [
      { value: 'eu', label: 'EU' },
      { value: 'na', label: 'NA' },
      { value: 'asia', label: 'Asia' },
      { value: 'tw', label: 'TW' },
      { value: 'hk', label: 'HK' },
      { value: 'mo', label: 'MO' },
    ],
    'World of Warcraft': [
      { value: 'eu', label: 'EU' },
      { value: 'na', label: 'NA' },
    ],
    'PlayStation': [
      { value: 'na', label: 'NA' },
      { value: 'uk', label: 'UK' },
      { value: 'turkey', label: 'Turkey' },
      { value: 'brazil', label: 'Brazil' },
      { value: 'india', label: 'India' },
      { value: 'japan', label: 'Japan' },
      { value: 'sweden', label: 'Sweden' },
      { value: 'german', label: 'German' },
      { value: 'poland', label: 'Poland' },
      { value: 'ukraine', label: 'Ukraine' },
    ],
    'Honkai: Star Rail': [
      { value: 'na', label: 'NA' },
      { value: 'eu', label: 'EU' },
      { value: 'asia', label: 'Asia' },
    ],
    'Mobile Legends': [
      { value: 'global', label: 'Global' },
      { value: 'asia', label: 'Asia' },
      { value: 'middle-east', label: 'Middle East' },
    ],
  };

  // Games that require platform selection (button-based). Albion Online currency uses Server (same field).
  const gamesWithPlatforms = {
    'Albion Online': ['Albion Americas', 'Albion Asia', 'Albion Europe'],
    'Overwatch 2': ['Battlenet', 'PlayStation', 'Xbox', 'Steam'],
    'Path of Exile 2': ['PC', 'Xbox'],
    'PUBG': ['Xbox', 'PlayStation', 'Steam'],
    'Rocket League': ['PC', 'Xbox', 'PlayStation'],
    'Rust': ['Xbox', 'PlayStation'],
    'Dead By Daylight': ['Epic Games', 'Steam', 'Xbox', 'PlayStation'],
    'Destiny 2': ['Epic Games', 'Steam', 'Xbox', 'PlayStation'],
    'EA Sports FC': ['PC (No Membership)', 'PC (With Membership)', 'Xbox', 'PlayStation'],
    'Elder Scrolls Online': ['PC', 'Xbox', 'PlayStation'],
  };

  const renderStepContent = () => {
    const serviceType = initialServiceType || formData.service_type;
    const isTopUps = serviceType === 'top-ups';

    // 8 Ball Pool / Albion Online currency: 4 steps — Game (+ Server for Albion), Details, Pricing, Preview
    const currencyUnit = (isArcRaidersCurrencyFlow || isDarkAndDarkerCurrencyFlow || isEASportsFCCurrencyFlow || isEASportsFCMobileCurrencyFlow || isFallout76CurrencyFlow || isLostArkCurrencyFlow || isNewWorldCurrencyFlow || isWarframeCurrencyFlow || isWoWMistsOfPandariaCurrencyFlow) ? 'K' : (isAlbionCurrencyFlow || isACNHCurrencyFlow || isBDOCurrencyFlow || isDeltaForceCurrencyFlow || isDiablo4CurrencyFlow || isDigimonSuperRumbleCurrencyFlow || isDonutSMPCurrencyFlow || isElderScrollsOnlineCurrencyFlow || isEscapeFromTarkovCurrencyFlow || isFinalFantasyXIVCurrencyFlow || isFischCurrencyFlow || isForzaHorizon5CurrencyFlow || isMinecraftCurrencyFlow || isOldSchoolRuneScapeCurrencyFlow || isPokemonGoCurrencyFlow || isRuneScape3CurrencyFlow) ? 'M' : (isGrowAGardenCurrencyFlow || isPathOfExileCurrencyFlow || isPathOfExile2CurrencyFlow || isRobloxCurrencyFlow || isThroneAndLibertyCurrencyFlow || isWoWClassicCurrencyFlow ? 'unit' : (isEldenRingCurrencyFlow ? 'B' : 'B'));
    if (is8BPCurrencyFlow || isAlbionCurrencyFlow || isACNHCurrencyFlow || isArcRaidersCurrencyFlow || isBDOCurrencyFlow || isDarkAndDarkerCurrencyFlow || isDeltaForceCurrencyFlow || isDiablo4CurrencyFlow || isDigimonSuperRumbleCurrencyFlow || isDonutSMPCurrencyFlow || isEASportsFCCurrencyFlow || isEASportsFCMobileCurrencyFlow || isEldenRingCurrencyFlow || isElderScrollsOnlineCurrencyFlow || isEscapeFromTarkovCurrencyFlow || isFallout76CurrencyFlow || isFinalFantasyXIVCurrencyFlow || isFischCurrencyFlow || isForzaHorizon5CurrencyFlow || isGrowAGardenCurrencyFlow || isLostArkCurrencyFlow || isMinecraftCurrencyFlow || isNewWorldCurrencyFlow || isOldSchoolRuneScapeCurrencyFlow || isPathOfExileCurrencyFlow || isPathOfExile2CurrencyFlow || isRobloxCurrencyFlow || isRuneScape3CurrencyFlow || isThroneAndLibertyCurrencyFlow || isWarframeCurrencyFlow || isWoWClassicCurrencyFlow || isWoWMistsOfPandariaCurrencyFlow || isPokemonGoCurrencyFlow) {
      switch (currentStep) {
        case 1: {
          const availableGamesForService = getAvailableGamesForAdForm('currency');
          const isEditing = !!ad;
          const albionServerOptions = ['Albion Americas', 'Albion Asia', 'Albion Europe'];
          return (
            <>
              <div className="form-group">
                <label>Game *</label>
                {isEditing ? (
                  <div style={{
                    padding: '0.75rem',
                    backgroundColor: '#1a1a1a',
                    borderRadius: '0.5rem',
                    border: '1px solid #3a3a3a',
                    color: '#a0a0a0',
                    fontSize: '0.875rem',
                    cursor: 'not-allowed',
                    opacity: 0.7
                  }}>
                    {formData.game || 'N/A'}
                    <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', marginBottom: 0 }}>
                      Game cannot be changed when editing
                    </p>
                  </div>
                ) : (
                  <CustomDropdown
                    value={formData.game}
                    onChange={(value) => setFormData({ ...formData, game: value, top_up_option: '' })}
                    options={availableGamesForService.map(game => ({ value: game, label: game, icon: getGameIcon(game) }))}
                    placeholder="Select your game"
                    required
                  />
                )}
              </div>
              {isAlbionCurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Server *</label>
                  <CustomDropdown
                    value={formData.platform}
                    onChange={(value) => setFormData({ ...formData, platform: value })}
                    options={albionServerOptions.map(p => ({ value: p, label: p }))}
                    placeholder="Select server"
                    required
                  />
                </div>
              )}
              {isDeltaForceCurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Server *</label>
                  <CustomDropdown
                    value={formData.platform}
                    onChange={(value) => setFormData({ ...formData, platform: value })}
                    options={['Tactical Turmoil', 'Havoc Warfare'].map(p => ({ value: p, label: p }))}
                    placeholder="Select server"
                    required
                  />
                </div>
              )}
              {isEscapeFromTarkovCurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Server *</label>
                  <CustomDropdown
                    value={formData.platform}
                    onChange={(value) => setFormData({ ...formData, platform: value })}
                    options={['Europe', 'North America', 'Asia'].map(p => ({ value: p, label: p }))}
                    placeholder="Select server"
                    required
                  />
                </div>
              )}
              {isBDOCurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Device *</label>
                    <CustomDropdown
                      value={formData.device || ''}
                      onChange={(value) => setFormData({ ...formData, device: value })}
                      options={['PC', 'PlayStation', 'Xbox'].map(d => ({ value: d, label: d }))}
                      placeholder="Select device"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Region *</label>
                    <CustomDropdown
                      value={formData.region || ''}
                      onChange={(value) => setFormData({ ...formData, region: value })}
                      options={['EU', 'Japan', 'Korea', 'MENA', 'NA', 'SEA', 'South America'].map(r => ({ value: r, label: r }))}
                      placeholder="Select region"
                      required
                    />
                  </div>
                </>
              )}
              {isElderScrollsOnlineCurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Device *</label>
                    <CustomDropdown
                      value={formData.device || ''}
                      onChange={(value) => setFormData({ ...formData, device: value })}
                      options={['PC', 'PlayStation', 'Xbox'].map(d => ({ value: d, label: d }))}
                      placeholder="Select device"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Region *</label>
                    <CustomDropdown
                      value={formData.region || ''}
                      onChange={(value) => setFormData({ ...formData, region: value })}
                      options={['EU', 'NA'].map(r => ({ value: r, label: r }))}
                      placeholder="Select region"
                      required
                    />
                  </div>
                </>
              )}
              {isFinalFantasyXIVCurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Region *</label>
                    <CustomDropdown
                      value={formData.region || ''}
                      onChange={(value) => setFormData({ ...formData, region: value })}
                      options={['NA', 'EU', 'JP', 'OC'].map(r => ({ value: r, label: r }))}
                      placeholder="Select region"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Server *</label>
                    <CustomDropdown
                      value={formData.server || ''}
                      onChange={(value) => setFormData({ ...formData, server: value })}
                      options={['Adamantoise', 'Aegis', 'Alexander', 'Alpha', 'Anima', 'Asura', 'Atomos', 'Bahamut', 'Balmung', 'Behemoth', 'Belias', 'Bismarck', 'Brynhildr', 'Cactuar', 'Carbuncle', 'Cerberus', 'Chocobo', 'Coeurl', 'Cuchulainn', 'Diabolos', 'Durandal', 'Excalibur', 'Exodus', 'Faerie', 'Famfrit', 'Fenrir', 'Garuda', 'Gilgamesh', 'Goblin', 'Golem', 'Gungnir', 'Hades', 'Halicarnassus', 'Hyperion', 'Ifrit', 'Innocence', 'Ixion', 'Jenova', 'Kraken', 'Kujata', 'Lamia', 'Leviathan', 'Lich', 'Louisoix', 'Maduin', 'Malboro', 'Mandragora', 'Marilith', 'Masamune', 'Mateus', 'Midgardsormr', 'Moogle', 'Odin', 'Omega', 'Pandaemonium', 'Phantom', 'Phoenix', 'Pixie', 'Rafflesia', 'Ragnarok', 'Raiden', 'Ramuh', 'Ravana', 'Ridill', 'Sagittarius', 'Sargatanas', 'Sephirot', 'Seraph', 'Shinryu', 'Shiva', 'Siren', 'Sophia', 'Spriggan', 'Tiamat', 'Titan', 'Titania', 'Tonberry', 'Twintania', 'Tycoon', 'Typhon', 'Ultima', 'Ultros', 'Unicorn', 'Valefor', 'Yojimbo', 'Zalera', 'Zeromus', 'Zodiark', 'Zurvan'].map(s => ({ value: s, label: s }))}
                      placeholder="Select server"
                      required
                    />
                  </div>
                </>
              )}
              {isFischCurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Currency *</label>
                  <CustomDropdown
                    value={formData.currency_type || ''}
                    onChange={(value) => setFormData({ ...formData, currency_type: value })}
                    options={['C$', 'E$', 'Other'].map(c => ({ value: c, label: c }))}
                    placeholder="Select currency"
                    required
                  />
                </div>
              )}
              {isForzaHorizon5CurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Device *</label>
                  <CustomDropdown
                    value={formData.device || ''}
                    onChange={(value) => setFormData({ ...formData, device: value })}
                    options={['PS5', 'PC', 'Xbox'].map(d => ({ value: d, label: d }))}
                    placeholder="Select device"
                    required
                  />
                </div>
              )}
              {isWarframeCurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Device *</label>
                  <CustomDropdown
                    value={formData.device || ''}
                    onChange={(value) => setFormData({ ...formData, device: value })}
                    options={['PC', 'Nintendo Switch', 'PlayStation', 'Xbox'].map(d => ({ value: d, label: d }))}
                    placeholder="Select device"
                    required
                  />
                </div>
              )}
              {isWoWClassicCurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Region *</label>
                    <CustomDropdown
                      value={formData.region || ''}
                      onChange={(value) => setFormData({ ...formData, region: value })}
                      options={['EU', 'EU Anniversary', 'EU Season of Discovery', 'NA & OC', 'NA & OC Anniversary', 'NA Season of Discovery'].map(r => ({ value: r, label: r }))}
                      placeholder="Select region"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Realm *</label>
                    <CustomDropdown
                      value={formData.server || ''}
                      onChange={(value) => setFormData({ ...formData, server: value })}
                      options={['Doomhowl - NA & OC Anniversary', 'Dreamscythe - NA & OC Anniversary', 'Maladath - NA & OC Anniversary', 'Nightslayer - NA & OC Anniversary'].map(s => ({ value: s, label: s }))}
                      placeholder="Select realm"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Faction *</label>
                    <CustomDropdown
                      value={formData.faction || ''}
                      onChange={(value) => setFormData({ ...formData, faction: value })}
                      options={['Alliance', 'Horde'].map(f => ({ value: f, label: f }))}
                      placeholder="Select faction"
                      required
                    />
                  </div>
                </>
              )}
              {isWoWMistsOfPandariaCurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Region *</label>
                    <CustomDropdown
                      value={formData.region || ''}
                      onChange={(value) => setFormData({ ...formData, region: value })}
                      options={['EU', 'NA', 'Oceanic'].map(r => ({ value: r, label: r }))}
                      placeholder="Select region"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Server *</label>
                    <CustomDropdown
                      value={formData.server || ''}
                      onChange={(value) => setFormData({ ...formData, server: value })}
                      options={['Myzrael - NA', 'Nazgrim - NA', 'Old Blanchy - NA', 'Pagle - NA', 'Ra-den - NA', 'Skyfury - NA', 'Sulfuras - NA', 'Westfall - NA', 'Whitemane - NA', 'Windseeker - NA'].map(s => ({ value: s, label: s }))}
                      placeholder="Select server"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Faction *</label>
                    <CustomDropdown
                      value={formData.faction || ''}
                      onChange={(value) => setFormData({ ...formData, faction: value })}
                      options={['Alliance', 'Horde'].map(f => ({ value: f, label: f }))}
                      placeholder="Select faction"
                      required
                    />
                  </div>
                </>
              )}
              {isLostArkCurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Region *</label>
                    <CustomDropdown
                      value={formData.region || ''}
                      onChange={(value) => setFormData({ ...formData, region: value })}
                      options={['NA', 'EU - Central'].map(r => ({ value: r, label: r }))}
                      placeholder="Select region"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Server *</label>
                    <CustomDropdown
                      value={formData.server || ''}
                      onChange={(value) => setFormData({ ...formData, server: value })}
                      options={['Balthorr - NA', 'Brelshaza - West - NA', 'Inanna - NA', 'Luterra - NA', 'Nineveh - NA', 'Thaemine - West - NA', 'Vairgrys - NA'].map(s => ({ value: s, label: s }))}
                      placeholder="Select server"
                      required
                    />
                  </div>
                </>
              )}
              {isNewWorldCurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Region *</label>
                    <CustomDropdown
                      value={formData.region || ''}
                      onChange={(value) => setFormData({ ...formData, region: value })}
                      options={['AP Southeast', 'EU - Central', 'SA East', 'US - East', 'US - West'].map(r => ({ value: r, label: r }))}
                      placeholder="Select region"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Server *</label>
                    <CustomDropdown
                      value={formData.server || ''}
                      onChange={(value) => setFormData({ ...formData, server: value })}
                      options={['Valhalla - US - East', 'Empi - US - East', 'Hudsonland - US - East', 'Pangea - US - East', 'Tumtum [Playstation] - US - East'].map(s => ({ value: s, label: s }))}
                      placeholder="Select server"
                      required
                    />
                  </div>
                </>
              )}
              {isThroneAndLibertyCurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Region *</label>
                    <CustomDropdown
                      value={formData.region || ''}
                      onChange={(value) => setFormData({ ...formData, region: value })}
                      options={['Eastern Americas', 'Europe', 'Japan, Oceania', 'Korea', 'South America', 'Western Americas'].map(r => ({ value: r, label: r }))}
                      placeholder="Select region"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Server *</label>
                    <CustomDropdown
                      value={formData.server || ''}
                      onChange={(value) => setFormData({ ...formData, server: value })}
                      options={['Adrenaline - Eastern Americas', 'Carnage - Eastern Americas', 'Ivory - Eastern Americas', 'Pippin - Eastern Americas', 'Snowburn - Eastern Americas', 'Stellarite - Eastern Americas'].map(s => ({ value: s, label: s }))}
                      placeholder="Select server"
                      required
                    />
                  </div>
                </>
              )}
              {isDiablo4CurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Game mode *</label>
                  <CustomDropdown
                    value={formData.game_mode || ''}
                    onChange={(value) => setFormData({ ...formData, game_mode: value })}
                    options={['Seasonal Realm - Softcore', 'Eternal Realm - Hardcore', 'Eternal Realm - Softcore', 'Seasonal Realm - Hardcore'].map(g => ({ value: g, label: g }))}
                    placeholder="Select game mode"
                    required
                  />
                </div>
              )}
              {isDigimonSuperRumbleCurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Server *</label>
                  <CustomDropdown
                    value={formData.platform || ''}
                    onChange={(value) => setFormData({ ...formData, platform: value })}
                    options={['Tokomon', 'Koromon', 'Mochimon', 'Pukamon'].map(p => ({ value: p, label: p }))}
                    placeholder="Select server"
                    required
                  />
                </div>
              )}
              {isEASportsFCMobileCurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Region *</label>
                  <CustomDropdown
                    value={formData.region || ''}
                    onChange={(value) => setFormData({ ...formData, region: value })}
                    options={['iOS', 'Android'].map(r => ({ value: r, label: r }))}
                    placeholder="Select region"
                    required
                  />
                </div>
              )}
              {isEldenRingCurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Device *</label>
                  <CustomDropdown
                    value={formData.device || ''}
                    onChange={(value) => setFormData({ ...formData, device: value })}
                    options={['PC', 'PlayStation', 'Xbox'].map(d => ({ value: d, label: d }))}
                    placeholder="Select device"
                    required
                  />
                </div>
              )}
              {isFallout76CurrencyFlow && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label>Device *</label>
                  <CustomDropdown
                    value={formData.device || ''}
                    onChange={(value) => setFormData({ ...formData, device: value })}
                    options={['PC', 'PlayStation', 'Xbox'].map(d => ({ value: d, label: d }))}
                    placeholder="Select device"
                    required
                  />
                </div>
              )}
              {isPathOfExileCurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Device *</label>
                    <CustomDropdown
                      value={formData.device || ''}
                      onChange={(value) => setFormData({ ...formData, device: value })}
                      options={['PC', 'PlayStation', 'Xbox'].map(d => ({ value: d, label: d }))}
                      placeholder="Select device"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Server *</label>
                    <CustomDropdown
                      value={formData.server || ''}
                      onChange={(value) => setFormData({ ...formData, server: value })}
                      options={['Hardcore', 'Hardcore Ruthless', 'HC Keepers', 'HC Legacy of Phrecia', 'Keepers', 'Legacy of Phrecia', 'Ruthless', 'Standard'].map(s => ({ value: s, label: s }))}
                      placeholder="Select server"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Orbs *</label>
                    <CustomDropdown
                      value={formData.orb || ''}
                      onChange={(value) => setFormData({ ...formData, orb: value })}
                      options={["Ancient Orb", "Blessed Orb", "Cartographer's Chisel", "Chaos Orb", "Chromatic Orb", "Divine Orb", "Eternal Orb", "Exalted Orb", "Gemcutter's Prism", "Harbinger's Orb", "Hinekora's Lock", "Jeweller's Orb", "Mirror of Kalandra", "Orb of Alchemy", "Orb of Alteration", "Orb of Annulment", "Orb of Chance", "Orb of Fusing", "Orb of Horizons", "Orb of Regret", "Orb of Scouring", "Other", "Regal Orb", "Vaal Orb"].map(o => ({ value: o, label: o }))}
                      placeholder="Select orb"
                      required
                    />
                  </div>
                </>
              )}
              {isPathOfExile2CurrencyFlow && (
                <>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Server *</label>
                    <CustomDropdown
                      value={formData.server || ''}
                      onChange={(value) => setFormData({ ...formData, server: value })}
                      options={['Fate of the Vaal', 'Hardcore', 'HC Fate of the Vaal', 'Standard'].map(s => ({ value: s, label: s }))}
                      placeholder="Select server"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Orbs *</label>
                    <CustomDropdown
                      value={formData.orb || ''}
                      onChange={(value) => setFormData({ ...formData, orb: value })}
                      options={['Divine Orb', 'Exalted Orb', "Greater Jeweller's Orb", "Lesser Jeweller's Orb", 'Mirror of Kalandra', 'Orb of Alchemy', 'Orb of Chance', 'Other', "Perfect Jeweller's Orb", 'Regal Orb'].map(o => ({ value: o, label: o }))}
                      placeholder="Select orb"
                      required
                    />
                  </div>
                </>
              )}
            </>
          );
        }
        case 2: {
          let currencyMinRequired = 1;
          if (isACNHCurrencyFlow || isDarkAndDarkerCurrencyFlow || isDonutSMPCurrencyFlow || isEASportsFCCurrencyFlow || isEASportsFCMobileCurrencyFlow) currencyMinRequired = 10;
          else if (isFallout76CurrencyFlow) currencyMinRequired = 40;
          else if (isArcRaidersCurrencyFlow || isBDOCurrencyFlow) currencyMinRequired = 1000;
          else if (isDeltaForceCurrencyFlow) currencyMinRequired = 5;
          else if (isDiablo4CurrencyFlow) currencyMinRequired = 5000;
          else if (isFinalFantasyXIVCurrencyFlow) currencyMinRequired = 5;
          else if (isFischCurrencyFlow) currencyMinRequired = 10;
          else if (isForzaHorizon5CurrencyFlow) currencyMinRequired = 100;
          else if (isGrowAGardenCurrencyFlow) currencyMinRequired = 500;
          else if (isMinecraftCurrencyFlow || isNewWorldCurrencyFlow) currencyMinRequired = 10;
          else if (isRobloxCurrencyFlow) currencyMinRequired = 100;
          else if (isRuneScape3CurrencyFlow) currencyMinRequired = 100;
          else if (isThroneAndLibertyCurrencyFlow) currencyMinRequired = 100;
          else if (isWarframeCurrencyFlow) currencyMinRequired = 1000;
          else if (isWoWClassicCurrencyFlow) currencyMinRequired = 100;
          else if (isWoWMistsOfPandariaCurrencyFlow) currencyMinRequired = 1000;
          else if (isLostArkCurrencyFlow || isDigimonSuperRumbleCurrencyFlow || isElderScrollsOnlineCurrencyFlow || isEscapeFromTarkovCurrencyFlow || isOldSchoolRuneScapeCurrencyFlow || isPathOfExileCurrencyFlow || isPathOfExile2CurrencyFlow || isPokemonGoCurrencyFlow) currencyMinRequired = 1;
          else if (isDonutSMPCurrencyFlow) currencyMinRequired = 10;
          else if (isEldenRingCurrencyFlow) currencyMinRequired = 1;
          const minQtyNum = formData.min_quantity != null && formData.min_quantity !== '' ? parseInt(formData.min_quantity, 10) : currencyMinRequired;
          const minQuantityError = (formData.min_quantity != null && formData.min_quantity !== '') && (isNaN(minQtyNum) || minQtyNum < currencyMinRequired);
          const stockQtyNum = formData.quantity != null && formData.quantity !== '' ? parseInt(formData.quantity, 10) : 0;
          const stockTooLowError = !isNaN(stockQtyNum) && !isNaN(minQtyNum) && stockQtyNum > 0 && stockQtyNum < minQtyNum;
          const minQuantityInputValue = (formData.min_quantity == null || formData.min_quantity === '' || minQtyNum < currencyMinRequired) ? currencyMinRequired : minQtyNum;
          const deliveryTimeOptions8BP = [
            { value: '20 min', label: '20 min' },
            { value: '1 h', label: '1 h' },
            { value: '5 h', label: '5 h' },
            { value: '12 h', label: '12 h' },
            { value: '1 day', label: '1 day' },
          ];
          return (
            <>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>Description (Optional)</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 500) setFormData({ ...formData, description: value });
                  }}
                  placeholder="Type here..."
                  maxLength={500}
                  rows={4}
                />
                <div className="form-hint" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.25rem', marginBottom: 0 }}>
                  {(formData.description || '').length}/500
                </div>
                <p className="form-hint" style={{ marginTop: '0.5rem' }}>
                  The listing title and description must be accurate and as informative as possible (no random or lottery). Misleading description is a violation of our <strong>Seller Rules</strong>.
                </p>
              </div>
              <div className="ad-form-section">
                <h3 className="ad-form-section-title">Delivery</h3>
                <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                  <label>Guaranteed delivery time *</label>
                  <CustomDropdown
                    value={formData.delivery_time}
                    onChange={(value) => setFormData({ ...formData, delivery_time: value })}
                    options={deliveryTimeOptions8BP}
                    placeholder="Choose delivery time"
                    required
                  />
                  <p className="form-hint" style={{ marginTop: '0.45rem' }}>
                    Faster delivery improves your offer’s ranking.
                  </p>
                </div>
                <DeliveryMethodSelector
                  deliveryMethods={deliveryMethods8BallPool}
                  selectedValue={formData.delivery_method}
                  onChange={(value) => setFormData({ ...formData, delivery_method: value })}
                  showBeta
                />
              </div>
              <div className="ad-form-section">
                <h3 className="ad-form-section-title">Quantity</h3>
                <div className="ad-form-section-grid">
                  <div className="form-group">
                    <label>Total quantity available (stock)</label>
                    <input
                      type="number"
                      min={currencyMinRequired}
                      value={formData.quantity ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') { setFormData({ ...formData, quantity: '' }); return; }
                        const q = parseInt(v, 10);
                        if (!isNaN(q) && q >= 1) setFormData({ ...formData, quantity: q });
                      }}
                      placeholder="e.g. 1000"
                      className={stockTooLowError ? 'input-error' : undefined}
                    />
                    <span className="form-hint" style={{ marginTop: '0.25rem', display: 'block' }}>in {currencyUnit} {currencyUnit === 'B' ? '(coins)' : currencyUnit === 'K' ? '(thousands)' : `(${CURRENCY_UNIT_LABEL_BY_GAME[formData.game] || 'Silver'})`}</span>
                    {stockTooLowError && (
                      <p className="form-error-text">
                        Total quantity (stock) must be greater than or equal to Minimum offer quantity.
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Minimum offer quantity</label>
                    <input
                      type="number"
                      min={currencyMinRequired}
                      value={minQuantityInputValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') { setFormData({ ...formData, min_quantity: '' }); return; }
                        const q = parseInt(v, 10);
                        if (!isNaN(q) && q >= 0) setFormData({ ...formData, min_quantity: q });
                      }}
                      placeholder={currencyMinRequired === 1000 ? '1000 K' : currencyMinRequired === 500 ? '500 unit' : currencyMinRequired === 100 ? '100 M' : currencyMinRequired === 10 ? '10 M' : currencyMinRequired === 5 ? '5 M' : (currencyUnit === 'M' ? '1 M' : currencyUnit === 'unit' ? '500 unit' : '1 B')}
                      className={minQuantityError ? 'input-error' : undefined}
                    />
                    <span className="form-hint" style={{ marginTop: '0.25rem', display: 'block' }}>Min. {currencyMinRequired} {currencyUnit}</span>
                    {minQuantityError && (
                      <p className="form-error-text">
                        Minimum offer quantity must be at least {currencyMinRequired} {currencyUnit}.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          );
        }
        case 3:
          return (
            <>
              <div style={{ padding: '1rem', backgroundColor: '#1f1f1f', borderRadius: '0.5rem', border: '1px solid #3a3a3a', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.9375rem', fontWeight: '600', color: '#e5e5e5' }}>Price</h3>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#e5e5e5' }}>Price per 1 {currencyUnit} :</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.price}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) setFormData({ ...formData, price: value });
                      }}
                      placeholder="Price"
                      style={{
                        flex: 1,
                        padding: '0.625rem 0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #3a3a3a',
                        backgroundColor: '#1a1a1a',
                        color: '#e5e5e5',
                        fontSize: '0.875rem'
                      }}
                    />
                    <span style={{ padding: '0.625rem 1rem', borderRadius: '0.5rem', border: '1px solid #3a3a3a', backgroundColor: '#1a1a1a', color: '#a0a0a0', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>$ USD</span>
                  </div>
                  <p className="form-hint" style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#a0a0a0' }}>
                    Competitive prices improve your offer's ranking in the offer list.
                  </p>
                </div>
              </div>
              {/* Volume discount - table with min quantity (B) and discount % */}
              <div style={{ padding: '1rem', backgroundColor: '#1f1f1f', borderRadius: '0.5rem', border: '1px solid #3a3a3a', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.9375rem', fontWeight: '600', color: '#e5e5e5' }}>Volume discount</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem', fontSize: '0.8125rem', color: '#a0a0a0', fontWeight: '500' }}>
                  <span>Minimum quantity for discount</span>
                  <span>Discount percentage</span>
                  <span style={{ width: 32 }} />
                </div>
                {(formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }]).map((row, index) => (
                  <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <input
                        type="number"
                        min="0"
                        value={row.min_quantity ?? ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                          const next = [...(formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }])];
                          next[index] = { ...next[index], min_quantity: val === '' ? 0 : (isNaN(val) ? next[index].min_quantity : val) };
                          setFormData({ ...formData, volume_discount: next });
                        }}
                        placeholder="0"
                        style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', border: '1px solid #3a3a3a', backgroundColor: '#1a1a1a', color: '#e5e5e5', fontSize: '0.875rem' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: '#a0a0a0', whiteSpace: 'nowrap' }}>{currencyUnit}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={row.discount_percent ?? ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                          const next = [...(formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }])];
                          next[index] = { ...next[index], discount_percent: val === '' ? 0 : (isNaN(val) ? next[index].discount_percent : Math.min(100, Math.max(0, val))) };
                          setFormData({ ...formData, volume_discount: next });
                        }}
                        placeholder="0"
                        style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', border: '1px solid #3a3a3a', backgroundColor: '#1a1a1a', color: '#e5e5e5', fontSize: '0.875rem' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: '#a0a0a0', whiteSpace: 'nowrap' }}>%</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const next = (formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }]).filter((_, i) => i !== index);
                        if (next.length === 0) next.push({ min_quantity: 0, discount_percent: 0 });
                        setFormData({ ...formData, volume_discount: next });
                      }}
                      style={{ padding: '0.5rem', background: 'none', border: 'none', color: '#a0a0a0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      aria-label="Delete row"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, volume_discount: [...(formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }]), { min_quantity: 0, discount_percent: 0 }] })}
                  style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #3a3a3a', backgroundColor: '#1a1a1a', color: '#e5e5e5', fontSize: '0.875rem', cursor: 'pointer', marginTop: '0.25rem' }}
                >
                  + Add row
                </button>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#1f1f1f', borderRadius: '0.5rem', border: '1px solid #3a3a3a' }}>
                <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9375rem', fontWeight: '600', color: '#e5e5e5' }}>Fee structure</h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#a0a0a0' }}>Flat fee (per purchase): $0.00 USD</p>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#a0a0a0' }}>Percentage fee (per purchase): 5% of Price</p>
              </div>
            </>
          );
        case 4: {
          let reviewDefaultMin = 1;
          if (isACNHCurrencyFlow || isDarkAndDarkerCurrencyFlow || isDonutSMPCurrencyFlow || isEASportsFCCurrencyFlow || isEASportsFCMobileCurrencyFlow) reviewDefaultMin = 10;
          else if (isFallout76CurrencyFlow) reviewDefaultMin = 40;
          else if (isArcRaidersCurrencyFlow || isBDOCurrencyFlow) reviewDefaultMin = 1000;
          else if (isDeltaForceCurrencyFlow) reviewDefaultMin = 5;
          else if (isDiablo4CurrencyFlow) reviewDefaultMin = 5000;
          else if (isFinalFantasyXIVCurrencyFlow) reviewDefaultMin = 5;
          else if (isFischCurrencyFlow || isMinecraftCurrencyFlow || isNewWorldCurrencyFlow) reviewDefaultMin = 10;
          else if (isRobloxCurrencyFlow || isRuneScape3CurrencyFlow || isThroneAndLibertyCurrencyFlow || isWoWClassicCurrencyFlow) reviewDefaultMin = 100;
          else if (isWarframeCurrencyFlow || isWoWMistsOfPandariaCurrencyFlow) reviewDefaultMin = 1000;
          else if (isForzaHorizon5CurrencyFlow) reviewDefaultMin = 100;
          else if (isGrowAGardenCurrencyFlow) reviewDefaultMin = 500;
          else if (isLostArkCurrencyFlow || isDigimonSuperRumbleCurrencyFlow || isElderScrollsOnlineCurrencyFlow || isEscapeFromTarkovCurrencyFlow || isOldSchoolRuneScapeCurrencyFlow || isPathOfExileCurrencyFlow || isPathOfExile2CurrencyFlow || isPokemonGoCurrencyFlow || isEldenRingCurrencyFlow) reviewDefaultMin = 1;
          const reviewMinQty = formData.min_quantity ?? reviewDefaultMin;
          return (
            <div className="form-group">
              <p style={{ color: '#e5e5e5', fontSize: '0.9375rem', margin: 0 }}>
                Review your offer: {isAlbionCurrencyFlow ? 'Albion Online Silver' : isACNHCurrencyFlow ? 'Animal Crossing: New Horizons Bells' : isArcRaidersCurrencyFlow ? 'Arc Raiders Currency' : isBDOCurrencyFlow ? 'Black Desert Online Silver' : isDarkAndDarkerCurrencyFlow ? 'Dark and Darker Gold' : isDeltaForceCurrencyFlow ? 'Delta Force Tekniq Alloy' : isDiablo4CurrencyFlow ? 'Diablo 4 Gold' : isDigimonSuperRumbleCurrencyFlow ? 'Digimon Super Rumble Bits' : isDonutSMPCurrencyFlow ? 'DonutSMP Money' : isEASportsFCCurrencyFlow ? 'EA Sports FC Coins' : isEASportsFCMobileCurrencyFlow ? 'EA Sports FC Mobile Coins' : isElderScrollsOnlineCurrencyFlow ? 'Elder Scrolls Online Gold' : isEscapeFromTarkovCurrencyFlow ? 'Escape From Tarkov Roubles' : isFallout76CurrencyFlow ? 'Fallout 76 Caps' : isFinalFantasyXIVCurrencyFlow ? 'Final Fantasy XIV Gil' : isFischCurrencyFlow ? 'Fisch Money' : isForzaHorizon5CurrencyFlow ? 'Forza Horizon 5 Credits' : isGrowAGardenCurrencyFlow ? 'Grow a Garden Tokens' : isLostArkCurrencyFlow ? 'Lost Ark Gold' : isMinecraftCurrencyFlow ? 'Minecraft Hypixel Coins' : isNewWorldCurrencyFlow ? 'New World Coins' : isOldSchoolRuneScapeCurrencyFlow ? 'Old School RuneScape Currency' : isPathOfExileCurrencyFlow ? 'Path of Exile Currency' : isPathOfExile2CurrencyFlow ? 'Path of Exile 2 Currency' : isRobloxCurrencyFlow ? 'Roblox Robux' : isRuneScape3CurrencyFlow ? 'RuneScape 3 Gold' : isThroneAndLibertyCurrencyFlow ? 'Throne and Liberty Lucent' : isWarframeCurrencyFlow ? 'Warframe Platinum' : isWoWClassicCurrencyFlow ? 'WoW Classic Gold' : isWoWMistsOfPandariaCurrencyFlow ? 'WoW Mists of Pandaria Gold' : isPokemonGoCurrencyFlow ? 'Pokemon Go Stardust' : isEldenRingCurrencyFlow ? 'Elden Ring Runes' : '8 Ball Pool Coins'} — {formData.quantity ?? 0} {currencyUnit} total, min {reviewMinQty} {currencyUnit}. Price: ${formData.price || '0'} per 1 {currencyUnit}.
              </p>
              <p style={{ color: '#a0a0a0', fontSize: '0.8125rem', marginTop: '0.5rem', marginBottom: 0 }}>
                Click "Publish Ad" to list your offer.
              </p>
            </div>
          );
        }
        default:
          return null;
      }
    }

    switch (currentStep) {
      case 1:
        // Step 1: Game Selection Only
        const availableGamesForService = getAvailableGamesForAdForm(serviceType);
        const isEditing = !!ad;
        return (
          <div className="form-group">
            <label>Game *</label>
            {isEditing ? (
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#1a1a1a',
                borderRadius: '0.5rem',
                border: '1px solid #3a3a3a',
                color: '#a0a0a0',
                fontSize: '0.875rem',
                cursor: 'not-allowed',
                opacity: 0.7
              }}>
                {formData.game || 'N/A'}
                <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', marginBottom: 0 }}>
                  Game cannot be changed when editing
                </p>
              </div>
            ) : (
              <CustomDropdown
                value={formData.game}
                onChange={(value) => setFormData({ ...formData, game: value, top_up_option: '' })}
                options={availableGamesForService.map(game => ({ value: game, label: game, icon: getGameIcon(game) }))}
                placeholder="Select your game"
                required
              />
            )}
          </div>
        );

      case 2:
        // Step 2: Offer Details (only for top-ups)
        if (isTopUps && formData.game) {
          const topUpsOptions = gameTopUpsOptions[formData.game] || [];
          const isEditing = !!ad;
          const selectedOption = topUpsOptions.find(opt => opt.value === formData.top_up_option);
          const optionLabel = selectedOption?.label || formData.top_up_option || 'N/A';
          const getTopUpOptionIcon = (gameName, optionValue) => {
            if (gameName !== 'League of Legends') return null;
            const amountNum = parseInt(String(optionValue || ''), 10);
            if (Number.isNaN(amountNum)) return null;
            if (amountNum <= 575) return '/topups/leagueoflegends/150riotpoints.webp';
            if (amountNum >= 1240 && amountNum <= 8390) return '/topups/leagueoflegends/1240riotpoints.webp';
            if (amountNum >= 10000) return '/topups/leagueoflegends/10000riotpoints.webp';
            return '/topups/leagueoflegends/150riotpoints.webp';
          };
          
          if (topUpsOptions.length === 0) {
            return (
              <div className="form-group">
                <label>Offer Details *</label>
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#2a2a2a', 
                  borderRadius: '0.5rem',
                  border: '1px solid #3a3a3a',
                  color: '#a0a0a0',
                  fontSize: '0.875rem'
                }}>
                  <p style={{ margin: 0 }}>
                    Top-up options for this game are not yet configured. Please contact support or use a different game.
                  </p>
                </div>
              </div>
            );
          }
          return (
            <div className="form-group">
              <label>Offer Details *</label>
              {isEditing ? (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: '#1a1a1a',
                  borderRadius: '0.5rem',
                  border: '1px solid #3a3a3a',
                  color: '#a0a0a0',
                  fontSize: '0.875rem',
                  cursor: 'not-allowed',
                  opacity: 0.7
                }}>
                  {optionLabel}
                  <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', marginBottom: 0 }}>
                    Offer details cannot be changed when editing
                  </p>
                </div>
              ) : (
                <CustomDropdown
                  value={formData.top_up_option}
                  onChange={(value) => setFormData({ ...formData, top_up_option: value })}
                  options={topUpsOptions.map(option => ({
                    value: option.value,
                    label: option.label,
                    icon: getTopUpOptionIcon(formData.game, option.value),
                  }))}
                  placeholder="Select what you want to sell"
                  required
                />
              )}
            </div>
          );
        }
        // 8 Ball Pool / Albion / Animal Crossing / Arc Raiders / Black Desert Online / Dark and Darker / Delta Force currency: Step 2 = Details (optional description, delivery, quantity) — no Service & Attributes
        const is8BPCurrencyStep2 = !isTopUps && formData.game === '8 Ball Pool' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isACNHCurrencyStep2 = !isTopUps && formData.game === 'Animal Crossing: New Horizons' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isAlbionCurrencyStep2Details = !isTopUps && formData.game === 'Albion Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isArcRaidersCurrencyStep2Details = !isTopUps && formData.game === 'Arc Raiders' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isBDOCurrencyStep2Details = !isTopUps && formData.game === 'Black Desert Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isDarkAndDarkerCurrencyStep2Details = !isTopUps && formData.game === 'Dark and Darker' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isDeltaForceCurrencyStep2Details = !isTopUps && formData.game === 'Delta Force' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isDiablo4CurrencyStep2Details = !isTopUps && formData.game === 'Diablo 4' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isDigimonSuperRumbleCurrencyStep2Details = !isTopUps && formData.game === 'Digimon Super Rumble' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isDonutSMPCurrencyStep2Details = !isTopUps && formData.game === 'DonutSMP' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isEASportsFCCurrencyStep2Details = !isTopUps && formData.game === 'EA Sports FC' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isEASportsFCMobileCurrencyStep2Details = !isTopUps && formData.game === 'EA Sports FC Mobile' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isPokemonGoCurrencyStep2Details = !isTopUps && formData.game === 'Pokemon Go' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        const isRuneScape3CurrencyStep2Details = !isTopUps && formData.game === 'RuneScape 3' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        if (is8BPCurrencyStep2 || isACNHCurrencyStep2 || isAlbionCurrencyStep2Details || isArcRaidersCurrencyStep2Details || isBDOCurrencyStep2Details || isDarkAndDarkerCurrencyStep2Details || isDeltaForceCurrencyStep2Details || isDiablo4CurrencyStep2Details || isDigimonSuperRumbleCurrencyStep2Details || isDonutSMPCurrencyStep2Details || isEASportsFCCurrencyStep2Details || isEASportsFCMobileCurrencyStep2Details || isPokemonGoCurrencyStep2Details || isRuneScape3CurrencyStep2Details) {
          const step2Unit = (isArcRaidersCurrencyStep2Details || isDarkAndDarkerCurrencyStep2Details || isEASportsFCCurrencyStep2Details || isEASportsFCMobileCurrencyStep2Details) ? 'K' : (isACNHCurrencyStep2 || isAlbionCurrencyStep2Details || isBDOCurrencyStep2Details || isDeltaForceCurrencyStep2Details || isDiablo4CurrencyStep2Details || isDigimonSuperRumbleCurrencyStep2Details || isDonutSMPCurrencyStep2Details || isPokemonGoCurrencyStep2Details || isRuneScape3CurrencyStep2Details) ? 'M' : 'B';
          const step2MinQty = (isACNHCurrencyStep2 || isDarkAndDarkerCurrencyStep2Details || isDonutSMPCurrencyStep2Details || isEASportsFCCurrencyStep2Details || isEASportsFCMobileCurrencyStep2Details) ? 10 : (isArcRaidersCurrencyStep2Details || isBDOCurrencyStep2Details ? 1000 : (isDeltaForceCurrencyStep2Details ? 5 : (isDiablo4CurrencyStep2Details ? 5000 : (isDigimonSuperRumbleCurrencyStep2Details || isPokemonGoCurrencyStep2Details ? 1 : (isRuneScape3CurrencyStep2Details ? 100 : 1)))));
          const minQtyNum = formData.min_quantity != null && formData.min_quantity !== '' ? parseInt(formData.min_quantity, 10) : step2MinQty;
          const minQuantityError = (formData.min_quantity != null && formData.min_quantity !== '') && (isNaN(minQtyNum) || minQtyNum < step2MinQty);
          const step2StockQty = formData.quantity != null && formData.quantity !== '' ? parseInt(formData.quantity, 10) : 0;
          const step2StockTooLow = !isNaN(step2StockQty) && !isNaN(minQtyNum) && step2StockQty > 0 && step2StockQty < minQtyNum;
          const deliveryTimeOptions8BP = [
            { value: '20 min', label: '20 min' },
            { value: '1 h', label: '1 h' },
            { value: '5 h', label: '5 h' },
            { value: '12 h', label: '12 h' },
            { value: '1 day', label: '1 day' },
          ];
          return (
            <>
              {/* Description (Optional) */}
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label style={{ color: '#a0a0a0', fontWeight: '500' }}>Description (Optional)</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 500) setFormData({ ...formData, description: value });
                  }}
                  placeholder="Type here..."
                  maxLength={500}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #3a3a3a',
                    backgroundColor: '#1a1a1a',
                    color: '#e5e5e5',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.25rem', fontSize: '0.8125rem', color: '#a0a0a0' }}>
                  {(formData.description || '').length}/500
                </div>
                <p className="form-hint" style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#a0a0a0' }}>
                  The listing title and description must be accurate and as informative as possible (no random or lottery). Misleading description is a violation of our <strong>Seller Rules</strong>.
                </p>
              </div>

              {/* Delivery */}
              <div className="ad-form-section">
                <h3 className="ad-form-section-title">Delivery</h3>
                <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                  <label>Guaranteed delivery time *</label>
                  <CustomDropdown
                    value={formData.delivery_time}
                    onChange={(value) => setFormData({ ...formData, delivery_time: value })}
                    options={deliveryTimeOptions8BP}
                    placeholder="Choose delivery time"
                    required
                  />
                  <p className="form-hint" style={{ marginTop: '0.45rem' }}>
                    Faster delivery improves your offer’s ranking.
                  </p>
                </div>
                <DeliveryMethodSelector
                  deliveryMethods={deliveryMethods8BallPool}
                  selectedValue={formData.delivery_method}
                  onChange={(value) => setFormData({ ...formData, delivery_method: value })}
                  showBeta
                />
              </div>

              {/* Quantity */}
              <div className="ad-form-section">
                <h3 className="ad-form-section-title">Quantity</h3>
                <div className="ad-form-section-grid">
                  <div className="form-group">
                    <label>Total quantity available (stock)</label>
                    <input
                      type="number"
                      min={step2MinQty}
                      value={formData.quantity ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') { setFormData({ ...formData, quantity: '' }); return; }
                        const q = parseInt(v, 10);
                        if (!isNaN(q) && q >= 1) setFormData({ ...formData, quantity: q });
                      }}
                      placeholder="e.g. 1000"
                      className={step2StockTooLow ? 'input-error' : undefined}
                    />
                    <span className="form-hint" style={{ marginTop: '0.25rem', display: 'block' }}>in {step2Unit} {step2Unit === 'K' ? '(thousands)' : '(millions)'}</span>
                    {step2StockTooLow && (
                      <p className="form-error-text">
                        Total quantity (stock) must be greater than or equal to Minimum offer quantity.
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Minimum offer quantity ({step2Unit})</label>
                    <input
                      type="number"
                      min={step2MinQty}
                      value={formData.min_quantity ?? (isACNHCurrencyStep2 || isDarkAndDarkerCurrencyStep2Details || isDonutSMPCurrencyStep2Details || isEASportsFCCurrencyStep2Details || isEASportsFCMobileCurrencyStep2Details ? 10 : (isArcRaidersCurrencyStep2Details || isBDOCurrencyStep2Details) ? 1000 : (isDeltaForceCurrencyStep2Details ? 5 : (isDiablo4CurrencyStep2Details ? 5000 : (isDigimonSuperRumbleCurrencyStep2Details || isPokemonGoCurrencyStep2Details ? 1 : (isRuneScape3CurrencyStep2Details ? 100 : '')))))}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') { setFormData({ ...formData, min_quantity: '' }); return; }
                        const q = parseInt(v, 10);
                        if (!isNaN(q) && q >= 0) setFormData({ ...formData, min_quantity: q });
                      }}
                      placeholder={step2MinQty === 1000 ? '1000 K' : step2MinQty === 10 ? '10 M' : (step2Unit === 'M' ? '1 M' : '1 B')}
                      className={minQuantityError ? 'input-error' : undefined}
                    />
                    <span className="form-hint" style={{ marginTop: '0.25rem', display: 'block' }}>Min. {step2MinQty} {step2Unit}{step2MinQty === 10 ? ' (10 million Bells)' : step2MinQty === 1000 ? ' (1000 = 1K)' : ''}</span>
                    {minQuantityError && (
                      <p className="form-error-text">
                        Minimum offer quantity must be at least {step2MinQty} {step2Unit}.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          );
        }
        // If not top-ups, step 2 is service type/platform - show that content
        // For non-top-ups, we show the service type/platform form in step 2
        if (!isTopUps) {
          const is8BallPool = formData.game === '8 Ball Pool';
          const isAlbionCurrencyStep2 = formData.game === 'Albion Online' && (formData.service_type === 'currency' || initialServiceType === 'currency');
          const showServerForAlbion = isAlbionCurrencyStep2 && gamesWithPlatforms['Albion Online'];
          const platformOptionsForAlbion = showServerForAlbion ? (gamesWithPlatforms['Albion Online'] || ['Albion Americas', 'Albion Asia', 'Albion Europe']) : [];
          return (
            <>
              {!initialServiceType && (
                <div className="form-group">
                  <label>Service Type *</label>
                  <CustomDropdown
                    value={formData.service_type}
                    onChange={(value) => setFormData({ ...formData, service_type: value })}
                    options={serviceTypes.map(type => ({ value: type, label: type }))}
                    placeholder="Select service"
                    required
                  />
                </div>
              )}

              {showServerForAlbion && (
                <div className="form-group">
                  <label>Server *</label>
                  <CustomDropdown
                    value={formData.platform}
                    onChange={(value) => setFormData({ ...formData, platform: value })}
                    options={platformOptionsForAlbion.map(p => ({ value: p, label: p }))}
                    placeholder="Select server"
                    required
                  />
                </div>
              )}

              {!is8BallPool && !isAlbionCurrencyStep2 && resolveRequiresPlatform(formData.game) && (
                <div className="form-group">
                  <label>Platform *</label>
                  <CustomDropdown
                    value={formData.platform}
                    onChange={(value) => setFormData({ ...formData, platform: value })}
                    options={platforms.map(platform => ({ value: platform, label: platform }))}
                    placeholder="Select platform"
                    required
                  />
                </div>
              )}

              {is8BallPool && (formData.service_type === 'accounts' || initialServiceType === 'accounts') && (
              <>
                <div className="form-group">
                  <label>Level *</label>
                  <input
                    type="text"
                    value={formData.account_attributes?.level || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      account_attributes: {
                        ...formData.account_attributes,
                        level: e.target.value
                      }
                    })}
                    placeholder="Enter account level"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Cash *</label>
                  <input
                    type="text"
                    value={formData.account_attributes?.cash || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      account_attributes: {
                        ...formData.account_attributes,
                        cash: e.target.value
                      }
                    })}
                    placeholder="Enter cash amount"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Coins *</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={(() => {
                        const coins = formData.account_attributes?.coins || '';
                        // Extract number part (remove m or b suffix)
                        const match = coins.match(/^(\d+\.?\d*)/);
                        return match ? match[1] : '';
                      })()}
                      onChange={(e) => {
                        const numberValue = e.target.value;
                        const currentCoins = formData.account_attributes?.coins || '';
                        // Get current suffix (m or b) or default to 'm'
                        const suffix = currentCoins.match(/([mb])$/i)?.[1]?.toLowerCase() || 'm';
                        const newValue = numberValue ? `${numberValue}${suffix}` : '';
                        setFormData({ 
                          ...formData, 
                          account_attributes: {
                            ...formData.account_attributes,
                            coins: newValue
                          }
                        });
                      }}
                      placeholder="Enter number"
                      required
                      min="0"
                      step="0.01"
                      style={{ flex: 1 }}
                    />
                    <select
                      value={(() => {
                        const coins = formData.account_attributes?.coins || '';
                        const suffix = coins.match(/([mb])$/i)?.[1]?.toLowerCase() || 'm';
                        return suffix;
                      })()}
                      onChange={(e) => {
                        const suffix = e.target.value;
                        const currentCoins = formData.account_attributes?.coins || '';
                        // Extract number part
                        const match = currentCoins.match(/^(\d+\.?\d*)/);
                        const numberValue = match ? match[1] : '';
                        const newValue = numberValue ? `${numberValue}${suffix}` : suffix;
                        setFormData({ 
                          ...formData, 
                          account_attributes: {
                            ...formData.account_attributes,
                            coins: newValue
                          }
                        });
                      }}
                      style={{
                        padding: '0.625rem 1.75rem 0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-quaternary)',
                        color: 'var(--text-primary)',
                        fontFamily: "Gilroy, sans-serif",
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        width: '60px',
                        flexShrink: 0,
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23a0a0a0' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.5rem center',
                        backgroundSize: '12px',
                        transition: 'background 0.3s ease, border-color 0.3s ease, color 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--accent-secondary)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-color)';
                      }}
                    >
                      <option value="m">m</option>
                      <option value="b">b</option>
                    </select>
                  </div>
                  <p className="form-hint" style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#a0a0a0' }}>
                    Enter number and select m (million) or b (billion)
                  </p>
                </div>
              </>
            )}

            {!is8BallPool && (formData.service_type === 'accounts' || initialServiceType === 'accounts') && formData.game && (() => {
              const accountAttrs = resolveAccountAttributes(formData.game);
              
              if (!accountAttrs) {
                return (
                  <div className="form-group">
                    <p className="form-hint">No extra attributes required for this game.</p>
                  </div>
                ); // normal games: no extra account type/attributes
              }

              if (accountAttrs.type === 'multi-select') {
                return accountAttrs.fields.map(field => (
                  <div key={field.name} className="form-group">
                    <label>{field.label} *</label>
                    <CustomDropdown
                      value={formData.account_attributes?.[field.name] || ''}
                      onChange={(value) => setFormData({ 
                        ...formData, 
                        account_attributes: {
                          ...formData.account_attributes,
                          [field.name]: value
                        }
                      })}
                      options={field.options.map(opt => ({ value: opt, label: opt }))}
                      placeholder={`Select ${field.label.toLowerCase()}`}
                      required
                    />
                  </div>
                ));
              } else {
                return (
                  <div className="form-group">
                    <label>{accountAttrs.label} *</label>
                    <CustomDropdown
                      value={formData.account_type}
                      onChange={(value) => setFormData({ ...formData, account_type: value })}
                      options={accountAttrs.options.map(type => ({ value: type, label: type }))}
                      placeholder={`Select ${accountAttrs.label.toLowerCase()}`}
                      required
                    />
                  </div>
                );
              }
            })()}
          </>
          );
        }
        return null;

      case 3:
        // Step 3: Service Type, Platform, Region, and Account Attributes (for top-ups only)
        if (isTopUps) {
          const is8BallPool = formData.game === '8 Ball Pool';
          
          // Normalize game name for lookup (handle slugs like "pubg" -> "PUBG")
          const normalizeGameName = (gameName) => {
            if (!gameName) return '';
            // Check if it's already a display name
            if (gamesWithPlatforms[gameName] || gamesWithRegions[gameName]) {
              return gameName;
            }
            // Use existing gameNameMap mapping
            const normalized = gameNameMap[gameName.toLowerCase()] || gameName;
            // If still not found, try the original name
            return normalized;
          };
          
          const normalizedGameName = normalizeGameName(formData.game);
          console.log('[AdForm] Step 3 - Game name normalization:', {
            original: formData.game,
            normalized: normalizedGameName,
            hasPlatform: !!gamesWithPlatforms[normalizedGameName],
            hasRegion: !!gamesWithRegions[normalizedGameName]
          });
          
          const gameRequiresRegion = gamesWithRegions[normalizedGameName] || gamesWithRegions[formData.game];
          const gameRequiresPlatform = gamesWithPlatforms[normalizedGameName] || gamesWithPlatforms[formData.game];
          // For top-ups, we ONLY show platform if it's explicitly in gamesWithPlatforms
          // We don't use resolveRequiresPlatform for top-ups since that's for accounts
          const requiresStandardPlatform = false; // Top-ups don't use standard platform dropdown
          
          // Check if there's anything to show in this step
          const hasServiceTypeSelection = !initialServiceType;
          const hasRegionSelection = !!gameRequiresRegion;
          const hasPlatformSelection = !!gameRequiresPlatform;
          const has8BallPoolAttributes = is8BallPool && (formData.service_type === 'accounts' || initialServiceType === 'accounts');
          
          // If nothing to show, display a message and allow proceeding
          if (!hasServiceTypeSelection && !hasRegionSelection && !hasPlatformSelection && !has8BallPoolAttributes) {
            return (
              <div className="form-group">
                <p style={{ color: '#a0a0a0', fontSize: '0.875rem', textAlign: 'center', padding: '2rem' }}>
                  No additional selections required for this game. Click "Next" to continue.
                </p>
              </div>
            );
          }
          
          return (
            <>
              {!initialServiceType && (
                <div className="form-group">
                  <label>Service Type *</label>
                  <CustomDropdown
                    value={formData.service_type}
                    onChange={(value) => setFormData({ ...formData, service_type: value })}
                    options={serviceTypes.map(type => ({ value: type, label: type }))}
                    placeholder="Select service"
                    required
                  />
                </div>
              )}

              {/* Region selection for games that require it */}
              {gameRequiresRegion && (
                <div className="form-group">
                  <label>Select game region *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    {gameRequiresRegion.map((region) => (
                      <button
                        key={region.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, top_up_region: region.value })}
                        style={{
                          padding: '0.625rem 1.25rem',
                          borderRadius: '0.5rem',
                          border: formData.top_up_region === region.value ? '2px solid #3b82f6' : '1px solid #3a3a3a',
                          backgroundColor: formData.top_up_region === region.value ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                          color: '#e5e5e5',
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {region.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Platform selection for games that require it (top-ups specific - button-based) */}
              {gameRequiresPlatform && (
                <div className="form-group">
                  <label>Platform *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    {gameRequiresPlatform.map((platform) => (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => {
                          console.log('[AdForm] Platform button clicked:', platform);
                          console.log('[AdForm] Current formData.platform:', formData.platform);
                          setFormData({ ...formData, platform: platform });
                          console.log('[AdForm] Setting platform to:', platform);
                        }}
                        style={{
                          padding: '0.625rem 1.25rem',
                          borderRadius: '0.5rem',
                          border: formData.platform === platform ? '2px solid #3b82f6' : '1px solid #3a3a3a',
                          backgroundColor: formData.platform === platform ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                          color: '#e5e5e5',
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Standard platform selection for other games (dropdown-based) */}
              {requiresStandardPlatform && !gameRequiresPlatform && (
                <div className="form-group">
                  <label>Platform *</label>
                  <CustomDropdown
                    value={formData.platform}
                    onChange={(value) => setFormData({ ...formData, platform: value })}
                    options={platforms.map(platform => ({ value: platform, label: platform }))}
                    placeholder="Select platform"
                    required
                  />
                </div>
              )}

              {is8BallPool && (formData.service_type === 'accounts' || initialServiceType === 'accounts') && (
                <>
                  <div className="form-group">
                    <label>Level *</label>
                    <input
                      type="text"
                      value={formData.account_attributes?.level || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        account_attributes: {
                          ...formData.account_attributes,
                          level: e.target.value
                        }
                      })}
                      placeholder="Enter account level"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Cash *</label>
                    <input
                      type="text"
                      value={formData.account_attributes?.cash || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        account_attributes: {
                          ...formData.account_attributes,
                          cash: e.target.value
                        }
                      })}
                      placeholder="Enter cash amount"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Coins *</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        type="number"
                        value={(() => {
                          const coins = formData.account_attributes?.coins || '';
                          const match = coins.match(/^(\d+\.?\d*)/);
                          return match ? match[1] : '';
                        })()}
                        onChange={(e) => {
                          const numberValue = e.target.value;
                          const currentCoins = formData.account_attributes?.coins || '';
                          const suffix = currentCoins.match(/([mb])$/i)?.[1]?.toLowerCase() || 'm';
                          const newValue = numberValue ? `${numberValue}${suffix}` : '';
                          setFormData({ 
                            ...formData, 
                            account_attributes: {
                              ...formData.account_attributes,
                              coins: newValue
                            }
                          });
                        }}
                        placeholder="Enter number"
                        required
                        min="0"
                        step="0.01"
                        style={{ flex: 1 }}
                      />
                      <select
                        value={(() => {
                          const coins = formData.account_attributes?.coins || '';
                          const suffix = coins.match(/([mb])$/i)?.[1]?.toLowerCase() || 'm';
                          return suffix;
                        })()}
                        onChange={(e) => {
                          const suffix = e.target.value;
                          const currentCoins = formData.account_attributes?.coins || '';
                          const match = currentCoins.match(/^(\d+\.?\d*)/);
                          const numberValue = match ? match[1] : '';
                          const newValue = numberValue ? `${numberValue}${suffix}` : suffix;
                          setFormData({ 
                            ...formData, 
                            account_attributes: {
                              ...formData.account_attributes,
                              coins: newValue
                            }
                          });
                        }}
                        style={{
                          padding: '0.625rem 1.75rem 0.625rem 0.75rem',
                          border: '1px solid #e5e5e5',
                          borderRadius: '0.5rem',
                          backgroundColor: '#fff',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="m">M</option>
                        <option value="b">B</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
              {!is8BallPool && (formData.service_type === 'accounts' || initialServiceType === 'accounts') && (() => {
                const accountAttrs = resolveAccountAttributes(formData.game);
                if (!accountAttrs) return null;
                if (accountAttrs.type === 'multi-select') {
                  return accountAttrs.fields.map((field) => (
                    <div key={field.name} className="form-group">
                      <label>{field.label} *</label>
                      <CustomDropdown
                        value={formData.account_attributes?.[field.name] || ''}
                        onChange={(value) => setFormData({ 
                          ...formData, 
                          account_attributes: {
                            ...formData.account_attributes,
                            [field.name]: value
                          }
                        })}
                        options={field.options.map(opt => ({ value: opt, label: opt }))}
                        placeholder={`Select ${field.label.toLowerCase()}`}
                        required
                      />
                    </div>
                  ));
                } else {
                  return (
                    <div className="form-group">
                      <label>{accountAttrs.label} *</label>
                      <CustomDropdown
                        value={formData.account_type}
                        onChange={(value) => setFormData({ ...formData, account_type: value })}
                        options={accountAttrs.options.map(type => ({ value: type, label: type }))}
                        placeholder={`Select ${accountAttrs.label.toLowerCase()}`}
                        required
                      />
                    </div>
                  );
                }
              })()}
            </>
          );
        }
        // 8 Ball Pool currency: no title, only optional description is on step 5
        const is8BPCurrencyStep3 = formData.game === '8 Ball Pool' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        if (is8BPCurrencyStep3) {
          return (
            <div className="form-group">
              <p style={{ margin: 0, color: '#a0a0a0', fontSize: '0.9375rem' }}>
                For 8 Ball Pool Coins there is no title — you can add an optional description in the next step.
              </p>
            </div>
          );
        }
        // For non-top-ups, case 3 should show title (moved from case 4)
        return (
          <>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Enter a descriptive title for your ad"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => {
                  const value = e.target.value;
                  // Count words
                  const words = value.trim().split(/\s+/).filter(word => word.length > 0);
                  if (words.length <= 500 || value.length < formData.description.length) {
                    setFormData({ ...formData, description: value });
                  }
                }}
                rows={6}
                placeholder="Describe your service in detail..."
                maxLength={3000}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.875rem', 
                  color: (formData.description.trim().split(/\s+/).filter(w => w.length > 0).length > 500) ? '#f87171' : '#a0a0a0'
                }}>
                  {formData.description.trim().split(/\s+/).filter(w => w.length > 0).length} / 500 words
                </p>
              </div>
            </div>
          </>
        );

      case 4:
        // Step 4: Quantity/Price/Delivery (for top-ups) OR Images (for others)
        if (isTopUps) {
          // For top-ups, step 4 is Quantity/Price/Delivery (no images step)
          return (
            <>
              <div className="form-group">
                <label>Total Quantity available *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.quantity || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty value while typing, but ensure it's a valid number
                    if (value === '') {
                      setFormData({ 
                        ...formData, 
                        quantity: ''
                      });
                    } else {
                      const qty = parseInt(value, 10);
                      if (!isNaN(qty) && qty >= 1) {
                        setFormData({ 
                          ...formData, 
                          quantity: qty
                        });
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // Ensure quantity is at least 1 when field loses focus
                    if (!formData.quantity || formData.quantity < 1) {
                      setFormData({ 
                        ...formData, 
                        quantity: 1
                      });
                    }
                  }}
                  required
                  placeholder="1"
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #3a3a3a',
                    backgroundColor: '#1a1a1a',
                    color: '#e5e5e5',
                    fontSize: '0.875rem'
                  }}
                />
                <p className="form-hint" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#a0a0a0' }}>
                  1 unit
                </p>
              </div>

              <div className="form-group">
                <label>Price per *</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.price}
                    onChange={(e) => {
                      // Only allow numbers, decimal point, and empty string
                      const value = e.target.value;
                      // Allow empty, numbers, and single decimal point
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setFormData({ ...formData, price: value });
                      }
                    }}
                    onKeyPress={(e) => {
                      // Prevent non-numeric characters (except decimal point)
                      const char = String.fromCharCode(e.which);
                      if (!/[0-9.]/.test(char)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="0.00"
                    required
                    style={{
                      flex: 1,
                      padding: '0.625rem 0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #3a3a3a',
                      backgroundColor: '#1a1a1a',
                      color: '#e5e5e5',
                      fontSize: '0.875rem'
                    }}
                  />
                  <span style={{ 
                    padding: '0.625rem 1rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #3a3a3a',
                    backgroundColor: '#1a1a1a',
                    color: '#a0a0a0',
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap'
                  }}>$ USD</span>
                </div>
                <p className="form-hint" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#a0a0a0' }}>
                  Competitive prices improve your offer's ranking in the offer list.
                </p>
              </div>

              {/* Delivery Method Selection – compact buttons + info panel below (same as currency) */}
              <div className="ad-form-section">
                <h3 className="ad-form-section-title">Delivery</h3>
                <DeliveryMethodSelector
                  deliveryMethods={deliveryMethods}
                  selectedValue={formData.delivery_method}
                  onChange={(value) => setFormData({ ...formData, delivery_method: value })}
                />

                <div className="form-group" style={{ marginTop: '0.85rem', marginBottom: 0 }}>
                  <label>Guaranteed delivery time *</label>
                  <CustomDropdown
                    value={formData.delivery_time}
                    onChange={(value) => setFormData({ ...formData, delivery_time: value })}
                    options={[
                      { value: 'Instant', label: 'Instant' },
                      { value: '5 min', label: '5 min' },
                      { value: '10 min', label: '10 min' },
                      { value: '15 min', label: '15 min' },
                      { value: '30 min', label: '30 min' },
                      { value: '1 hour', label: '1 hour' },
                      { value: '2 hours', label: '2 hours' },
                      { value: '6 hours', label: '6 hours' },
                      { value: '12 hours', label: '12 hours' },
                      { value: '24 hours', label: '24 hours' },
                      { value: '1-2 days', label: '1-2 days' },
                      { value: '3-5 days', label: '3-5 days' }
                    ]}
                    placeholder="Select delivery time"
                    required
                  />
                  <p className="form-hint" style={{ marginTop: '0.45rem' }}>
                    Faster delivery improves your offer’s ranking.
                  </p>
                </div>
              </div>

              {/* Delivery Instructions */}
              <div className="form-group">
                <label>Delivery instructions</label>
                <textarea
                  value={formData.delivery_instructions}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Count words
                    const words = value.trim().split(/\s+/).filter(word => word.length > 0);
                    if (words.length <= 500 || value.length < formData.delivery_instructions.length) {
                      setFormData({ ...formData, delivery_instructions: value });
                    }
                  }}
                  placeholder="Provide detailed instructions for how you will deliver this top-up..."
                  rows={6}
                  maxLength={3000}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #3a3a3a',
                    backgroundColor: '#1a1a1a',
                    color: '#e5e5e5',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                  <p className="form-hint" style={{ margin: 0, fontSize: '0.875rem', color: '#a0a0a0' }}>
                    Explain the delivery process to buyers (e.g., "I'll need your UID and will deliver within 10 minutes")
                  </p>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.875rem', 
                    color: (formData.delivery_instructions.trim().split(/\s+/).filter(w => w.length > 0).length > 500) ? '#f87171' : '#a0a0a0'
                  }}>
                    {formData.delivery_instructions.trim().split(/\s+/).filter(w => w.length > 0).length} / 500 words
                  </p>
                </div>
              </div>
            </>
          );
        }
        // 8 Ball Pool currency: step 4 = no images
        if (formData.game === '8 Ball Pool' && (formData.service_type === 'currency' || initialServiceType === 'currency')) {
          return (
            <div className="form-group">
              <p style={{ color: '#a0a0a0', fontSize: '0.9375rem', textAlign: 'center', padding: '2rem', margin: 0 }}>
                No images for this offer type. Click "Next" to continue to Pricing.
              </p>
            </div>
          );
        }
        // For non-top-ups, step 4 is Images (image upload UI would go here; for 8BP we skip above)
        // Fall-through: show Images for other currency/items (if any)
        // For non-top-ups, step 5 is Quantity/Price
        return (
          <>
            <div className="form-group">
              <label>Quantity *</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => {
                  const qty = parseInt(e.target.value) || 1;
                  setFormData({ 
                    ...formData, 
                    quantity: qty
                  });
                }}
                required
                placeholder="1"
              />
              <p className="form-hint">How many items are you selling?</p>
            </div>

            {(formData.service_type === 'accounts' || initialServiceType === 'accounts') && (
              <>
                <div className="form-group">
                  <div className="auto-delivery-container">
                    <label className="auto-delivery-label">
                      <input
                        type="checkbox"
                        checked={formData.auto_delivery}
                        onChange={(e) => setFormData({ ...formData, auto_delivery: e.target.checked })}
                        className="auto-delivery-checkbox"
                      />
                      <span className="auto-delivery-text">Enable Auto-Delivery</span>
                    </label>
                    <p className="form-hint auto-delivery-hint">
                      When enabled, account details will be automatically sent to buyers upon purchase.
                    </p>
                  </div>
                </div>

                {formData.auto_delivery && (
                  <div className="form-group">
                    <label>Account Details * ({formData.quantity} required)</label>
                    <div className="account-details-sections">
                      {Array.from({ length: formData.quantity }, (_, index) => (
                        <div key={index} className="account-detail-section">
                          <label className="account-detail-label">Account {index + 1}:</label>
                          <textarea
                            value={formData.account_details[index] || ''}
                            onChange={(e) => {
                              const newDetails = [...(formData.account_details || [])];
                              newDetails[index] = e.target.value;
                              setFormData({ ...formData, account_details: newDetails });
                            }}
                            rows={3}
                            placeholder={`Account ${index + 1} details`}
                            required={formData.auto_delivery}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Price</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.price}
                  onChange={(e) => {
                    // Only allow numbers, decimal point, and empty string
                    const value = e.target.value;
                    // Allow empty, numbers, and single decimal point
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setFormData({ ...formData, price: value });
                    }
                  }}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters (except decimal point)
                    const char = String.fromCharCode(e.which);
                    if (!/[0-9.]/.test(char)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label>Currency</label>
                <input
                  type="text"
                  value="EUR"
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
              </div>
            </div>

            {/* Delivery Time for accounts */}
            {(formData.service_type === 'accounts' || initialServiceType === 'accounts') && (
              <div className="form-group">
                <label>Delivery Time *</label>
                <select
                  value={formData.delivery_time || (formData.auto_delivery ? 'Instant' : '')}
                  onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                  required
                  disabled={formData.auto_delivery}
                >
                  <option value="">Select delivery time</option>
                  {formData.auto_delivery ? (
                    <option value="Instant">Instant</option>
                  ) : formData.game === '8 Ball Pool' ? (
                    <>
                      <option value="20 min">20 min</option>
                      <option value="1 day">1 day</option>
                    </>
                  ) : (
                    <>
                      <option value="Instant">Instant</option>
                      <option value="1-24 hours">1-24 hours</option>
                    </>
                  )}
                </select>
                {formData.auto_delivery && (
                  <p className="form-hint">Delivery time is automatically set to "Instant" when auto-delivery is enabled.</p>
                )}
              </div>
            )}
          </>
        );

      case 5:
        // Step 5: Preview (for top-ups) OR Pricing (8 Ball Pool currency) OR Quantity/Price (for others)
        // 8 Ball Pool currency: Pricing only (Price per 1 B, Volume discount, Fee structure)
        const is8BPCurrencyStep5 = formData.game === '8 Ball Pool' && (formData.service_type === 'currency' || initialServiceType === 'currency');
        if (is8BPCurrencyStep5) {
          return (
            <>
              {/* Price */}
              <div style={{ padding: '1rem', backgroundColor: '#1f1f1f', borderRadius: '0.5rem', border: '1px solid #3a3a3a', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.9375rem', fontWeight: '600', color: '#e5e5e5' }}>Price</h3>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#e5e5e5' }}>Price per 1 B :</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.price}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) setFormData({ ...formData, price: value });
                      }}
                      placeholder="Price"
                      style={{
                        flex: 1,
                        padding: '0.625rem 0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #3a3a3a',
                        backgroundColor: '#1a1a1a',
                        color: '#e5e5e5',
                        fontSize: '0.875rem'
                      }}
                    />
                    <span style={{ padding: '0.625rem 1rem', borderRadius: '0.5rem', border: '1px solid #3a3a3a', backgroundColor: '#1a1a1a', color: '#a0a0a0', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>$ USD</span>
                  </div>
                  <p className="form-hint" style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#a0a0a0' }}>
                    Competitive prices improve your offer's ranking in the offer list.
                  </p>
                </div>
              </div>

              {/* Volume discount - table with min quantity (B) and discount % */}
              <div style={{ padding: '1rem', backgroundColor: '#1f1f1f', borderRadius: '0.5rem', border: '1px solid #3a3a3a', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '0.9375rem', fontWeight: '600', color: '#e5e5e5' }}>Volume discount</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem', fontSize: '0.8125rem', color: '#a0a0a0', fontWeight: '500' }}>
                  <span>Minimum quantity for discount</span>
                  <span>Discount percentage</span>
                  <span style={{ width: 32 }} />
                </div>
                {(formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }]).map((row, index) => (
                  <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <input
                        type="number"
                        min="0"
                        value={row.min_quantity ?? ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                          const next = [...(formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }])];
                          next[index] = { ...next[index], min_quantity: val === '' ? 0 : (isNaN(val) ? next[index].min_quantity : val) };
                          setFormData({ ...formData, volume_discount: next });
                        }}
                        placeholder="0"
                        style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', border: '1px solid #3a3a3a', backgroundColor: '#1a1a1a', color: '#e5e5e5', fontSize: '0.875rem' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: '#a0a0a0', whiteSpace: 'nowrap' }}>B</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={row.discount_percent ?? ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                          const next = [...(formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }])];
                          next[index] = { ...next[index], discount_percent: val === '' ? 0 : (isNaN(val) ? next[index].discount_percent : Math.min(100, Math.max(0, val))) };
                          setFormData({ ...formData, volume_discount: next });
                        }}
                        placeholder="0"
                        style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', border: '1px solid #3a3a3a', backgroundColor: '#1a1a1a', color: '#e5e5e5', fontSize: '0.875rem' }}
                      />
                      <span style={{ fontSize: '0.875rem', color: '#a0a0a0', whiteSpace: 'nowrap' }}>%</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const next = (formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }]).filter((_, i) => i !== index);
                        if (next.length === 0) next.push({ min_quantity: 0, discount_percent: 0 });
                        setFormData({ ...formData, volume_discount: next });
                      }}
                      style={{ padding: '0.5rem', background: 'none', border: 'none', color: '#a0a0a0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      aria-label="Delete row"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, volume_discount: [...(formData.volume_discount || [{ min_quantity: 0, discount_percent: 0 }]), { min_quantity: 0, discount_percent: 0 }] })}
                  style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #3a3a3a', backgroundColor: '#1a1a1a', color: '#e5e5e5', fontSize: '0.875rem', cursor: 'pointer', marginTop: '0.25rem' }}
                >
                  + Add row
                </button>
              </div>

              {/* Fee structure */}
              <div style={{ padding: '1rem', backgroundColor: '#1f1f1f', borderRadius: '0.5rem', border: '1px solid #3a3a3a' }}>
                <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9375rem', fontWeight: '600', color: '#e5e5e5' }}>Fee structure</h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#a0a0a0' }}>Flat fee (per purchase): $0.00 USD</p>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#a0a0a0' }}>Percentage fee (per purchase): 5% of Price</p>
              </div>
            </>
          );
        }
        if (isTopUps) {
          // For top-ups, step 5 is Preview - replicate the order-summary card design
          const selectedOptionLabel = gameTopUpsOptions[formData.game]?.find(opt => opt.value === formData.top_up_option)?.label || formData.top_up_option || 'Top-up';
          const deliveryTime = formData.delivery_time || '1-24 hours';
          const price = formData.price ? parseFloat(formData.price).toFixed(2) : '0.00';
          const deliveryInstructions = formData.delivery_instructions || '';
          const deliveryMethod = formData.delivery_method || '';
          const currentUser = getCachedUser();
          
          // Format delivery instructions
          const formatDeliveryInstructions = (instructions) => {
            if (!instructions) return [];
            if (typeof instructions === 'string') {
              return instructions.split('\n').filter(line => line.trim());
            }
            return [];
          };
          
          // Get delivery method display name
          const getDeliveryMethodName = (method) => {
            const methods = {
              'autoclaim-uid': 'AutoClaim (UID)',
              'login-method': 'Login Method',
              'redeem-code': 'Redeem Code',
              'donation': 'Donation'
            };
            return methods[method] || method;
          };
          
          // Clock icon component
          const ClockIcon = ({ color = '#a0a0a0', size = 16 }) => (
            <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6" stroke={color} strokeWidth="1.5"/>
              <path d="M8 4V8L10.5 10.5" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          );
          
          // Check icon component
          const CheckIcon = ({ color = '#4ade80', size = 16 }) => (
            <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.3333 4L6 11.3333L2.66667 8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          );
          
          return (
            <div style={{ maxWidth: '400px', margin: '0 auto' }}>
              <div className="order-summary">
                <div className="selected-item-display">
                  <div className="selected-item-icon">
                    <div style={{ 
                      width: '56px', 
                      height: '56px', 
                      backgroundColor: '#1a1a1a', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#a0a0a0',
                      fontSize: '12px'
                    }}>
                      Icon
                    </div>
                  </div>
                  <div className="selected-item-info">
                    <div className="selected-item-title">{selectedOptionLabel}</div>
                    <div className="delivery-time">
                      <ClockIcon size={14} />
                      <span>{deliveryTime}</span>
                    </div>
                  </div>
                </div>

                <div className="total-price">
                  Total: NOK{price}
                </div>

                <button 
                  className="btn-buy-now-main"
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                >
                  Buy now
                </button>

                <div className="guarantees-section">
                  <div className="guarantee-item">
                    <span className="guarantee-icon">
                      <CheckIcon size={16} />
                    </span>
                    <span>Money-back Guarantee Protected by TradeShield</span>
                  </div>
                </div>

                {deliveryInstructions && (
                  <div className="delivery-instructions">
                    <h4 className="instructions-title">Delivery Instructions</h4>
                    {formatDeliveryInstructions(deliveryInstructions).map((instruction, idx) => (
                      <div key={idx} className="instruction-item">
                        <span className="instruction-check">
                          <CheckIcon size={14} />
                        </span>
                        <span>{instruction}</span>
                      </div>
                    ))}
                  </div>
                )}

                {currentUser && (
                  <div className="seller-info-card">
                    <div className="seller-info-header">
                      <div className="seller-info-avatar">
                        {currentUser.avatar_url && !currentUser.avatar_url.startsWith('letter:') ? (
                          <img 
                            src={currentUser.avatar_url} 
                            alt={currentUser.username} 
                            style={{ width: '32px', height: '32px', borderRadius: '50%' }} 
                          />
                        ) : (
                          <div style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%', 
                            backgroundColor: '#7353eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}>
                            {currentUser.username?.charAt(0).toUpperCase() || 'S'}
                          </div>
                        )}
                      </div>
                      <div className="seller-info-details">
                        <div className="seller-info-name">{currentUser.username || 'Seller'}</div>
                        <div className="seller-info-rating">99.4% (0)</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }
        // For non-top-ups, step 5 is Quantity/Price (handled in case 5)
        return null;

      case 6:
        // Step 6: Preview (only for non-top-ups)
        if (isTopUps) {
          return null; // Top-ups only have 5 steps
        }
        // For non-top-ups, step 5 is Quantity/Price (handled in case 5 for non-top-ups)
        return null;

      default:
        return null;
    }
  };

  const serviceTypeForLabels = initialServiceType || formData.service_type;
  const isTopUpsForLabels = serviceTypeForLabels === 'top-ups';
  const isCurrencyFlowLabels = (is8BPCurrencyFlow || isAlbionCurrencyFlow || isACNHCurrencyFlow || isArcRaidersCurrencyFlow || isBDOCurrencyFlow || isDarkAndDarkerCurrencyFlow || isDeltaForceCurrencyFlow || isDiablo4CurrencyFlow || isDigimonSuperRumbleCurrencyFlow || isDonutSMPCurrencyFlow || isEASportsFCCurrencyFlow || isEASportsFCMobileCurrencyFlow || isEldenRingCurrencyFlow || isElderScrollsOnlineCurrencyFlow || isEscapeFromTarkovCurrencyFlow || isFallout76CurrencyFlow || isFinalFantasyXIVCurrencyFlow || isFischCurrencyFlow || isForzaHorizon5CurrencyFlow || isGrowAGardenCurrencyFlow || isLostArkCurrencyFlow || isMinecraftCurrencyFlow || isNewWorldCurrencyFlow || isOldSchoolRuneScapeCurrencyFlow || isPathOfExileCurrencyFlow || isPathOfExile2CurrencyFlow || isPokemonGoCurrencyFlow || isRobloxCurrencyFlow || isRuneScape3CurrencyFlow || isThroneAndLibertyCurrencyFlow || isWarframeCurrencyFlow || isWoWClassicCurrencyFlow || isWoWMistsOfPandariaCurrencyFlow);

  const stepList = isCurrencyFlowLabels ? [1, 2, 3, 4] : isTopUpsForLabels ? [1, 2, 3, 4, 5] : [1, 2, 3, 4, 5, 6];

  const getStepLabel = (step) => {
    if (step === 1) return 'Game';
    if (step === 2) {
      if (isCurrencyFlowLabels) return 'Details';
      if (isTopUpsForLabels) return 'Offer';
      return 'Attributes';
    }
    if (step === 3) {
      if (isCurrencyFlowLabels) return 'Price';
      if (isTopUpsForLabels) return 'Platform';
      return 'Details';
    }
    if (step === 4) {
      if (isCurrencyFlowLabels) return 'Preview';
      if (isTopUpsForLabels) return 'Delivery';
      return 'Images';
    }
    if (step === 5) return isTopUpsForLabels ? 'Preview' : 'Price';
    if (step === 6 && !isTopUpsForLabels) return 'Preview';
    return '';
  };

  const getStepHelp = () => {
    const label = getStepLabel(currentStep);
    const map = {
      Game: 'Pick the game this offer is for.',
      Details: 'Add stock, limits, and a short description buyers will see.',
      Offer: 'Choose the top-up option you are selling.',
      Attributes: 'Set platform and account attributes for this listing.',
      Price: 'Set your price in EUR. You can adjust it anytime.',
      Platform: 'Select platform or region if required.',
      Delivery: 'Set quantity, price, and how you deliver.',
      Images: 'Add clear photos so buyers trust your offer.',
      Preview: 'Double-check everything, then publish.',
    };
    return map[label] || 'Fill in the fields below to continue.';
  };

  const progressPct = Math.min(100, Math.round((currentStep / Math.max(totalSteps, 1)) * 100));

  const adFormOverlay = (
    <div className="ad-form-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="ad-form" role="dialog" aria-modal="true" aria-labelledby="ad-form-title">
        <div className="form-header">
          <div className="form-header-top">
            <div className="form-header-copy">
              <p className="form-header-kicker">
                {ad ? 'Edit offer' : 'New offer'} · Step {currentStep} of {totalSteps}
              </p>
              <h3 id="ad-form-title">{ad ? 'Edit Ad' : 'Create Ad'}</h3>
            </div>
            <button type="button" className="ad-form-close" onClick={onCancel} aria-label="Close">
              ×
            </button>
          </div>
          <div className="form-progress" aria-hidden>
            <div className="form-progress-bar" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="form-steps">
            {stepList.map((step) => (
              <div
                key={step}
                className={`form-step-indicator ${currentStep === step ? 'active' : currentStep > step ? 'completed' : ''}`}
              >
                <div className="step-number">{currentStep > step ? '✓' : step}</div>
                <div className="step-label">{getStepLabel(step)}</div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-step-content">
            <h4 className="form-step-heading">{getStepLabel(currentStep)}</h4>
            <p className="form-step-sub">{getStepHelp()}</p>
            {renderStepContent()}
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel}>Cancel</button>
            {currentStep > 1 && (
              <button type="button" onClick={handlePrevious} className="btn-previous">
                Back
              </button>
            )}
            {currentStep < totalSteps ? (
              <button 
                type="button" 
                onClick={handleNext} 
                className="btn-next"
                disabled={!canProceed() || submitting}
              >
                {currentStep === totalSteps - 1 ? 'Review' : 'Continue'}
              </button>
            ) : (
              <button 
                type="submit" 
                className="btn-submit"
                disabled={!canProceed() || submitting}
              >
                {submitting ? 'Publishing…' : (ad ? 'Save changes' : 'Publish offer')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(adFormOverlay, document.body)
    : adFormOverlay;
};

// Icon cache for instant loading
const iconCache = new Map();

// Preload icon helper
const preloadIcon = (src) => {
  if (!src || iconCache.has(src)) {
    return Promise.resolve();
  }
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      iconCache.set(src, true);
      resolve();
    };
    img.onerror = () => {
      iconCache.set(src, false);
      reject();
    };
    img.src = src;
  });
};

// Delivery Method Selector – compact buttons (no radio), info panel below with slide-down
const DeliveryMethodSelector = ({ deliveryMethods, selectedValue, onChange, showBeta = false }) => {
  const [hoveredMethod, setHoveredMethod] = useState(null);
  const hovered = deliveryMethods.find((m) => m.value === hoveredMethod);

  return (
    <div
      className="form-group delivery-method-selector"
      onMouseLeave={() => setHoveredMethod(null)}
    >
      <label>
        Delivery method *
        {showBeta ? <span className="delivery-method-beta">BETA</span> : null}
      </label>
      <div className="delivery-method-options">
        {deliveryMethods.map((method) => (
          <button
            key={method.value}
            type="button"
            className={`delivery-method-option ${selectedValue === method.value ? 'selected' : ''}`}
            onClick={() => onChange(method.value)}
          >
            <span className="delivery-method-label">{method.label}</span>
            <span
              className="delivery-method-info-icon"
              onMouseEnter={(e) => { e.stopPropagation(); setHoveredMethod(method.value); }}
              onClick={(e) => e.stopPropagation()}
              role="button"
              aria-label={`Info about ${method.label}`}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 5V8M8 11H8.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </span>
          </button>
        ))}
      </div>
      <div className={`delivery-method-info-panel-wrap ${hovered ? 'visible' : ''}`}>
        <div className="delivery-method-info-panel">
          {hovered && (
            <>
              <h4>{hovered.label}</h4>
              <p>{hovered.description}</p>
              <ol>
                {hovered.steps.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom Dropdown Component (styled like the image)
const CustomDropdown = ({ value, onChange, options, placeholder, required }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadedIcons, setLoadedIcons] = useState(new Set());
  const [loadingIcons, setLoadingIcons] = useState(new Set());
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [sheetEntered, setSheetEntered] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChangeMq = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChangeMq);
    return () => mq.removeEventListener('change', onChangeMq);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setSheetEntered(false);
  }, []);

  const closeMobileSheetAnimated = useCallback(() => {
    setSheetEntered(false);
    window.setTimeout(() => {
      setIsOpen(false);
      setSearchQuery('');
    }, 320);
  }, []);

  useEffect(() => {
    if (!isOpen || !isMobile) {
      setSheetEntered(false);
      return undefined;
    }
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setSheetEntered(true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (isOpen && isMobile) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [isOpen, isMobile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
        setSearchQuery('');
        setSheetEntered(false);
      }
    };

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen || isMobile) {
      setMenuPos(null);
      return undefined;
    }
    const updatePos = () => {
      const el = dropdownRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const menuMax = 280;
      const openUp = spaceBelow < menuMax && r.top > spaceBelow;
      setMenuPos({
        top: openUp ? undefined : r.bottom + 4,
        bottom: openUp ? window.innerHeight - r.top + 4 : undefined,
        left: r.left,
        width: Math.max(r.width, 180),
      });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (isMobile) closeMobileSheetAnimated();
        else closeDropdown();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isMobile, closeDropdown, closeMobileSheetAnimated]);

  useEffect(() => {
    if (isOpen && isMobile && sheetEntered && inputRef.current) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [isOpen, isMobile, sheetEntered]);

  // Preload icons when dropdown opens
  useEffect(() => {
    if (isOpen) {
      const iconsToLoad = options
        .filter(opt => opt.icon && !loadedIcons.has(opt.icon) && !loadingIcons.has(opt.icon) && !iconCache.has(opt.icon))
        .map(opt => opt.icon);
      
      iconsToLoad.forEach(iconSrc => {
        setLoadingIcons(prev => new Set(prev).add(iconSrc));
        preloadIcon(iconSrc)
          .then(() => {
            setLoadedIcons(prev => new Set(prev).add(iconSrc));
            setLoadingIcons(prev => {
              const next = new Set(prev);
              next.delete(iconSrc);
              return next;
            });
          })
          .catch(() => {
            setLoadingIcons(prev => {
              const next = new Set(prev);
              next.delete(iconSrc);
              return next;
            });
          });
      });
    }
  }, [isOpen, options, loadedIcons, loadingIcons]);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedOption = options.find(opt => opt.value === value);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchQuery('');
    setSheetEntered(false);
  };

  const renderDropdownItems = () => {
    if (filteredOptions.length === 0) {
      return <div className="custom-dropdown-no-results">No results found</div>;
    }
    return filteredOptions.map((option) => (
      <div
        key={option.value}
        className={`custom-dropdown-item ${value === option.value ? 'selected' : ''}`}
        onClick={() => handleSelect(option)}
        role="option"
        aria-selected={value === option.value}
      >
        {option.icon && (
          <>
            {(!iconCache.has(option.icon) && !loadedIcons.has(option.icon) && loadingIcons.has(option.icon)) ? (
              <div className="custom-dropdown-item-icon-skeleton" />
            ) : (
              <img
                src={option.icon}
                alt={option.label}
                className="custom-dropdown-item-icon"
                onError={(e) => { e.target.style.display = 'none'; }}
                onLoad={() => {
                  if (!loadedIcons.has(option.icon)) {
                    setLoadedIcons((prev) => new Set(prev).add(option.icon));
                  }
                }}
              />
            )}
          </>
        )}
        <span>{option.label}</span>
      </div>
    ));
  };

  const mobileSheetPortal =
    isOpen &&
    isMobile &&
    typeof document !== 'undefined' &&
    createPortal(
      <div className="custom-dropdown-mobile-root" role="presentation">
        <button
          type="button"
          className={`custom-dropdown-mobile-backdrop ${sheetEntered ? 'custom-dropdown-mobile-backdrop--open' : ''}`}
          aria-label="Close"
          onClick={closeMobileSheetAnimated}
        />
        <div
          className={`custom-dropdown-mobile-sheet ${sheetEntered ? 'custom-dropdown-mobile-sheet--open' : ''}`}
          role="listbox"
          aria-label={placeholder || 'Options'}
        >
          <div className="custom-dropdown-mobile-sheet-handle" aria-hidden />
          <div className="custom-dropdown-mobile-sheet-title">{placeholder || 'Select'}</div>
          <div className="custom-dropdown-search">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={placeholder}
              className="custom-dropdown-search-input"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <div className="custom-dropdown-list custom-dropdown-mobile-sheet-list">{renderDropdownItems()}</div>
          <div className="custom-dropdown-mobile-sheet-footer">
            <button type="button" className="custom-dropdown-mobile-sheet-cancel" onClick={closeMobileSheetAnimated}>
              Cancel
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="custom-dropdown-wrapper" ref={dropdownRef}>
      <div
        className={`custom-dropdown-input ${isOpen ? 'open' : ''}`}
        onClick={() => {
          if (isOpen) {
            if (isMobile) closeMobileSheetAnimated();
            else {
              setIsOpen(false);
              setSearchQuery('');
              setSheetEntered(false);
            }
          } else {
            setIsOpen(true);
            if (!isMobile && inputRef.current) {
              setTimeout(() => inputRef.current?.focus(), 0);
            }
          }
        }}
      >
        {selectedOption ? (
          <div className="custom-dropdown-selected">
            {selectedOption.icon && (
              <>
                {(!iconCache.has(selectedOption.icon) && !loadedIcons.has(selectedOption.icon) && loadingIcons.has(selectedOption.icon)) ? (
                  <div className="custom-dropdown-icon-skeleton" />
                ) : (
                  <img 
                    src={selectedOption.icon} 
                    alt={selectedOption.label}
                    className="custom-dropdown-icon"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    onLoad={() => {
                      if (!loadedIcons.has(selectedOption.icon)) {
                        setLoadedIcons(prev => new Set(prev).add(selectedOption.icon));
                      }
                    }}
                  />
                )}
              </>
            )}
            <span>{selectedOption.label}</span>
          </div>
        ) : (
          <span className="custom-dropdown-placeholder">{placeholder}</span>
        )}
        <span className="custom-dropdown-arrow" aria-hidden>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d={isOpen ? 'M9 7.5L6 4.5L3 7.5' : 'M3 4.5L6 7.5L9 4.5'}
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      {isOpen && !isMobile && menuPos && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={menuRef}
              className="custom-dropdown-menu custom-dropdown-menu--portal"
              style={{
                position: 'fixed',
                top: menuPos.top,
                bottom: menuPos.bottom,
                left: menuPos.left,
                width: menuPos.width,
                zIndex: 13050,
              }}
            >
              <div className="custom-dropdown-search">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={placeholder}
                  className="custom-dropdown-search-input"
                  autoFocus
                />
              </div>
              <div className="custom-dropdown-list">{renderDropdownItems()}</div>
            </div>,
            document.body
          )
        : null}

      {mobileSheetPortal}
    </div>
  );
};

export default OffersTab;
